import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion } from 'motion/react';
import { Navigate } from 'react-router-dom';
import Select from 'react-select';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import debounce              from 'lodash/debounce';
import OverviewTab3         from './OverviewTab3';
import OverviewTab5         from './OverviewTab5';
import OverviewTab7         from './OverviewTab7';
import LiveTab2             from './LiveTab2';
import SoldTab2             from './SoldTab2';
import LiveTab3             from './LiveTab3';
import SoldTab3             from './SoldTab3';
import AnalyticsTab2        from './AnalyticsTab2';
import TradedTab2           from './TradedTab2';
import WishlistTab2         from './WishlistTab2';
import InvestmentsTab2      from './InvestmentsTab2';
import HistoryTab           from './HistoryTab';
import OverviewTabZone      from './OverviewTabZone';
import LiveTabZone          from './LiveTabZone';
import SoldTabZone          from './SoldTabZone';
import TradedTabZone        from './TradedTabZone';
import WishlistTabZone      from './WishlistTabZone';
import InvestmentsTabZone   from './InvestmentsTabZone';
import { getFolders, makeFolder, isVaultRow, applyFolderPick, FOLDER_COLORS } from './investments';
import AnalyticsTabZone     from './AnalyticsTabZone';
import InventoryTabZone     from './InventoryTabZone';
import HistoryTabZone       from './HistoryTabZone';
import LendingTab           from './LendingTab';
import LendingTabZone       from './LendingTabZone';
import TradedTab            from './TradedTab';
import PricerTab            from './PricerTab';
import PricerTabZone        from './PricerTabZone';
import InventoryImportModal from './InventoryImportModal';
import ExtensionPanel       from './ExtensionPanel';
import ProfileTab           from './ProfileTab';
import GroupedPurchaseModal from './GroupedPurchaseModal';
import WalletChip           from './WalletChip';
import PaidWith             from './PaidWith';
import CryptoWalletSettings from './CryptoWalletSettings';
import { NewItemsBanner, NewItemsModal, AutoSoldBanner, UntrackedSalesModal } from './NewItemsPrompt';
import VanishedItemsPrompt from './VanishedItemsPrompt';
import { TradeBootBanner, TradeBootModal } from './TradeBootPrompt';
import GroupSellModal from './GroupSellModal';
import GroupPriceModal from './GroupPriceModal';
import FolderPickModal from './FolderPickModal';
import InvestmentsTab from './InvestmentsTab';
import './InventoryGrid.css';
import './ModalTheme.css';
import { API, apiFetch, clearSession, getCachedAccount, STEAM_LINK_RESULT } from '../api';
import { EXT_EVENT } from '../extensionEvents';
import { getBuffPrice, allocateCents, getTradeLock, calcProfit, getRowImage, isTradedAway, getInventoryIcon, coveredAssetIds } from './helpers';
import { TradePartnerBadge, buildTradePartnerIndex } from './TradePartner';
// Sales accepted on CSFloat but still inside Steam's trade-protection window.
// Scoped once here so the item-detail trade chain — opened from any tab — can
// show them without fifteen call sites passing the same prop down.
import { SettlingProvider } from './settlingSales';
import { TradeHistoryProvider } from './tradeHistoryContext';
import { CharmSlot } from './StickerSlots';
import {
  isLent, lendRow, returnRow, writeOffRow, unwindTrade, knownFriends,
  suggestedPartner, openLends, rowFromHistoryItem,
  borrowerFromCandidates, departureFromCandidates,
} from './lending';
import LendModal from './LendModal';
import LendPicker from './LendPicker';
import ItemFormModalAlt from './ItemFormModalAlt';
import useIsMobile from '../mobile/useIsMobile';
import MobileApp from '../mobile/MobileApp';
import { findSkinImage } from './skinIndex';

// Edit-modal design being trialled. Persisted so the choice survives reloads
// while the three variants are being compared.
const EDIT_DESIGNS = [
  { id: 'classic', label: 'Stacked' },
  { id: 'twopane', label: 'Two pane' },
  { id: 'receipt', label: 'Receipt' },
  { id: 'zone',    label: 'Zone' },
];

function DesignSwitcher({ value, onChange }) {
  return (
    <div className="ialt-switch" onClick={e => e.stopPropagation()}>
      {EDIT_DESIGNS.map(d => (
        <button key={d.id} type="button" className={value === d.id ? 'on' : ''}
          onClick={() => onChange(d.id)} title={`Switch to the ${d.label} layout`}>
          {d.label}
        </button>
      ))}
    </div>
  );
}

// ─── Constants ────────────────────────────────────────────
const weaponMappings = {
  'ak':'AK-47','deagle':'Desert Eagle','m4a4':'M4A4','m4a1':'M4A1-S',
  'usp':'USP-S','usps':'USP-S','p2000':'P2000','galil':'Galil AR',
  'awp':'AWP','ssg':'SSG 08','aug':'AUG','famas':'FAMAS','glock':'Glock-18',
  'p250':'P250','p90':'P90','ump':'UMP-45','mp9':'MP9','mac10':'MAC-10',
  'mac':'MAC-10','nova':'Nova','sawed':'Sawed-Off','mag7':'MAG-7',
  'xm1014':'XM1014','bizon':'PP-Bizon','mp7':'MP7','mp5':'MP5-SD',
  'cz':'CZ75-Auto','cz75':'CZ75-Auto','tec9':'Tec-9','revolver':'R8 Revolver',
  'r8':'R8 Revolver','negev':'Negev','m249':'M249','flip':'Flip Knife',
  'bayonet':'Bayonet','karambit':'Karambit','m9':'M9 Bayonet',
  'm9 bayonet':'M9 Bayonet','butterfly':'Butterfly Knife','talon':'Talon Knife',
  'stiletto':'Stiletto Knife','huntsman':'Huntsman Knife','falchion':'Falchion Knife',
  'daggers':'Shadow Daggers','shadow':'Shadow Daggers','gut':'Gut Knife',
  'navaja':'Navaja Knife','ursus':'Ursus Knife','classic':'Classic Knife',
  'paracord':'Paracord Knife','survival':'Survival Knife','nomad':'Nomad Knife',
  'skeleton':'Skeleton Knife',
};

const CONDITION_MAP = {
  'Factory New':   'Factory New',
  'Minimal Wear':  'Minimal Wear',
  'Field Tested':  'Field-Tested',
  'Well Worn':     'Well-Worn',
  'Battle Scarred':'Battle-Scarred',
};

const EXTERIOR_TO_CONDITION = {
  'Factory New':   'Factory New',
  'Minimal Wear':  'Minimal Wear',
  'Field-Tested':  'Field Tested',
  'Well-Worn':     'Well Worn',
  'Battle-Scarred':'Battle Scarred',
};

// Normalize row.itemType: supports both legacy single-object and new array format
function getItemTypes(row) {
  if (!row?.itemType) return [];
  return Array.isArray(row.itemType) ? row.itemType : [row.itemType];
}

const COND_PILLS = [
  { value:'Factory New',   short:'FN' },
  { value:'Minimal Wear',  short:'MW' },
  { value:'Field Tested',  short:'FT' },
  { value:'Well Worn',     short:'WW' },
  { value:'Battle Scarred',short:'BS' },
];

const DEFAULT_SETTINGS = {
  itemTypes: [
    { value:'Normal',  label:'Normal',  color:'#ffffff', showInOverview:false },
    { value:'Kings',   label:'Kings',   color:'#f59e0b', showInOverview:true  },
    { value:'Crafts',  label:'Crafts',  color:'#f472b6', showInOverview:true  },
    { value:'Keepers', label:'Keepers', color:'#4ade80', showInOverview:true  },
  ],
  backgroundColor: '#05080f',
  profitTextColor:  '#4ade80',
  lossTextColor:    '#f87171',
  // Whether the hand-typed Cash row exists at all.
  //
  // Separate from trackWallets below, because the two costs are different.
  // CSFloat and crypto read themselves, so having them costs nothing to keep
  // current. Cash is the only wallet that is pure manual upkeep: nothing can
  // read your bank, so every figure in that row is one you typed and will have
  // to retype. Someone who does not want that job should be able to switch the
  // row off rather than look at a number they know is stale.
  //
  // Turning it off hides the row and drops Cash as a payment source. The stored
  // balance is kept, not deleted, so turning it back on restores it.
  useBankWallet: true,
  // Whether buying something or paying cash on a trade should also move a
  // wallet balance.
  //
  // Off is a perfectly coherent way to use this app. The AMOUNT is what fixes
  // your cost basis, and it is recorded either way; naming a wallet only adds
  // the bookkeeping of keeping that wallet's figure current. Someone who just
  // wants to note "I paid $30 on top of this trade" should not be forced to
  // declare how much cash they hold in order to say it.
  //
  // Defaults on, because it is already on for anyone who has been using it.
  trackWallets: true,
  // Whether the Lending page exists at all.
  //
  // Off by default, and deliberately so: most people never lend a skin, and a
  // tab that is always empty is worse than no tab. Turning it off only hides
  // the page and the lend actions — any `lend` already written to a row is
  // kept, exactly like the cash wallet's balance, so switching back on
  // restores what was there.
  lendingEnabled: false,
  sites: [
    { value:'buff',         label:'Buff (2.50% / 0%)',          salesFee:0.025, withdrawalFee:0     },
    { value:'csfloat',      label:'CSFloat (2% / 1%)',           salesFee:0.02,  withdrawalFee:0.01  },
    { value:'cash',         label:'Cash (0% / 0%)',              salesFee:0.0,   withdrawalFee:0     },
    { value:'csmoney',      label:'CS.Money (5% / 1.5%)',        salesFee:0.05,  withdrawalFee:0.015 },
    { value:'csmoneypromo', label:'CS.Money Promo (2% / 1.5%)', salesFee:0.02,  withdrawalFee:0.015 },
  ],
};

// ─── Helpers ──────────────────────────────────────────────
function stripCondition(name) {
  return name.replace(/ \((Factory New|Minimal Wear|Field-Tested|Well-Worn|Battle-Scarred)\)$/, '').trim();
}


function getLivePrice(livePrices, row) {
  if (!livePrices || !row.skin?.value) return null;
  // Items with condition (skins)
  if (row.condition?.value) {
    const cond = CONDITION_MAP[row.condition.value] || row.condition.value;
    return livePrices[`${row.skin.value} (${cond})`] || null;
  }
  // Items without condition (stickers, agents, graffiti, etc.)
  return livePrices[row.skin.value] || null;
}

// Picks a row matching `predicate` — when more than one candidate matches
// (e.g. two identical unsold skins bought at different prices), takes the
// oldest by timestamp (FIFO) instead of relying on incidental array order,
// and warns so a surprising match is at least visible in dev tools.
function pickFifoMatch(rows, predicate, label) {
  const candidates = rows
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => predicate(r));
  if (candidates.length === 0) return -1;
  if (candidates.length > 1) {
    console.warn(`[pickFifoMatch] ${candidates.length} ambiguous candidates for "${label}" — using oldest (FIFO)`);
  }
  candidates.sort((a, b) => new Date(a.r.timestamp || 0) - new Date(b.r.timestamp || 0));
  return candidates[0].i;
}

function createEmptyRow(settings) {
  return {
    skin: null, description: '', condition: null, itemType: [],
    buyPrice: '', sellPrice: '',
    siteFee: settings?.sites?.[0] || null,
    profit: '',
    timestamp: new Date().toISOString(),
    targetPrice: '',
    inspectLink: '',
    float: null,
    paintSeed: null,
    priceHistory: [],
  };
}

function mergeSites(existing = [], defaults = DEFAULT_SETTINGS.sites) {
  const merged = [...defaults];
  existing.forEach(s => { if (!merged.some(m => m.value === s.value)) merged.push(s); });
  return merged;
}

function createSearchOptions(data) {
  return data.map(skin => {
    const terms = new Set();
    const [weapon, skinPart] = skin.name.split(' | ');
    terms.add(skin.name.toLowerCase());
    if (skinPart) terms.add(skinPart.toLowerCase());
    terms.add(weapon.toLowerCase());
    Object.entries(weaponMappings).forEach(([abbr, full]) => {
      if (weapon === full) {
        terms.add(abbr);
        if (skinPart) {
          terms.add(`${abbr} ${skinPart}`.toLowerCase());
          if (full.includes('Knife')) {
            const short = abbr.replace(' knife','').replace('knife','');
            terms.add(`${short} ${skinPart}`.toLowerCase());
            terms.add(short);
          }
        }
      }
    });
    if (weapon.includes('Knife')) {
      const noKnife = weapon.replace(' Knife','');
      terms.add(noKnife.toLowerCase());
      if (skinPart) terms.add(`${noKnife} ${skinPart}`.toLowerCase());
    }
    return { value:skin.name, label:skin.name, searchableTerms:Array.from(terms) };
  });
}

// ─── Mark Sold / Traded modal ─────────────────────────────
const EXTERIOR_TO_COND = {
  'Factory New': 'Factory New', 'Minimal Wear': 'Minimal Wear',
  'Field-Tested': 'Field Tested', 'Well-Worn': 'Well Worn', 'Battle-Scarred': 'Battle Scarred',
};

function MarkSoldTradedModal({ row, settings, inventoryItems = [], claimedAssetIds = new Set(), searchableOptions = [], livePrices, csfloatPrices, onConfirm, onClose }) {
  const trackWallets = settings?.trackWallets !== false;
  const trackBank    = settings?.useBankWallet !== false;
  const [mode, setMode]           = useState('sold');
  const [platform, setPlatform]   = useState(row.siteFee?.value || settings?.sites?.[0]?.value || '');
  const [price, setPrice]         = useState('');
  const [receivedItems, setReceivedItems] = useState([]);
  const [addMode, setAddMode]       = useState('inventory');
  const [invSearch, setInvSearch]   = useState('');
  const [stageName, setStageName]   = useState('');
  const [stageCond, setStageCond]   = useState(null);
  // The cash side of a trade. Amount is always typed as a positive number and
  // the direction is a separate choice, because "-150" in a money field is a
  // typo waiting to happen, and which way the cash went is the thing people
  // actually get wrong. The two combine into one signed figure on confirm.
  const [cashReceived, setCashReceived] = useState('');
  // What you paid, asked for ONLY when the row cannot answer it — a row
  // reconstructed from a vanished item, or one that never had a cost recorded.
  // Everywhere else this modal keeps showing the stored basis read-only: a sale
  // dialog is no place to quietly rewrite a purchase you already entered.
  const [paid, setPaid] = useState('');
  const [bootDir, setBootDir]     = useState('received');   // 'received' | 'paid'
  const [bootSource, setBootSource] = useState(null);

  useEffect(() => {
    const esc = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  const siteFee = settings?.sites?.find(s => s.value === platform) || null;
  // costUnknown is stamped by whatever built the row without a price; the
  // zero/blank test catches older rows that predate the flag.
  const askPaid = !!row.costUnknown || !(parseFloat(row.buyPrice) > 0);
  const basis   = askPaid ? paid : row.buyPrice;
  const profit  = calcProfit(basis, price, siteFee);

  // Live prices for auto-fill
  const modalLivePrice = (() => {
    if (!row.skin?.value) return null;
    const lp = getLivePrice(livePrices, row);
    const buffP = lp?.starting_at?.price ?? null;
    const csfKey = row.skin?.value && row.condition?.value
      ? `${row.skin.value} (${CONDITION_MAP[row.condition.value] || row.condition.value})`
      : row.skin?.value;
    const csfP = csfloatPrices?.[csfKey]?.price ?? null;
    return { buff: buffP, csf: csfP };
  })();

  const addFromInventory = item => {
    if (receivedItems.some(r => r.assetId === item.assetId)) return;
    // Extension items have `name` (clean) and `marketHashName`; Steam-API items have `market_hash_name`
    const name = item.name
      || (item.marketHashName || item.market_hash_name || '').replace(/ \((Factory New|Minimal Wear|Field-Tested|Well-Worn|Battle-Scarred)\)$/, '').trim();
    const cond = EXTERIOR_TO_COND[item.exterior] || null;
    const iconUrl = item.iconUrl || item.icon_url || null;
    setReceivedItems(prev => [...prev, { name, condition: cond, assetId: item.assetId, icon_url: iconUrl }]);
  };

  const addManual = () => {
    if (!stageName.trim()) return;
    setReceivedItems(prev => [...prev, { name: stageName.trim(), condition: stageCond, assetId: null }]);
    setStageName(''); setStageCond(null);
  };

  const removeReceived = idx => setReceivedItems(prev => prev.filter((_, i) => i !== idx));

  const canConfirm = mode === 'sold'
    ? parseFloat(price) > 0
    : true;

  const filteredInv = inventoryItems.filter(i =>
    i.assetId !== row.assetId &&
    (!invSearch.trim() || (i.name || i.marketHashName || i.market_hash_name || '').toLowerCase().includes(invSearch.toLowerCase()))
  ).map(i => ({ ...i, _claimed: claimedAssetIds.has(i.assetId) }));

  const inp = {
    boxSizing: 'border-box', width: '100%', padding: '8px 12px',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 10, color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '0.98rem', outline: 'none',
  };

  const itemLabel = row.skin?.value + (row.condition?.value ? ` (${row.condition.value})` : '');

  return (
    <div className="modal-backdrop" onClick={onClose} style={{ alignItems: 'flex-start', paddingTop: 60 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#0b1220', border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 18, boxShadow: '0 24px 70px rgba(0,0,0,0.65)',
        width: 'min(520px, 96%)', maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.04rem', fontWeight: 700, color: '#fff' }}>
              Mark as Sold / Traded
            </div>
            <div style={{ fontSize: '0.92rem', color: 'var(--text-dim)', marginTop: 3, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 340 }}>{itemLabel}</div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Mode toggle */}
          <div style={{ display: 'flex', gap: 8 }}>
            {[{ id: 'sold', label: 'Sold', color: '#f59e0b' }, { id: 'traded', label: 'Traded', color: '#818cf8' }].map(m => (
              <button key={m.id} onClick={() => setMode(m.id)} style={{
                flex: 1, padding: '9px', borderRadius: 10, cursor: 'pointer',
                fontFamily: 'var(--font-body)', fontSize: '0.99rem', fontWeight: 700,
                border: `1px solid ${mode === m.id ? m.color + '80' : 'rgba(255,255,255,0.10)'}`,
                background: mode === m.id ? m.color + '18' : 'rgba(255,255,255,0.04)',
                color: mode === m.id ? m.color : 'var(--text-dim)', transition: 'all 0.15s',
              }}>{m.label}</button>
            ))}
          </div>

          {/* What you paid — above the mode branches because both need it. On a
              sale it decides the profit; on a trade it is the basis that gets
              split across whatever came back, and a zero there would hand every
              received item a cost of nothing. Shown only when the row cannot
              answer for itself. */}
          {askPaid && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-dim)', marginBottom: 5,
                textTransform: 'uppercase', letterSpacing: '0.05em' }}>What you paid ($)</div>
              <input type="number" step="0.01" min="0" placeholder="0.00" value={paid}
                onChange={e => setPaid(e.target.value)} style={inp} />
              <div style={{ fontSize: '0.86rem', color: 'var(--text-dim)', marginTop: 5 }}>
                This item has no purchase price on record. Leave it blank if you
                genuinely paid nothing.
              </div>
            </div>
          )}

          {/* Sold form */}
          {mode === 'sold' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-dim)', marginBottom: 5,
                    textTransform: 'uppercase', letterSpacing: '0.05em' }}>Platform</div>
                  <select value={platform} onChange={e => setPlatform(e.target.value)}
                    style={{ ...inp, appearance: 'auto' }}>
                    <option value="">Select…</option>
                    {(settings?.sites || []).map(s => (
                      <option key={s.value} value={s.value}>{s.label?.split(' ')[0]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-dim)', marginBottom: 5,
                    textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sell Price ($)</div>
                  <input type="number" step="0.01" min="0" placeholder="0.00" value={price}
                    onChange={e => setPrice(e.target.value)} style={inp} autoFocus />
                  {(modalLivePrice?.buff != null || modalLivePrice?.csf != null) && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                      {modalLivePrice?.buff != null && (
                        <button type="button" onClick={() => setPrice(modalLivePrice.buff.toFixed(2))} style={{
                          padding: '3px 10px', borderRadius: 7, cursor: 'pointer', fontSize: '0.92rem', fontWeight: 600,
                          border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)',
                          color: 'var(--text-muted)', fontFamily: 'var(--font-body)',
                        }}>
                          Buff ${modalLivePrice.buff.toFixed(2)}
                        </button>
                      )}
                      {modalLivePrice?.csf != null && (
                        <button type="button" onClick={() => setPrice(modalLivePrice.csf.toFixed(2))} style={{
                          padding: '3px 10px', borderRadius: 7, cursor: 'pointer', fontSize: '0.92rem', fontWeight: 600,
                          border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)',
                          color: 'var(--text-muted)', fontFamily: 'var(--font-body)',
                        }}>
                          CSF ${modalLivePrice.csf.toFixed(2)}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {parseFloat(price) > 0 && (
                <div style={{ padding: '10px 14px', borderRadius: 10,
                  background: parseFloat(profit) >= 0 ? 'rgba(74,222,128,0.07)' : 'rgba(248,113,113,0.07)',
                  border: `1px solid ${parseFloat(profit) >= 0 ? 'rgba(74,222,128,0.20)' : 'rgba(248,113,113,0.20)'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.96rem' }}>
                    <span style={{ color: 'var(--text-dim)' }}>Buy</span>
                    <span style={{ color: 'var(--text)' }}>${parseFloat(basis || 0).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.96rem', marginTop: 3 }}>
                    <span style={{ color: 'var(--text-dim)' }}>Sell (after fees)</span>
                    <span style={{ color: 'var(--text)' }}>${(parseFloat(price) * (1 - (siteFee?.salesFee || 0) - (siteFee?.withdrawalFee || 0))).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.01rem', fontWeight: 700, marginTop: 6 }}>
                    <span style={{ color: 'var(--text-dim)' }}>Profit</span>
                    <span style={{ color: parseFloat(profit) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                      {parseFloat(profit) >= 0 ? '+' : ''}${parseFloat(profit).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Traded form */}
          {mode === 'traded' && (
            <>
              <div style={{ fontSize: '0.95rem', color: 'var(--text-dim)', fontWeight: 600 }}>What did you receive?</div>

              {receivedItems.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {receivedItems.map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                      background: 'rgba(129,140,248,0.08)', border: '1px solid rgba(129,140,248,0.22)',
                      borderRadius: 8 }}>
                      {item.icon_url && <img src={item.icon_url} alt="" style={{ width: 40, height: 26, objectFit: 'contain', flexShrink: 0 }} />}
                      <span style={{ flex: 1, fontSize: '0.97rem', color: '#c4b5fd', fontWeight: 600,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.name}{item.condition ? ` (${item.condition})` : ''}
                      </span>
                      <button onClick={() => removeReceived(i)} style={{ padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
                        border: '1px solid rgba(248,113,113,0.30)', background: 'rgba(248,113,113,0.08)',
                        color: '#f87171', fontFamily: 'var(--font-body)', fontSize: '0.95rem', fontWeight: 700 }}>×</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add mode toggle */}
              <div style={{ display: 'flex', gap: 6 }}>
                {['inventory', 'manual'].map(m => (
                  <button key={m} onClick={() => setAddMode(m)} style={{
                    padding: '5px 14px', borderRadius: 999, cursor: 'pointer',
                    fontFamily: 'var(--font-body)', fontSize: '0.92rem', fontWeight: 600,
                    border: `1px solid ${addMode === m ? 'rgba(129,140,248,0.50)' : 'rgba(255,255,255,0.10)'}`,
                    background: addMode === m ? 'rgba(129,140,248,0.12)' : 'rgba(255,255,255,0.04)',
                    color: addMode === m ? '#818cf8' : 'var(--text-dim)',
                  }}>{m === 'inventory' ? 'From inventory' : 'Manual entry'}</button>
                ))}
              </div>

              {/* From inventory */}
              {addMode === 'inventory' && (
                <div>
                  <input value={invSearch} onChange={e => setInvSearch(e.target.value)}
                    placeholder="Search current inventory…"
                    style={{ ...inp, marginBottom: 8 }} />
                  {filteredInv.length === 0 ? (
                    <div style={{ fontSize: '0.95rem', color: 'var(--text-dim)', textAlign: 'center', padding: '12px 0' }}>
                      {inventoryItems.length === 0 ? 'No inventory synced yet — use Manual entry' : 'No items match'}
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
                      {filteredInv.map(item => {
                        const sel     = receivedItems.some(r => r.assetId === item.assetId);
                        const claimed = item._claimed;
                        const blocked = sel || claimed;
                        const name = item.name
                          || (item.marketHashName || item.market_hash_name || '').replace(/ \((Factory New|Minimal Wear|Field-Tested|Well-Worn|Battle-Scarred)\)$/, '').trim();
                        const iconUrl = item.iconUrl || item.icon_url;
                        return (
                          <div key={item.assetId} onClick={() => !blocked && addFromInventory(item)} style={{
                            borderRadius: 8, cursor: blocked ? 'not-allowed' : 'pointer', overflow: 'hidden',
                            border: `1px solid ${sel ? 'rgba(129,140,248,0.55)' : claimed ? 'rgba(248,113,113,0.30)' : 'rgba(255,255,255,0.07)'}`,
                            background: sel ? 'rgba(129,140,248,0.10)' : claimed ? 'rgba(248,113,113,0.06)' : 'rgba(255,255,255,0.03)',
                            opacity: claimed ? 0.55 : 1, transition: 'border-color 0.1s, background 0.1s',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                              padding: '6px 4px 2px', background: 'rgba(0,0,0,0.18)' }}>
                              {iconUrl
                                ? <img src={iconUrl} alt="" style={{ width: 60, height: 40, objectFit: 'contain' }} loading="lazy" />
                                : <div style={{ width: 60, height: 40, background: 'rgba(255,255,255,0.04)', borderRadius: 4 }} />
                              }
                            </div>
                            <div style={{ padding: '4px 6px 6px' }}>
                              <div style={{ fontSize: '0.88rem', fontWeight: 600, color: sel ? '#818cf8' : '#fff',
                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.3 }}>
                                {name}
                              </div>
                              {claimed
                                ? <div style={{ fontSize: '0.83rem', color: '#f87171', fontWeight: 700, marginTop: 2 }}>In trade</div>
                                : item.exterior && <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', marginTop: 1 }}>{item.exterior}</div>
                              }
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Manual entry */}
              {addMode === 'manual' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ position: 'relative' }}>
                    <InlineSearch
                      searchableOptions={searchableOptions}
                      value={stageName}
                      onChange={setStageName}
                      placeholder="Search item name…"
                      inp={inp}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {COND_PILLS.map(cp => {
                      const active = stageCond === cp.value;
                      return (
                        <button key={cp.value} onClick={() => setStageCond(active ? null : cp.value)} style={{
                          padding: '4px 12px', borderRadius: 999, cursor: 'pointer',
                          fontFamily: 'var(--font-body)', fontSize: '0.92rem', fontWeight: 600,
                          border: `1px solid ${active ? 'rgba(129,140,248,0.55)' : 'rgba(255,255,255,0.12)'}`,
                          background: active ? 'rgba(129,140,248,0.14)' : 'rgba(255,255,255,0.04)',
                          color: active ? '#818cf8' : 'var(--text-muted)',
                        }}>{cp.short} <span style={{ opacity: 0.6, fontSize: '0.89rem' }}>{cp.value}</span></button>
                      );
                    })}
                  </div>
                  <button onClick={addManual} disabled={!stageName.trim()} style={{
                    alignSelf: 'flex-start', padding: '6px 16px', borderRadius: 8,
                    cursor: stageName.trim() ? 'pointer' : 'not-allowed',
                    border: '1px solid rgba(129,140,248,0.35)', background: 'rgba(129,140,248,0.10)',
                    color: '#818cf8', fontFamily: 'var(--font-body)', fontSize: '0.96rem', fontWeight: 700,
                    opacity: stageName.trim() ? 1 : 0.45,
                  }}>+ Add item</button>
                </div>
              )}

              {receivedItems.length === 0 && (
                <div style={{ fontSize: '0.92rem', color: 'var(--text-dim)', fontStyle: 'italic' }}>
                  Leave empty if you gave it away or details are unknown.
                </div>
              )}

              {/* Cash on the side. Trades are rarely even: you take money back
                  for giving up the better item, or add money to get one. */}
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                  Cash on the side <span style={{ color: 'var(--text-dim)', fontWeight: 400, textTransform: 'none' }}>(optional)</span>
                </div>

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  {[
                    { id: 'received', label: 'I received' },
                    { id: 'paid',     label: 'I paid' },
                  ].map(d => {
                    const active = bootDir === d.id;
                    return (
                      <button key={d.id} onClick={() => setBootDir(d.id)} style={{
                        padding: '4px 12px', borderRadius: 999, cursor: 'pointer',
                        fontFamily: 'var(--font-body)', fontSize: '0.92rem', fontWeight: 600,
                        border: `1px solid ${active ? 'rgba(129,140,248,0.55)' : 'rgba(255,255,255,0.12)'}`,
                        background: active ? 'rgba(129,140,248,0.14)' : 'rgba(255,255,255,0.04)',
                        color: active ? '#818cf8' : 'var(--text-muted)',
                      }}>{d.label}</button>
                    );
                  })}

                  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                    <span style={{ position: 'absolute', left: 10, fontSize: '0.98rem', color: 'var(--text-muted)', pointerEvents: 'none' }}>$</span>
                    <input type="number" min="0" step="0.01" value={cashReceived} onChange={e => setCashReceived(e.target.value)}
                      placeholder="0.00"
                      style={{ ...inp, width: 110, paddingLeft: 22 }} />
                  </div>
                </div>

                {/* Which wallet the cash moved through. Only asked once there is
                    cash to move, because an empty trade has no funding source. */}
                {parseFloat(cashReceived) > 0 && trackWallets && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.91rem', color: 'var(--text-dim)' }}>
                      {bootDir === 'paid' ? 'Paid from' : 'Received into'}
                    </span>
                    {[
                      trackBank && { id: 'bank', label: 'Cash' },
                      { id: 'crypto',  label: 'Crypto' },
                      { id: 'csfloat', label: 'CSFloat' },
                    ].filter(Boolean).map(s => {
                      const active = bootSource === s.id;
                      return (
                        <button key={s.id} onClick={() => setBootSource(s.id)} style={{
                          padding: '3px 10px', borderRadius: 999, cursor: 'pointer',
                          fontFamily: 'var(--font-body)', fontSize: '0.91rem', fontWeight: 600,
                          border: `1px solid ${active ? 'rgba(34,197,94,0.5)' : 'rgba(255,255,255,0.12)'}`,
                          background: active ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.04)',
                          color: active ? 'var(--green-dim)' : 'var(--text-muted)',
                        }}>{s.label}</button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)',
          display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 10, cursor: 'pointer',
            border: '1px solid rgba(255,255,255,0.10)', background: 'transparent',
            color: 'var(--text-muted)', fontFamily: 'var(--font-body)', fontSize: '0.98rem', fontWeight: 600 }}>
            Cancel
          </button>
          <button onClick={() => canConfirm && onConfirm({
            mode, platform, price: parseFloat(price) || 0, receivedItems,
            // Only present when the modal actually asked. undefined means "the
            // row already knows what it cost", which is not the same as zero.
            ...(askPaid && parseFloat(paid) > 0 ? { buyPrice: parseFloat(paid).toFixed(2) } : {}),
            // One signed figure out of the amount and the direction, so nothing
            // downstream has to combine them and none of it can combine them wrongly.
            boot: {
              amount: (parseFloat(cashReceived) || 0) * (bootDir === 'paid' ? -1 : 1),
              source: bootSource,
            },
          })}
            disabled={!canConfirm} style={{
              padding: '8px 22px', borderRadius: 10, border: 'none', fontFamily: 'var(--font-body)',
              fontSize: '0.99rem', fontWeight: 700, color: '#000',
              background: canConfirm
                ? (mode === 'sold' ? '#f59e0b' : '#818cf8')
                : 'rgba(255,255,255,0.12)',
              cursor: canConfirm ? 'pointer' : 'not-allowed',
            }}>
            {mode === 'sold' ? 'Mark Sold' : 'Mark Traded'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Inline search for MarkSoldTradedModal ─────────────────
function InlineSearch({ searchableOptions, value, onChange, placeholder, inp }) {
  const [q, setQ]       = useState(value || '');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const ref = useRef(null);

  const search = v => {
    if (!v.trim()) { setResults([]); setOpen(false); return; }
    const words = v.toLowerCase().split(/\s+/);
    const out = [];
    for (const opt of (searchableOptions || [])) {
      if (out.length >= 10) break;
      if (words.every(w => opt.value.toLowerCase().includes(w))) out.push(opt);
    }
    setResults(out); setOpen(out.length > 0);
  };

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <input type="text" value={q}
        onChange={e => { setQ(e.target.value); search(e.target.value); onChange(e.target.value); }}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        placeholder={placeholder}
        style={inp}
      />
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#0e1826',
          border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, zIndex: 99999,
          maxHeight: 180, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
          {results.map(opt => (
            <div key={opt.value} onMouseDown={() => { setQ(opt.value); onChange(opt.value); setOpen(false); }}
              style={{ padding: '7px 12px', cursor: 'pointer', fontSize: '0.97rem', color: 'var(--text-muted)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(74,222,128,0.08)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              {opt.value}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Skin Autocomplete ────────────────────────────────────
function SkinAutocomplete({ value, onChange, searchableOptions, placeholder }) {
  const [query, setQuery]         = useState(value?.label || '');
  const [results, setResults]     = useState([]);
  const [open, setOpen]           = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef(null);
  const dropRef  = useRef(null);

  // Keep query in sync when value is cleared externally
  useEffect(() => { if (!value) setQuery(''); }, [value]);

  const search = (q) => {
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    const out = [];
    for (const opt of searchableOptions) {
      if (out.length >= 15) break;
      const name = opt.value.toLowerCase();
      // Match all words anywhere in name OR searchable terms (handles 'ak', 'deagle' etc.)
      const byName  = words.every(w => name.includes(w));
      const byTerms = !byName && opt.searchableTerms.some(t => words.every(w => t.includes(w)));
      if (byName || byTerms) out.push(opt);
    }
    setResults(out);
    setOpen(out.length > 0);
    setActiveIdx(-1);
  };

  const handleChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    search(q);
    if (!q) onChange(null);
  };

  const pick = (opt) => {
    onChange({ value: opt.value, label: opt.value });
    setQuery(opt.value);
    setResults([]);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && activeIdx >= 0) { e.preventDefault(); pick(results[activeIdx]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  // Compute dropdown position from input bounding rect
  const [dropPos, setDropPos] = useState({});
  const reposition = () => {
    if (!inputRef.current) return;
    const r = inputRef.current.getBoundingClientRect();
    setDropPos({ position: 'fixed', top: r.bottom + 4, left: r.left, width: r.width, zIndex: 999999 });
  };

  useEffect(() => { if (open) reposition(); }, [open, results]);

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => results.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        placeholder={placeholder || 'Type to search skins…'}
        autoComplete="off"
        style={{
          width: '100%', padding: '9px 12px', boxSizing: 'border-box',
          background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)',
          borderRadius: 10, color: 'var(--text)', fontFamily: 'var(--font-body)',
          fontSize: '0.98rem', outline: 'none',
        }}
      />
      {open && results.length > 0 && (
        <div ref={dropRef} style={{
          ...dropPos,
          background: '#0e1826', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 12, boxShadow: '0 16px 40px rgba(0,0,0,0.60)',
          maxHeight: 280, overflowY: 'auto',
        }}>
          {results.map((opt, i) => (
            <div
              key={opt.value}
              onMouseDown={() => pick(opt)}
              onMouseEnter={() => setActiveIdx(i)}
              style={{
                padding: '9px 12px', cursor: 'pointer', fontSize: '0.98rem',
                color: i === activeIdx ? '#fff' : 'var(--text-muted)',
                background: i === activeIdx ? 'rgba(74,222,128,0.10)' : 'transparent',
                borderRadius: i === 0 ? '12px 12px 0 0' : i === results.length - 1 ? '0 0 12px 12px' : 0,
              }}
            >
              {opt.value}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Item Form Modal (Add + Edit) ─────────────────────────
function ItemFormModal({ isNew, initialRow, realIndex, onSave, onClose, onDelete, settings, searchableOptions, skinsData, livePrices, itemsWithConditions, liveKeys, switcher }) {
  const [form, setForm]       = useState({ ...initialRow });
  const [chartSrc, setChartSrc] = useState('buff'); // price history source toggle
  const [showExtras, setShowExtras]     = useState(false);
  const [showHistory, setShowHistory]   = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const ref = useRef(null);

  // True when the selected item has wear tiers (skins, gloves, knives).
  // False for stickers, graffiti, cases, keys, pins, music kits, agents, etc.
  const hasCondition = !form.skin?.value || itemsWithConditions.has(form.skin.value);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const esc = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', esc); };
  }, [onClose]);

  // Ctrl/Cmd+Enter saves without reaching for the mouse.
  useEffect(() => {
    const onKey = e => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); handleSave(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const profit = useMemo(() => calcProfit(form.buyPrice, form.sellPrice, form.siteFee), [form.buyPrice, form.sellPrice, form.siteFee]);

  const img      = getRowImage(form, skinsData);
  const lp       = getLivePrice(livePrices, form);
  const mktPrice = lp?.starting_at?.price;
  const buy      = parseFloat(form.buyPrice) || 0;

  const set = (field, val) => setForm(f => {
    const next = { ...f, [field]: val };
    // Clear condition when switching to a no-condition item
    if (field === 'skin' && val && !itemsWithConditions.has(val.value)) {
      next.condition = null;
    }
    return next;
  });

  const handleSave = () => {
    if (!form.skin) return;
    onSave({ ...form, profit: profit || '0' }, realIndex);
    onClose();
  };

  const handleDelete = () => { if (realIndex !== -1) onDelete(realIndex); onClose(); };

  const profitVal = profit !== '' ? parseFloat(profit) : null;

  // The banner shows realized profit once a sell price exists, otherwise the
  // mark-to-market gain, so the headline figure always answers "how is this
  // item doing?" as you type.
  const isSold      = profitVal !== null;
  const unrealized  = mktPrice != null && buy > 0 ? mktPrice - buy : null;
  const headline    = isSold ? profitVal : unrealized;
  const headlineCol = headline == null ? 'var(--text-dim)'
    : headline >= 0 ? (settings?.profitTextColor||'var(--green)') : (settings?.lossTextColor||'var(--red)');
  const headlineRoi = headline != null && buy > 0 ? (headline / buy * 100) : null;
  const money = (v) => `${v < 0 ? '−' : '+'}$${Math.abs(v).toFixed(2)}`;

  // Count of filled optional fields, surfaced on the collapsed section so
  // nothing hidden is ever forgotten about.
  const extrasFilled = [
    form.description, form.targetPrice, form.float, form.paintSeed,
  ].filter(v => v !== '' && v !== null && v !== undefined).length;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <motion.div
        ref={ref}
        className="imodal"
        initial={{ opacity: 0, y: 14, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header — always states which item is open */}
        <div className="imodal-head">
          <div className="imodal-thumb">
            {img ? <img src={img} alt="" /> : <span>No image</span>}
          </div>
          <div className="imodal-titles">
            <div className="imodal-eyebrow">{isNew ? 'Add item' : 'Editing'}</div>
            <div className="imodal-title">{form.skin?.value || 'Pick a skin to get started'}</div>
            <div className="imodal-sub">
              {form.condition?.value || (hasCondition ? 'No condition set' : 'No condition needed')}
              {mktPrice != null && ` · Buff $${mktPrice.toFixed(2)}`}
            </div>
          </div>
          {switcher}
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Live profit banner */}
        <div className="imodal-pl">
          <div className="imodal-pl-main">
            <div className="imodal-pl-label">
              {isSold ? 'Profit on this sale' : 'If you sold at market today'}
            </div>
            <div className="imodal-pl-value" style={{ color: headlineCol }}>
              {headline == null ? 'n/a' : money(headline)}
              {headlineRoi != null && (
                <span style={{ fontSize: '0.96rem', fontWeight: 700, marginLeft: 8, opacity: 0.75 }}>
                  {headlineRoi >= 0 ? '+' : '−'}{Math.abs(headlineRoi).toFixed(1)}%
                </span>
              )}
            </div>
          </div>
          <div className="imodal-pl-side">
            <div>Paid <b>{buy > 0 ? `$${buy.toFixed(2)}` : 'n/a'}</b></div>
            <div>
              {isSold
                ? <>Sold <b>${(parseFloat(form.sellPrice) || 0).toFixed(2)}</b></>
                : <>Market <b>{mktPrice != null ? `$${mktPrice.toFixed(2)}` : 'n/a'}</b></>}
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="imodal-body">

          {/* Skin */}
          <div>
            <label className="imodal-label">Skin</label>
            <SkinAutocomplete
              value={form.skin}
              onChange={v => set('skin', v)}
              searchableOptions={searchableOptions}
              placeholder={searchableOptions.length === 0 ? 'Loading skins…' : 'Type to search skins…'}
            />
          </div>

          {/* Condition pills */}
          <div style={{ opacity: hasCondition ? 1 : 0.45 }}>
            <label className="imodal-label">
              Condition
              {!hasCondition && <span className="imodal-na">Not applicable to this item</span>}
            </label>
            <div className="imodal-pills">
              {COND_PILLS.map(c => {
                const active = form.condition?.value === c.value;
                return (
                  <button
                    key={c.value}
                    className={`imodal-pill ${active && hasCondition ? 'on' : ''}`}
                    onClick={() => hasCondition && set('condition', { value:c.value, label:c.value })}
                    disabled={!hasCondition}
                    title={c.value}
                  >
                    {c.short}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Item Type pills */}
          <div>
            <label className="imodal-label">Category</label>
            <div className="imodal-pills">
              {(settings?.itemTypes || []).map(type => {
                const formTypes = getItemTypes(form);
                const active = formTypes.some(t => t.value === type.value);
                return (
                  <button key={type.value} className="imodal-pill" onClick={() => {
                    const next = active
                      ? formTypes.filter(t => t.value !== type.value)
                      : [...formTypes, { value:type.value, label:type.label, color:type.color }];
                    set('itemType', next);
                  }} style={active ? {
                    border: `1px solid ${type.color}80`,
                    background: type.color + '20',
                    color: type.color,
                  } : undefined}>
                    {type.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Prices */}
          <div className="imodal-grid2">
            <div>
              <label className="imodal-label">What you paid</label>
              <div className="imodal-money">
                <input className="imodal-input" type="number" step="0.01" min="0" value={form.buyPrice}
                  onChange={e => set('buyPrice', e.target.value)} placeholder="0.00" />
              </div>
              {settings?.trackWallets !== false && (
                <div style={{ marginTop: 8 }}>
                  <label className="imodal-label">Paid with</label>
                  <PaidWith value={form.paidWith} onChange={v => set('paidWith', v)}
                    allowBank={settings?.useBankWallet !== false} />
                </div>
              )}
            </div>
            <div>
              <label className="imodal-label">What you sold it for</label>
              <div className="imodal-money">
                <input className="imodal-input" type="number" step="0.01" min="0" value={form.sellPrice}
                  onChange={e => set('sellPrice', e.target.value)} placeholder="Empty while holding" />
              </div>
            </div>
          </div>

          {/* Marketplace */}
          <div>
            <label className="imodal-label">
              Sold on
              {form.siteFee?.value != null && (
                <span className="imodal-na">fee applied to profit</span>
              )}
            </label>
            <Select
              value={form.siteFee} onChange={v => set('siteFee', v)}
              options={settings?.sites} isClearable classNamePrefix="Select"
              placeholder="Pick a marketplace…" menuPlacement="auto"
              menuPortalTarget={document.body} menuPosition="fixed"
              styles={{ menuPortal: base => ({ ...base, zIndex: 100000 }) }}
            />
          </div>

          {/* Optional details, collapsed so the essentials stay above the fold */}
          <div className="imodal-disc">
            <button
              type="button"
              className={`imodal-disc-btn ${showExtras ? 'open' : ''}`}
              onClick={() => setShowExtras(v => !v)}
            >
              Notes, alerts &amp; float
              <span className="count">
                {extrasFilled > 0 ? `${extrasFilled} set` : 'optional'}
              </span>
              <span className="chev">▼</span>
            </button>
            {showExtras && (
              <div className="imodal-disc-body">
                <div>
                  <label className="imodal-label">Notes</label>
                  <input className="imodal-input" type="text" value={form.description || ''}
                    onChange={e => set('description', e.target.value)}
                    placeholder="Sticker combos, why you bought it…" />
                </div>
                <div className="imodal-grid3">
                  <div>
                    <label className="imodal-label">Alert at</label>
                    <div className="imodal-money">
                      <input className="imodal-input" type="number" step="0.01" min="0" value={form.targetPrice || ''}
                        onChange={e => set('targetPrice', e.target.value)} placeholder="0.00" />
                    </div>
                  </div>
                  <div>
                    <label className="imodal-label">Float</label>
                    <input className="imodal-input" type="number" step="0.000001" min="0" max="1" value={form.float ?? ''}
                      onChange={e => set('float', e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder="0.000000" />
                  </div>
                  <div>
                    <label className="imodal-label">Pattern</label>
                    <input className="imodal-input" type="number" step="1" min="0" max="1000" value={form.paintSeed ?? ''}
                      onChange={e => set('paintSeed', e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="0–1000" />
                  </div>
                </div>
                {form.inspectLink && (
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <a className="imodal-link" href={form.inspectLink} target="_blank" rel="noreferrer"
                      style={{ color:'#818cf8', border:'1px solid rgba(129,140,248,0.30)', background:'rgba(129,140,248,0.08)' }}>
                      Inspect in game ↗
                    </a>
                    <a className="imodal-link"
                      href={`https://steamcommunity.com/market/listings/730/${encodeURIComponent((form.skin?.value || '') + (form.condition?.value ? ` (${CONDITION_MAP[form.condition.value] || form.condition.value})` : ''))}`}
                      target="_blank" rel="noreferrer"
                      style={{ color:'#38bdf8', border:'1px solid rgba(56,189,248,0.30)', background:'rgba(56,189,248,0.08)' }}>
                      Steam Market ↗
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Price history chart */}
          {form.priceHistory?.length >= 2 && !isNew && (() => {
            // Normalise: old snapshots used `price`, new ones use `buffPrice`/`csfloatPrice`
            const chartData  = form.priceHistory.map(p => ({
              date:         p.date,
              buffPrice:    p.buffPrice    ?? p.price ?? null,
              csfloatPrice: p.csfloatPrice ?? null,
            }));
            const hasCSF    = chartData.some(p => p.csfloatPrice != null);
            const dataKey   = chartSrc === 'buff' ? 'buffPrice' : 'csfloatPrice';
            const color     = chartSrc === 'buff' ? '#4ade80' : '#818cf8';
            const srcLabel  = chartSrc === 'buff' ? 'Buff163' : 'CSFloat';
            const validData = chartData.filter(p => p[dataKey] != null);
            return (
              <div className="imodal-disc">
                <button
                  type="button"
                  className={`imodal-disc-btn ${showHistory ? 'open' : ''}`}
                  onClick={() => setShowHistory(v => !v)}
                >
                  Price history
                  <span className="count">{form.priceHistory.length} days recorded</span>
                  <span className="chev">▼</span>
                </button>
                {showHistory && (
                <div className="imodal-disc-body">
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:2 }}>
                  <div style={{ flex:1, fontSize:'0.91rem', color:'var(--text-dim)' }}>
                    Dashed line marks what you paid
                  </div>
                  {[{ id:'buff', label:'Buff163' }, { id:'csfloat', label:'CSFloat' }].map(src => {
                    const active    = chartSrc === src.id;
                    const c         = src.id === 'buff' ? '#4ade80' : '#818cf8';
                    const disabled  = src.id === 'csfloat' && !hasCSF;
                    return (
                      <button key={src.id} onClick={() => !disabled && setChartSrc(src.id)}
                        disabled={disabled}
                        style={{
                          padding:'3px 10px', borderRadius:8, cursor: disabled ? 'not-allowed' : 'pointer',
                          fontFamily:'var(--font-body)', fontSize:'0.92rem', fontWeight:600,
                          opacity: disabled ? 0.4 : 1,
                          border:`1px solid ${active ? c+'66' : 'rgba(255,255,255,0.10)'}`,
                          background: active ? c+'18' : 'rgba(255,255,255,0.04)',
                          color: active ? c : 'var(--text-muted)',
                        }}>{src.label}</button>
                    );
                  })}
                </div>
                <div style={{ background:'rgba(0,0,0,0.20)', borderRadius:10, padding:'12px 4px 8px', border:'1px solid var(--border)' }}>
                  <ResponsiveContainer width="100%" height={130}>
                    <AreaChart data={validData} margin={{ top:4, right:16, left:0, bottom:0 }}>
                      <defs>
                        <linearGradient id="phGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={color} stopOpacity={0.22} />
                          <stop offset="95%" stopColor={color} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                      <XAxis dataKey="date" tick={{ fill:'#6b7280', fontSize:13.5 }} axisLine={false} tickLine={false}
                        interval="preserveStartEnd" tickFormatter={d => d.slice(5)} />
                      <YAxis tick={{ fill:'#6b7280', fontSize:13.5 }} axisLine={false} tickLine={false}
                        tickFormatter={v => `$${v}`} width={46} domain={['auto','auto']} />
                      <Tooltip formatter={v => [`$${parseFloat(v).toFixed(2)}`, srcLabel]}
                        contentStyle={{ background:'#0e1826', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, fontSize:'0.95rem' }}
                        labelStyle={{ color:'var(--text-muted)' }} />
                      {buy > 0 && <ReferenceLine y={buy} stroke="rgba(245,158,11,0.60)" strokeDasharray="4 3"
                        label={{ value:`Cost $${buy.toFixed(2)}`, fill:'#f59e0b', fontSize:13.5, position:'insideTopLeft' }} />}
                      <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={1.5}
                        fill="url(#phGrad)" dot={false} activeDot={{ fill:color, r:3, strokeWidth:0 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                </div>
                )}
              </div>
            );
          })()}

          <div style={{ fontSize:'0.91rem', color:'var(--text-dim)', textAlign:'right' }}>
            Added {new Date(form.timestamp || Date.now()).toLocaleDateString()}
          </div>
        </div>

        {/* Footer actions */}
        <div className="imodal-foot">
          {!isNew && (
            confirmDelete ? (
              <>
                <button className="imodal-btn danger" onClick={handleDelete}>Yes, delete</button>
                <button className="imodal-btn ghost" onClick={() => setConfirmDelete(false)}>Keep</button>
              </>
            ) : (
              <button className="imodal-btn danger" onClick={() => setConfirmDelete(true)}>Delete</button>
            )
          )}
          {isNew && liveKeys && form.skin?.value && (() => {
            const key = `${form.skin.value}__${form.condition?.value || ''}`;
            if (!liveKeys.has(key)) return null;
            return <div className="imodal-warn">Already in your Live tracker, so this may be a duplicate</div>;
          })()}
          <div style={{ flex:1 }} />
          {!confirmDelete && (
            <span className="imodal-hint"><kbd>Ctrl</kbd>+<kbd>Enter</kbd> to save</span>
          )}
          <button className="imodal-btn ghost" onClick={onClose}>Cancel</button>
          <button className="imodal-btn primary" onClick={handleSave} disabled={!form.skin}
            title={!form.skin ? 'Pick a skin first' : undefined}>
            {isNew ? 'Add item' : 'Save changes'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Navbar ───────────────────────────────────────────────
// Two skins share the same markup: the classic dark chrome, and 'zone' —
// the neo-brutalist board look from Overview 7 (see ZoneTheme.css).
function AppNavbar({ username, account, onSettings, onLogout, onProfile, profileActive, alertCount, pendingTradeCount, navTheme, onToggleTheme, zoneWidth, onToggleWidth, wallets, onSaveWallet, onRefreshCrypto, onLoadWalletLedger, walletTransfers, cryptoIsAutomatic, onResolveTransfer, onDepositWallet, useBankWallet }) {
  const zone = navTheme === 'zone';
  return (
    <nav className={`app-navbar ${zone ? 'zn-nav' : ''}`}>
      <div className="app-navbar-brand">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>
        </svg>
        CS2 Profit Tracker
      </div>
      <div className="app-navbar-right">
        <WalletChip wallets={wallets} onSave={onSaveWallet} onRefreshCrypto={onRefreshCrypto}
                    onLoadLedger={onLoadWalletLedger} transfers={walletTransfers}
                    cryptoIsAutomatic={cryptoIsAutomatic} onResolveTransfer={onResolveTransfer}
                    onDeposit={onDepositWallet} useBank={useBankWallet} zone={zone} />
        {/* A Steam-created account's username is `steam_<17 digits>`, which is
            the primary key and not something to show anyone. The persona name
            is what they recognise; the raw username is the fallback for
            password accounts, which have no Steam name of their own.
            The chip is also the way into the Profile page — it used to be a
            tab, but the account lives up here, so clicking yourself is where
            people look for it. */}
        <button className={`app-navbar-user${profileActive ? ' active' : ''}`}
                onClick={onProfile}
                title={`${username} — open your profile`}>
          {account?.avatarUrl && (
            <img src={account.avatarUrl} alt="" width={20} height={20}
                 style={{ borderRadius: '50%', marginRight: 6, verticalAlign: -5 }} />
          )}
          {account?.steamName || username}
        </button>
        {alertCount > 0 && (
          <span className="zn-nav-pill alerts" style={{ fontSize:'0.91rem', fontWeight:700, color:'var(--green)', background:'rgba(74,222,128,0.12)',
            border:'1px solid rgba(74,222,128,0.30)', borderRadius:999, padding:'2px 8px' }}>
            {alertCount} alert{alertCount!==1?'s':''}
          </span>
        )}
        {pendingTradeCount > 0 && (
          <span className="zn-nav-pill trades" style={{ fontSize:'0.91rem', fontWeight:700, color:'#818cf8', background:'rgba(129,140,248,0.12)',
            border:'1px solid rgba(129,140,248,0.30)', borderRadius:999, padding:'2px 8px' }}>
            ⏳ {pendingTradeCount} trade{pendingTradeCount!==1?'s':''}
          </span>
        )}
        {/* Width only affects the Zone board, so it hides in Classic. */}
        {zone && (
          <button className="zn-theme-btn width" onClick={onToggleWidth}
            title={zoneWidth === 'wide'
              ? 'The board runs full width. Click to box it back in.'
              : 'The board is boxed in the middle. Click to use the full window.'}>
            {zoneWidth === 'wide' ? '⇹ Wide' : '⇹ Boxed'}
          </button>
        )}
        <button className="zn-theme-btn" onClick={onToggleTheme}
          title={zone
            ? 'Zone style. Click for the classic dark look; the data stays the same.'
            : 'Classic style. Click for the Profit Zone board; the data stays the same.'}>
          {zone ? '✦ Zone' : '✦ Classic'}
        </button>
        <button className="app-navbar-btn settings" onClick={onSettings}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
          Settings
        </button>
        <button className="app-navbar-btn logout" onClick={onLogout}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Logout
        </button>
      </div>
    </nav>
  );
}

// ─── Tab Bar ──────────────────────────────────────────────
// One tab per page. Tabs that have a Zone-theme twin carry `zoneLabel` /
// `zoneIcon`; the Classic/Zone switch in the navbar picks which component the
// tab renders, so the board no longer needs a duplicate set of tabs.
function TabBar({ active, onChange, pricesLoaded, soldCount, tradedCount, investCount, lentCount, lendingEnabled, navTheme }) {
  const zone = navTheme === 'zone';
  const tabs = [
    { id:'overview3', label:'Overview', zoneLabel:'Big Board',
      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>,
      zoneIcon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18"/><path d="M3 9h18M9 21V9"/></svg> },
    { id:'overview5', label:'Overview 5', hidden:true,
      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h4M4 4v4M20 4h-4M20 4v4M4 20h4M4 20v-4M20 20h-4M20 20v-4"/><circle cx="12" cy="12" r="3"/></svg> },
    { id:'overview7', label:'Overview 7', hidden:true,
      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l2.4 4.8 5.6.8-4 3.9.9 5.5-4.9-2.6-4.9 2.6.9-5.5-4-3.9 5.6-.8z"/></svg> },
    { id:'analytics2', label:'Analytics', zoneLabel:'Report',
      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 16v-4M12 8h.01"/></svg>,
      zoneIcon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="3" width="16" height="18"/><path d="M8 8h8M8 12h8M8 16h5"/></svg> },
    { id:'tracker2',  label:'Live', zoneLabel:'Armory',
      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="8" height="8" rx="2"/><rect x="13" y="3" width="8" height="8" rx="2"/><rect x="3" y="13" width="8" height="8" rx="2"/><rect x="13" y="13" width="8" height="8" rx="2"/></svg>,
      zoneIcon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="8" height="8"/><rect x="13" y="3" width="8" height="8"/><rect x="3" y="13" width="8" height="8"/><rect x="13" y="13" width="8" height="8"/></svg> },
    { id:'tracker3',  label:'Live 3', hidden:true,
      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h18M3 10h18M3 15h18M3 20h12"/></svg> },
    { id:'sold2',     label:'Sold 2', hidden:true,
      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 3h16v18l-2-1.5L16 21l-2-1.5L12 21l-2-1.5L8 21l-2-1.5L4 21z"/><path d="M8 8h8M8 12h8"/></svg> },
    { id:'sold3',     label:'Sold', zoneLabel:'Tickets', badge: soldCount,
      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8a2 2 0 0 0 0 8v3h18v-3a2 2 0 0 1 0-8V5H3z"/><path d="M13 5v2M13 11v2M13 17v2"/></svg> },
    { id:'traded',    label:'Traded', hidden:true,
      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg> },
    { id:'traded2',   label:'Traded 2', zoneLabel:'Swaps', badge: tradedCount,
      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3"/><path d="M7 12h10M14 9l3 3-3 3"/></svg>,
      zoneIcon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 12h10M14 9l3 3-3 3"/><rect x="3" y="4" width="18" height="16"/></svg> },
    // Optional page: `hidden` is driven by the setting rather than a constant,
    // which is what makes it a switch instead of a code edit.
    { id:'lending',   label:'Lending', zoneLabel:'On Loan', hidden: !lendingEnabled, badge: lentCount,
      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-6"/><path d="M12 3v11"/><polyline points="8 7 12 3 16 7"/></svg>,
      zoneIcon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="10" width="18" height="11"/><path d="M12 3v7"/><polyline points="8 7 12 3 16 7"/></svg> },
    { id:'history',   label:'History', zoneLabel:'Paper Trail',
      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/><path d="M12 7v5l3 2"/></svg>,
      zoneIcon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 3h14v16l-2.3-1.6L14.4 19 12 17.4 9.6 19l-2.3-1.6L5 19z"/><path d="M9 8h6M9 12h6"/></svg> },
    { id:'wishlist2', label:'Wishlist', zoneLabel:'Watchboard',
      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4.5"/><circle cx="12" cy="12" r="0.5"/></svg> },
    // A calculator, not a view over the tracker's data: it prices a basket you
    // assemble by hand, so it carries no badge and reads nothing off `rows`.
    { id:'pricer',    label:'Pricer', zoneLabel:'Quote Desk',
      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.6 13.4 12 22l-9-9V4a1 1 0 0 1 1-1h9z"/><circle cx="7.5" cy="7.5" r="1.2"/></svg>,
      zoneIcon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16"/><path d="M7 9h10M7 13h6M15 17h2"/></svg> },
    { id:'investments', label:'Investments (old)', hidden:true,
      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg> },
    { id:'investments2', label:'Investments', zoneLabel:'Vault', badge: investCount,
      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="18" height="14" rx="2"/><circle cx="12" cy="13" r="3.5"/><path d="M12 11v2l1.5 1"/></svg>,
      zoneIcon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16"/><circle cx="12" cy="12" r="4"/><path d="M12 10v2l1.5 1"/></svg> },
    // Reached from the user chip in the navbar, not from the bar — the page
    // still renders, it just has no tab of its own.
    { id:'profile',   label:'Profile', zoneLabel:'ID Card', hidden:true,
      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1"/></svg>,
      zoneIcon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14"/><circle cx="9" cy="11" r="2.5"/><path d="M5 17c.8-1.8 2.3-2.8 4-2.8s3.2 1 4 2.8M15 9h4M15 13h4"/></svg> },
    { id:'extension', label:'Inventory', zoneLabel:'Locker',
      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2v-4M9 21H5a2 2 0 0 1-2-2v-4m0 0h18"/></svg>,
      zoneIcon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="3" width="16" height="18"/><path d="M14 3v18"/><path d="M17 11v2"/></svg> },
  ];
  return (
    <div className={`tab-bar ${zone ? 'zn-nav' : ''}`}>
      {/* `hidden` tabs keep their entry and their render branch so they can be
          brought back by deleting one flag; they just stay out of the bar. */}
      {tabs.filter(t => !t.hidden).map(t => (
        <button key={t.id} className={`tab-btn ${active===t.id?'active':''}`} onClick={() => onChange(t.id)}>
          {(zone && t.zoneIcon) || t.icon}{zone && t.zoneLabel ? t.zoneLabel : t.label}
          {t.badge > 0 && (
            <span style={{
              marginLeft: 4, padding: '1px 7px', borderRadius: zone ? 0 : 999, fontSize: '0.87rem',
              fontWeight: 700,
              background: zone ? '#c9f65a' : 'rgba(74,222,128,0.16)',
              border: zone ? '2px solid #131313' : 'none',
              color: zone ? '#131313' : 'var(--green)',
            }}>{t.badge}</span>
          )}
          {active === t.id && !zone && (
            <motion.span
              layoutId="tab-underline"
              className="tab-underline"
              transition={{ type: 'spring', stiffness: 480, damping: 34 }}
            />
          )}
        </button>
      ))}
      {pricesLoaded && (
        zone ? (
          <span className="zn-live-pill">
            <span style={{ width:7, height:7, background:'#131313', display:'inline-block' }} />
            Buff 163 prices
          </span>
        ) : (
          <span style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:5, fontSize:'0.92rem', color:'var(--green)', paddingRight:4 }}>
            <span style={{ width:6, height:6, borderRadius:'50%', background:'var(--green)', display:'inline-block' }} />
            Buff 163 prices
          </span>
        )
      )}
    </div>
  );
}

// ─── Bulk Add Modal ───────────────────────────────────────
// Wears either theme via ModalTheme.css: variant="zone" or "classic".
function BulkAddModal({ onClose, onAdd, settings, searchableOptions, variant = 'classic' }) {
  const [text, setText] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const esc = e => { if (e.key==='Escape') onClose(); };
    const clickOut = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('keydown', esc);
    document.addEventListener('mousedown', clickOut);
    return () => { document.body.style.overflow=''; document.removeEventListener('keydown', esc); document.removeEventListener('mousedown', clickOut); };
  }, [onClose]);

  const parseCondition = s => {
    if (!s) return { value:'Factory New', label:'Factory New' };
    const l = s.toLowerCase().trim();
    const map = { fn:'Factory New', mw:'Minimal Wear', ft:'Field Tested', ww:'Well Worn', bs:'Battle Scarred' };
    if (map[l]) return { value:map[l], label:map[l] };
    const found = COND_PILLS.find(c => c.value.toLowerCase().includes(l) || c.short.toLowerCase() === l);
    return found ? { value:found.value, label:found.value } : { value:'Factory New', label:'Factory New' };
  };

  const findSkin = name => {
    const clean = name.trim().replace(/-/g,' ').replace(/\s+/g,' ').toLowerCase();
    const match = searchableOptions.find(o =>
      o.searchableTerms.includes(clean) ||
      o.searchableTerms.some(t => clean.split(' ').every(w => t.includes(w)))
    );
    return match ? { value:match.value, label:match.value } : null;
  };

  const handleAdd = () => {
    const lines = text.split('\n').filter(l => l.trim());
    const items = lines
      .map(l => { const [a='',b=''] = l.split(',').map(p=>p.trim()); return { skin:findSkin(a), condition:parseCondition(b) }; })
      .filter(i => i.skin)
      .map(i => ({ ...createEmptyRow(settings), ...i }));
    if (items.length) { onAdd(items); onClose(); }
  };

  return (
    <div className={`mdl-overlay ${variant}`}>
      <div className="mdl-panel narrow" ref={ref} role="dialog" aria-modal="true" aria-label="Bulk add items">
        <div className="mdl-head">
          <div className="mdl-head-txt">
            <div className="mdl-title">Bulk Add Items</div>
            <div className="mdl-sub">Skin Name, Condition. One per line.</div>
          </div>
          <button className="mdl-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="mdl-body">
          <div className="mdl-stack">
            <div className="mdl-card dash">
              <div className="mdl-card-title">Example</div>
              <div className="mdl-mono">Flip Night, FN<br/>AK Redline, FT<br/>AWP Asiimov, FT</div>
            </div>
            <textarea className="mdl-area" value={text} onChange={e => setText(e.target.value)}
              placeholder={'Flip Night, FN\nAK Redline, FT\nAWP Asiimov, FT'} rows={10} />
            <div className="mdl-save end"><button className="mdl-btn primary wide" onClick={handleAdd}>Add Items</button></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Settings Modal ───────────────────────────────────────
// Wears either theme: variant="zone" for the paper skin, "classic" for the
// dark one. All styling lives in ModalTheme.css.
function SettingsModal({ onClose, settings, onUpdate, rows, onRestore, cryptoWallet, onRefreshCrypto, variant = 'classic' }) {
  const [local, setLocal]       = useState(settings);
  const [tab, setTab]           = useState('types');
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState('#ffffff');
  const [nSN, setNSN]           = useState('');
  const [nSF, setNSF]           = useState('');
  const [nWF, setNWF]           = useState('');
  const ref = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const esc = e => { if (e.key==='Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => { document.body.style.overflow=''; document.removeEventListener('keydown', esc); };
  }, [onClose]);

  const save = () => { onUpdate(local); onClose(); };
  const updateType = (i, f, v) => { const t=[...local.itemTypes]; t[i]={...t[i],[f]:v}; setLocal(p=>({...p,itemTypes:t})); };
  const addType = () => {
    if (!newLabel.trim()) return;
    const t = { value:newLabel.replace(/\s+/g,'').toLowerCase(), label:newLabel, color:newColor, showInOverview:true };
    setLocal(p => ({...p, itemTypes:[...p.itemTypes, t]}));
    setNewLabel(''); setNewColor('#ffffff');
  };
  const removeType = i => setLocal(p => ({...p, itemTypes:p.itemTypes.filter((_,idx)=>idx!==i)}));
  const addSite = () => {
    if (!nSN.trim() || !nSF || !nWF) return;
    const site = { value:nSN.toLowerCase().replace(/\s+/g,''), label:`${nSN} (${(parseFloat(nSF)*100).toFixed(2)}% / ${(parseFloat(nWF)*100).toFixed(2)}%)`, salesFee:parseFloat(nSF), withdrawalFee:parseFloat(nWF) };
    if (!local.sites.some(s=>s.value===site.value)) { setLocal(p=>({...p,sites:[...p.sites,site]})); setNSN('');setNSF('');setNWF(''); }
  };
  const removeSite = i => setLocal(p => ({...p, sites:p.sites.filter((_,idx)=>idx!==i)}));

  // No click-outside close: the panes hold unsaved edits until Save.
  return (
    <div className={`mdl-overlay ${variant}`}>
      <div className="mdl-panel" ref={ref} role="dialog" aria-modal="true" aria-label="Settings">
        <div className="mdl-head">
          <div className="mdl-head-txt">
            <div className="mdl-title">Settings</div>
            <div className="mdl-sub">Customize your tracker</div>
          </div>
          <button className="mdl-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="mdl-tabs">
          {[{id:'types',label:'Item Types'},{id:'sites',label:'Sites & Fees'},{id:'colors',label:'Colors'},{id:'general',label:'General'},{id:'crypto',label:'Crypto Wallets'},{id:'backup',label:'Backup'}].map(t=>(
            <button key={t.id} className={`mdl-tab ${tab===t.id?'on':''}`} onClick={()=>setTab(t.id)}>{t.label}</button>
          ))}
        </div>
        <div className="mdl-body">
          {tab==='types' && (
            <div className="mdl-stack">
              {local.itemTypes.map((type,i)=>(
                <div key={i} className="mdl-card row">
                  <input type="color" className="mdl-color" value={type.color} onChange={e=>updateType(i,'color',e.target.value)} />
                  <input type="text" className="mdl-input" value={type.label} onChange={e=>updateType(i,'label',e.target.value)} style={{ flex:1,minWidth:100 }} />
                  <label className="mdl-check">
                    <input type="checkbox" checked={type.overrideProfitLoss||false} onChange={e=>updateType(i,'overrideProfitLoss',e.target.checked)} /> Override P/L
                  </label>
                  {i>=4&&<button className="mdl-btn danger sm" onClick={()=>removeType(i)}>Remove</button>}
                </div>
              ))}
              <div className="mdl-card dash row">
                <input type="color" className="mdl-color" value={newColor} onChange={e=>setNewColor(e.target.value)} />
                <input type="text" className="mdl-input" value={newLabel} onChange={e=>setNewLabel(e.target.value)} placeholder="Type name…" style={{ flex:1,minWidth:100 }} />
                <button className="mdl-btn primary" onClick={addType}>Add</button>
              </div>
              <div className="mdl-save"><button className="mdl-btn primary wide" onClick={save}>Save Changes</button></div>
            </div>
          )}
          {tab==='sites' && (
            <div className="mdl-stack">
              {local.sites.map((site,i)=>(
                <div key={i} className="mdl-card split">
                  <div>
                    <div className="mdl-card-title">{site.label}</div>
                    <div className="mdl-card-hint">Sales {(site.salesFee*100).toFixed(2)}% · Withdrawal {(site.withdrawalFee*100).toFixed(2)}%</div>
                  </div>
                  <button className="mdl-btn danger sm" onClick={()=>removeSite(i)}>Remove</button>
                </div>
              ))}
              <div className="mdl-card dash">
                <div className="mdl-fields sites">
                  <div><span className="mdl-label">Site Name</span><input type="text" className="mdl-input" value={nSN} onChange={e=>setNSN(e.target.value)} placeholder="Site name" /></div>
                  <div><span className="mdl-label">Sales Fee</span><input type="number" className="mdl-input" value={nSF} onChange={e=>setNSF(e.target.value)} placeholder="0.025" step="0.001" min="0" max="1" /></div>
                  <div><span className="mdl-label">Withdrawal</span><input type="number" className="mdl-input" value={nWF} onChange={e=>setNWF(e.target.value)} placeholder="0.01" step="0.001" min="0" max="1" /></div>
                  <button className="mdl-btn primary" onClick={addSite}>Add</button>
                </div>
              </div>
              <div className="mdl-save"><button className="mdl-btn primary wide" onClick={save}>Save Changes</button></div>
            </div>
          )}
          {tab==='colors' && (
            <div className="mdl-grid2">
              {[{key:'backgroundColor',label:'Background'},{key:'profitColor',label:'Profit Row'},{key:'lossColor',label:'Loss Row'},{key:'profitTextColor',label:'Profit Text'},{key:'lossTextColor',label:'Loss Text'}].map(({key,label})=>(
                <div key={key} className="mdl-card">
                  <div className="mdl-card-title">{label}</div>
                  <div className="mdl-controls">
                    <input type="color" className="mdl-color" value={local[key]||'#ffffff'} onChange={e=>setLocal(p=>({...p,[key]:e.target.value}))} />
                    <span className="mdl-mono">{local[key]}</span>
                  </div>
                </div>
              ))}
              <div className="mdl-span mdl-save"><button className="mdl-btn primary wide" onClick={save}>Save All</button></div>
            </div>
          )}
          {tab==='general' && (
            <div className="mdl-stack gap">
              <div className="mdl-card">
                <div className="mdl-card-title">Break-even ROI target (%)</div>
                <div className="mdl-card-hint">
                  Live cards show the sell price you need to reach this % return. Cards glow green when market price already exceeds it.
                </div>
                <div className="mdl-controls">
                  <input type="number" className="mdl-input narrow" min="0" max="1000" step="1"
                    value={local.targetRoiPct ?? 10}
                    onChange={e => setLocal(p => ({ ...p, targetRoiPct: parseFloat(e.target.value) || 0 }))}
                  />
                  <span className="mdl-unit">%</span>
                  {local.targetRoiPct > 0 && (
                    <span className="mdl-eg">
                      e.g. buy $100 → need ${(100 * (1 + (local.targetRoiPct ?? 10) / 100)).toFixed(2)} to sell
                    </span>
                  )}
                </div>
              </div>
              <div className="mdl-card">
                <div className="mdl-card-title">Cash wallet</div>
                <div className="mdl-card-hint">
                  Nothing can read a bank, so the Cash row is the one balance you have to keep current
                  by hand. Off, it disappears from the wallet and stops being offered as a payment
                  source. CSFloat and crypto are unaffected, they read themselves. Your balance is kept,
                  so turning it back on restores it.
                </div>
                <div className="mdl-controls">
                  <span className={`mdl-eg ${local.useBankWallet !== false ? 'mdl-ok' : ''}`}>
                    {local.useBankWallet !== false ? 'Shown in your wallet' : 'Hidden'}
                  </span>
                  <button className={`mdl-btn ${local.useBankWallet !== false ? 'danger' : 'primary'}`}
                    onClick={() => setLocal(p => ({ ...p, useBankWallet: p.useBankWallet === false }))}>
                    {local.useBankWallet !== false ? 'Turn off' : 'Turn on'}
                  </button>
                </div>
              </div>

              <div className="mdl-card">
                <div className="mdl-card-title">Track which wallet paid</div>
                <div className="mdl-card-hint">
                  On, buying an item or paying cash on a trade also comes out of the wallet you name,
                  so the balances stay current. Off, you are never asked: amounts are still recorded and
                  your cost basis is still right, there is just no cash balance to keep up to date.
                  CSFloat and crypto are read automatically either way.
                </div>
                <div className="mdl-controls">
                  <span className={`mdl-eg ${local.trackWallets !== false ? 'mdl-ok' : ''}`}>
                    {local.trackWallets !== false ? 'Wallets follow your trades' : 'Amounts only'}
                  </span>
                  <button className={`mdl-btn ${local.trackWallets !== false ? 'danger' : 'primary'}`}
                    onClick={() => setLocal(p => ({ ...p, trackWallets: p.trackWallets === false }))}>
                    {local.trackWallets !== false ? 'Turn off' : 'Turn on'}
                  </button>
                </div>
              </div>

              <div className="mdl-card">
                <div className="mdl-card-title">Lending page</div>
                <div className="mdl-card-hint">
                  Track skins you have lent to friends. A lend is not a sale or a trade: the item
                  keeps its cost basis and still counts toward your portfolio, it is just not in your
                  inventory. On, you get a Lending tab and a "Lend out" action on trades and in the
                  Locker. Off, the page is hidden and nothing you have already recorded is deleted.
                </div>
                <div className="mdl-controls">
                  <span className={`mdl-eg ${local.lendingEnabled ? 'mdl-ok' : ''}`}>
                    {local.lendingEnabled ? 'Tracking loans' : 'Hidden'}
                  </span>
                  <button className={`mdl-btn ${local.lendingEnabled ? 'danger' : 'primary'}`}
                    onClick={() => setLocal(p => ({ ...p, lendingEnabled: !p.lendingEnabled }))}>
                    {local.lendingEnabled ? 'Turn off' : 'Turn on'}
                  </button>
                </div>
              </div>

              <div className="mdl-card">
                <div className="mdl-card-title">Browser notifications</div>
                <div className="mdl-card-hint">
                  Get a notification when a wishlist item hits its target price or a live item drops below your alert threshold — works even when the window is minimized.
                </div>
                {typeof Notification === 'undefined' ? (
                  <div className="mdl-controls"><span className="mdl-eg mdl-bad">Not supported in this browser.</span></div>
                ) : Notification.permission === 'granted' ? (
                  <div className="mdl-controls">
                    <span className="mdl-eg mdl-ok">Enabled</span>
                    <button className={`mdl-btn ${local.notificationsEnabled ? 'danger' : 'primary'}`}
                      onClick={() => setLocal(p => ({ ...p, notificationsEnabled: !p.notificationsEnabled }))}>
                      {local.notificationsEnabled ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                ) : Notification.permission === 'denied' ? (
                  <div className="mdl-controls">
                    <span className="mdl-eg mdl-bad">Blocked by browser — allow notifications for this site in browser settings.</span>
                  </div>
                ) : (
                  <div className="mdl-controls">
                    <button className="mdl-btn primary" onClick={async () => {
                      const perm = await Notification.requestPermission();
                      if (perm === 'granted') setLocal(p => ({ ...p, notificationsEnabled: true }));
                    }}>
                      Enable notifications
                    </button>
                  </div>
                )}
              </div>
              <div className="mdl-card">
                <div className="mdl-card-title">Price drop alert threshold (%)</div>
                <div className="mdl-card-hint">
                  Live cards show a red warning when the market price drops this % below your buy price.
                </div>
                <div className="mdl-controls">
                  <input type="number" className="mdl-input narrow" min="1" max="100" step="1"
                    value={local.priceDropAlertPct ?? 10}
                    onChange={e => setLocal(p => ({ ...p, priceDropAlertPct: parseFloat(e.target.value) || 10 }))}
                  />
                  <span className="mdl-unit">%</span>
                  <span className="mdl-eg">
                    e.g. buy $100 → alert when market ≤ ${(100 * (1 - (local.priceDropAlertPct ?? 10) / 100)).toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="mdl-save"><button className="mdl-btn primary wide" onClick={save}>Save Changes</button></div>
            </div>
          )}
          {tab==='crypto' && (
            <CryptoWalletSettings
              wallets={local.cryptoWallets || []}
              onChange={next => setLocal(p => ({ ...p, cryptoWallets: next }))}
              onSave={save}
              cryptoWallet={cryptoWallet}
              onRefresh={onRefreshCrypto}
            />
          )}
          {tab==='backup' && (() => {
            const handleExport = () => {
              const data = { rows: rows || [], settings, exportedAt: new Date().toISOString() };
              const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
              const url  = URL.createObjectURL(blob);
              const a    = document.createElement('a');
              a.href = url;
              a.download = `cs2pt-backup-${new Date().toISOString().slice(0,10)}.json`;
              a.click();
              URL.revokeObjectURL(url);
            };
            const handleImport = (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = ev => {
                try {
                  const parsed = JSON.parse(ev.target.result);
                  if (!Array.isArray(parsed.rows)) { alert('Invalid backup file — missing rows array.'); return; }
                  if (!window.confirm(`This will replace all ${(rows||[]).length} current item(s) with ${parsed.rows.length} from the backup. Continue?`)) return;
                  onRestore(parsed);
                  onClose();
                } catch { alert('Could not parse backup file.'); }
              };
              reader.readAsText(file);
              e.target.value = '';
            };
            return (
              <div className="mdl-stack gap">
                <div className="mdl-card">
                  <div className="mdl-card-title">Export backup</div>
                  <div className="mdl-card-hint">
                    Download all your tracker rows and settings as a JSON file. Store it somewhere safe.
                  </div>
                  <div className="mdl-controls">
                    <button onClick={handleExport} className="mdl-btn wide">
                      Download backup ({(rows||[]).length} items)
                    </button>
                  </div>
                </div>
                <div className="mdl-card warn">
                  <div className="mdl-card-title">Restore from backup</div>
                  <div className="mdl-card-hint mdl-bad">
                    Warning: this will overwrite your current data with the contents of the backup file.
                  </div>
                  <div className="mdl-controls">
                    <label className="mdl-btn danger wide mdl-file">
                      Choose backup file
                      <input type="file" accept=".json" onChange={handleImport} />
                    </label>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// ─── Category Items Modal ─────────────────────────────────
function CategoryItems({ rows, category, onClose, settings, skinsData }) {
  const ref = useRef(null);
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const esc = e => { if(e.key==='Escape') onClose(); };
    const out = e => { if(ref.current&&!ref.current.contains(e.target)) onClose(); };
    document.addEventListener('keydown', esc); document.addEventListener('mousedown', out);
    return () => { document.body.style.overflow=''; document.removeEventListener('keydown',esc); document.removeEventListener('mousedown',out); };
  }, [onClose]);

  const items  = rows.filter(r=>getItemTypes(r).some(t=>t.value===category.value)).sort((a,b)=>parseFloat(b.buyPrice)-parseFloat(a.buyPrice));
  const sold   = items.filter(r=>parseFloat(r.sellPrice)>0);
  const profit = sold.reduce((s,r)=>s+(parseFloat(r.profit)||0),0);

  return (
    <div className="modal-backdrop">
      <div className="modal-box" ref={ref}>
        <div className="modal-header">
          <div><h3 style={{ color:category.color }}>{category.label}</h3><p>{items.length} items · profit: <span style={{ color:profit>=0?'var(--green)':'var(--red)',fontWeight:700 }}>${profit.toFixed(2)}</span></p></div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ display:'flex',flexDirection:'column',gap:6 }}>
          {items.map((row,i)=>{
            const img=findSkinImage(skinsData,row.skin?.value,row.condition?.value);
            const isSold=parseFloat(row.sellPrice)>0; const p=parseFloat(row.profit)||0;
            return (<div key={i} style={{ display:'flex',alignItems:'center',gap:10,padding:'10px 12px',borderRadius:10,background:'rgba(255,255,255,0.025)',border:'1px solid var(--border)' }}>
              {img&&<img src={img} alt="" style={{ width:56,height:36,objectFit:'contain' }} />}
              <div style={{ flex:1,minWidth:0 }}><div style={{ fontSize:'1rem',fontWeight:600,color:'#fff',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{row.skin?.value}</div><div style={{ fontSize:'0.92rem',color:'var(--text-dim)' }}>{row.condition?.value} · Buy ${row.buyPrice}</div></div>
              <div style={{ textAlign:'right',flexShrink:0 }}>
                {isSold?<div style={{ fontSize:'1rem',fontWeight:700,color:p>=0?'var(--green)':'var(--red)' }}>{p>=0?'+':''}${p.toFixed(2)}</div>:<div style={{ fontSize:'0.95rem',color:'var(--amber)',fontWeight:600 }}>Holding</div>}
              </div>
            </div>);
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Main SkinTable ───────────────────────────────────────
export default function SkinTable() {
  const [rows, setRows]                 = useState(null);
  const [settings, setSettings]         = useState(null);
  const [user, setUser]                 = useState(() => localStorage.getItem('user'));
  // Steam name and avatar for the navbar. Read from cache rather than fetched:
  // AuthPage asks /me and writes the answer before routing here, and the
  // Inventory tab's Steam panel refreshes it, so a request here would only ask
  // the same question a third time for a name in the corner.
  const [account]                       = useState(() => getCachedAccount());
  const [loadError, setLoadError]       = useState(false);
  const dataLoadedRef                   = useRef(false);
  const notifiedRef                     = useRef(new Set()); // tracks item+trigger combos already notified this session
  // Land on Inventory when returning from a Steam link, since that is the tab
  // whose button started it and the only one showing the result.
  const [activeTab, setActiveTab]       = useState(
    STEAM_LINK_RESULT.linked || STEAM_LINK_RESULT.error ? 'extension' : 'overview3'
  );

  // Below 768px the desktop navbar / tab bar / pages are swapped for the
  // phone shell in src/mobile. Everything else in this component — data
  // loading, saving, and every modal — is shared by both shells, and
  // `activeTab` is shared too, so resizing keeps you on the same page.
  const isMobile = useIsMobile();

  // App skin: 'classic' (default) or 'zone' (the Overview 7 board look). Drives
  // the navbar, the tab bar, and which component each themed tab renders, so a
  // page keeps its data and only changes its look. Persisted across reloads.
  const [navTheme, setNavTheme] = useState(() => localStorage.getItem('navTheme') || 'classic');
  const toggleNavTheme = useCallback(() => {
    setNavTheme(prev => {
      const next = prev === 'zone' ? 'classic' : 'zone';
      localStorage.setItem('navTheme', next);
      return next;
    });
  }, []);
  const zoneTheme = navTheme === 'zone';

  // Paint the page, not just the board. The shell carries the class in its
  // own className below; html/body get it here so the overscroll strip
  // matches instead of flashing the classic dark.
  useEffect(() => {
    const els = [document.documentElement, document.body];
    els.forEach(el => el.classList.toggle('zn-theme', zoneTheme));
    return () => els.forEach(el => el.classList.remove('zn-theme'));
  }, [zoneTheme]);

  // Zone board width: 'boxed' keeps the centered max-width board, 'wide' lets
  // it run the full window so grids fit more columns per row.
  const [zoneWidth, setZoneWidth] = useState(() => localStorage.getItem('zoneWidth') || 'boxed');
  const toggleZoneWidth = useCallback(() => {
    setZoneWidth(prev => {
      const next = prev === 'wide' ? 'boxed' : 'wide';
      localStorage.setItem('zoneWidth', next);
      return next;
    });
  }, []);

  // Tracker UI state
  const [editingItem, setEditingItem]       = useState(null); // { row, realIndex }
  const [editDesign, setEditDesign]         = useState(() => localStorage.getItem('editModalDesign') || 'classic');
  const chooseEditDesign = useCallback(d => {
    setEditDesign(d);
    localStorage.setItem('editModalDesign', d);
  }, []);
  const [soldTradedModal, setSoldTradedModal] = useState(null); // { row, realIndex }
  const [showAddModal, setShowAddModal] = useState(false);
  const [selCat, setSelCat]             = useState(null);

  // Global state
  const [livePrices, setLivePrices]         = useState(null);
  const [csfloatPrices, setCsfloatPrices]   = useState(null);
  const csfloatRef = useRef(null); // ref so price history effect reads latest without re-running
  const [skinsData, setSkinsData]       = useState([]);
  const [searchableOptions, setSearchableOptions]     = useState([]);
  const [itemsWithConditions, setItemsWithConditions] = useState(new Set());
  const [showBulkAdd, setShowBulkAdd]             = useState(false);
  const [showInventoryImport, setShowInventoryImport] = useState(false);
  const [showSettings, setShowSettings]           = useState(false);
  const [syncedInventory, setSyncedInventory]     = useState([]);
  // Items you put in a storage unit. Deliberately NOT folded into
  // syncedInventory: that list is "what Steam can see right now", and half the
  // app reads it as exactly that — trade locks, lending, group building, the
  // assetId map. Storage is a separate list precisely so it can only appear
  // where it is asked for.
  const [storedItems, setStoredItems]             = useState([]);
  const [tradeHistory, setTradeHistory]           = useState([]);
  const [selectedForGroup, setSelectedForGroup]   = useState(new Set());
  const [showGroupedPurchase, setShowGroupedPurchase] = useState(false);
  // Items on their way into the Vault, waiting on "which folder?". Holds the
  // asset ids picked in the Inventory tab; the rows are not created until the
  // question is answered, so cancelling sends nothing.
  const [folderPick, setFolderPick]               = useState(null);
  const [groupedPurchases, setGroupedPurchases]   = useState([]);
  const [pendingNewItems, setPendingNewItems]     = useState([]);
  const [showNewItemsModal, setShowNewItemsModal] = useState(false);
  const [newItemsBannerDismissed, setNewItemsBannerDismissed] = useState(false);
  // Bumped whenever something could have added to the "where did this go?"
  // queue, which is the only thing that makes VanishedItemsPrompt re-read it.
  const [vanishCheck, setVanishCheck] = useState(0);
  const [selectMode, setSelectMode]               = useState(false);
  const [selectedForSell, setSelectedForSell]     = useState(new Set());
  const [showGroupSell, setShowGroupSell]         = useState(false);
  const [groupPriceItems, setGroupPriceItems]     = useState(null);  // Live: items being priced as one purchase
  // Lending. `lendModal` holds the rows going out plus whatever the app already
  // knows about the loan: { indexes, suggested, defaultAt }. `lendPicker` is the
  // fallback path for lends with no trade behind them.
  const [lendModal, setLendModal]                 = useState(null);
  const [lendPicker, setLendPicker]               = useState(false);
  const [showBulkTypeMenu, setShowBulkTypeMenu]   = useState(false);
  const [wishlist, setWishlist]                   = useState([]);
  const [wallets, setWallets]                     = useState({});
  const [transfers, setTransfers]                 = useState([]);
  const [tradeBoot, setTradeBoot]                 = useState([]);
  const [showTradeBoot, setShowTradeBoot]         = useState(false);
  const [tradeBootDismissed, setTradeBootDismissed] = useState(false);
  const [cryptoIsAutomatic, setCryptoIsAutomatic] = useState(false);
  const [isAtBottom, setIsAtBottom]     = useState(false);

  // ── Auth / data load ───────────────────────────────────
  // Shared by the on-mount "already logged in" path and a fresh login from the
  // Login screen — both need to actually fetch rows/settings, otherwise the app
  // is stuck on "Loading…" forever (rows/settings only ever set here).
  const loadRowsAndSettings = useCallback((username, attempt = 0) => {
    return apiFetch(`/api/tracker/rows/${username}`)
      .then(r => {
        if (!r.ok) throw Object.assign(new Error('http'), { status: r.status });
        return r.json();
      })
      .then(data => {
        dataLoadedRef.current = true;
        setLoadError(false);
        const s = data.settings || DEFAULT_SETTINGS;
        s.sites  = mergeSites(s.sites);
        setSettings(s);
        setRows(data.rows?.length ? data.rows : []);
      })
      .catch(err => {
        if (err.status === 401) {
          // Stale/missing session token (e.g. logged in before session auth
          // existed) — send back to the login screen instead of retrying forever.
          clearSession();
          setUser(null);
          return;
        }
        // Retry up to 4 times on network errors (server still starting)
        const next = attempt + 1;
        if (!err.status && next < 4) {
          setTimeout(() => loadRowsAndSettings(username, next), 1000 * next);
        } else {
          // Definitive HTTP error or retries exhausted — show error, don't wipe data
          setLoadError(true);
        }
      });
  }, []);

  // ── Sold-item reconciliation ───────────────────────────
  // Items sold on CSFloat leave the Steam inventory and never come back, but
  // nothing in Steam says where they went, so without this they sit in Live
  // forever showing a position that was closed weeks ago.
  //
  // The proof is server-side — your CSFloat sale records against your inventory
  // — so the server does the matching and hands back the corrected rows. Doing
  // the same arithmetic here would be a second implementation of one rule, and
  // taking the server's rows wholesale is also what stops the next autosave
  // from writing the stale set back over it.
  const [saleReconcile, setSaleReconcile] = useState(null);
  const [saleReconcileDismissed, setSaleReconcileDismissed] = useState(false);
  const [showUntrackedSales, setShowUntrackedSales] = useState(false);

  const reconcileSales = useCallback(async (username, currentAssetIds) => {
    const u = username || user;
    if (!u) return null;
    try {
      const res = await apiFetch(`/api/tracker/reconcile-sales/${u}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(currentAssetIds?.length ? { currentAssetIds } : {}),
      });
      if (!res.ok) return null;
      const { rows: reconciledRows, ...report } = await res.json();
      // Same guard as the autosave: never put rows on screen before a load has
      // succeeded. If the load is still retrying it will fetch these anyway —
      // the server has already written them.
      if (Array.isArray(reconciledRows) && dataLoadedRef.current) {
        setRows(prev => {
          // A sale you filed by hand in the last moment may not have reached the
          // server yet, which makes this response older than what is on screen.
          // Taking it would drop the filing. Keeping local state costs nothing:
          // the reconciler is idempotent, so whatever it just did it will do
          // again on the next pass once the save has landed.
          const known = new Set(reconciledRows.map(r => r.csfloatSale?.tradeId).filter(Boolean));
          const unsaved = prev.some(r =>
            r.csfloatSale?.matchedBy === 'manual' && !known.has(r.csfloatSale.tradeId));
          return unsaved ? prev : reconciledRows;
        });
      }
      // Anything that changed the books re-opens the banner, not just moves out
      // of Live: a row written from a CSFloat purchase and sale is a change you
      // are entitled to see even if you dismissed the last one.
      if (report.applied?.length || report.autoFiled?.length) setSaleReconcileDismissed(false);
      // The report only, not the rows it came with — those are already in state
      // and keeping a second copy of them alive here would just go stale.
      setSaleReconcile(report);
      return report;
    } catch {
      // Nothing was moved, which is the safe direction — the items stay in Live
      // and the next sync tries again.
      return null;
    }
  }, [user]);

  // Stop the server asking about these items again. Every ending uses it: an
  // answered question is answered whether the answer was "here is what I paid"
  // or "that one is not mine".
  //
  // Takes a list because filing forty at once should be one request, not forty
  // — the endpoint has always accepted an array of ids.
  const closeSaleQuestions = useCallback((sales) => {
    const list = (sales || []).filter(Boolean);
    if (!list.length) return;

    const tradeIds = new Set(list.map(s => s.tradeId).filter(Boolean));
    setSaleReconcile(prev => prev && ({
      ...prev,
      unmatched: (prev.unmatched || []).filter(s => !tradeIds.has(s.tradeId)),
    }));

    const assetIds = [...new Set(list.map(s => s.assetId).filter(Boolean))];
    if (user && assetIds.length) {
      apiFetch(`/api/tracker/pending-disappearances-resolve/${user}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetIds }),
      }).catch(() => {});
    }
  }, [user]);

  const closeSaleQuestion = useCallback((sale) => closeSaleQuestions([sale]), [closeSaleQuestions]);

  // One sold row from one proven sale plus the cost the user supplied.
  //
  // Shared by the single-item button and the "add everything priced" one, so a
  // row filed either way is the same record — the bulk path was the obvious
  // place for a second, subtly different row builder to appear.
  const buildRowForSale = useCallback((sale, buyPrice) => {
    const siteFee = settings?.sites?.find(s => s.value === 'csfloat') || settings?.sites?.[0] || null;
    const sellStr = Number(sale.price).toFixed(2);
    const buyStr  = Number(buyPrice || 0).toFixed(2);

    return {
      ...createEmptyRow(settings),
      skin:        { value: sale.name, label: sale.name },
      condition:   sale.condition ? { value: sale.condition, label: sale.condition } : null,
      assetId:     sale.assetId || undefined,
      iconUrl:     sale.iconUrl || undefined,
      float:       sale.float ?? undefined,
      paintSeed:   sale.paintSeed ?? undefined,
      inspectLink: sale.inspectLink || '',
      stickers:    sale.stickers?.length ? sale.stickers : undefined,
      buyPrice:    buyStr,
      sellPrice:   sellStr,
      siteFee,
      profit:      calcProfit(buyStr, sellStr, siteFee) || '0',
      // Dated to the sale, not to now: this is a position that closed weeks ago
      // and dating it today would put it in the wrong place on every chart.
      timestamp:   sale.soldAt || new Date().toISOString(),
      // Same receipt the reconciler writes, so a row filed by hand here and one
      // it moved on its own are the same kind of record — and so the trade can
      // never be counted twice.
      csfloatSale: { tradeId: sale.tradeId, state: 'verified', soldAt: sale.soldAt, matchedBy: 'manual' },
    };
  }, [settings]);

  // A proven sale of something that never had a row. Everything except the cost
  // basis is already known, so the row is built here complete rather than being
  // pushed through the import flow, which exists to find items you still own.
  const handleAddUntrackedSale = useCallback((sale, buyPrice) => {
    setRows(prev => prev.some(r => r.csfloatSale?.tradeId === sale.tradeId)
      ? prev
      : [...prev, buildRowForSale(sale, buyPrice)]);

    closeSaleQuestion(sale);
  }, [buildRowForSale, closeSaleQuestion]);

  // The same thing for every sale the user has priced, in one go.
  //
  // Not a loop over the single-item handler, for two reasons that both bite at
  // forty items: each call would queue its own state update and its own request
  // to clear one question, and the duplicate guard would be re-evaluated
  // against a `prev` that does not yet include the rows the previous calls are
  // adding. One pass, one guard, one request.
  const handleAddUntrackedSales = useCallback((entries) => {
    if (!entries?.length) return;

    setRows(prev => {
      const filed = new Set(prev.map(r => r.csfloatSale?.tradeId).filter(Boolean));
      const additions = entries
        .filter(e => e?.sale?.tradeId && !filed.has(e.sale.tradeId))
        .map(e => buildRowForSale(e.sale, e.buyPrice));
      return additions.length ? [...prev, ...additions] : prev;
    });

    closeSaleQuestions(entries.map(e => e.sale));
  }, [buildRowForSale, closeSaleQuestions]);

  // The same proven sale, but the user has identified which existing row it is.
  // This is the path that stops the item being counted twice when the row was
  // never provable — trade-received rows carry no assetId and no float, so
  // nothing but a person can tie them to a sale.
  // Takes the row itself, not its index. A reconcile or an autosave can rebuild
  // the array between this modal rendering and the click landing, and an index
  // captured before that would then point at somebody else's row. Reference
  // identity is what the rest of the app updates rows by, for the same reason.
  const handleCloseRowForSale = useCallback((sale, targetRow) => {
    const siteFee = settings?.sites?.find(s => s.value === 'csfloat') || settings?.sites?.[0] || null;
    const sellStr = Number(sale.price).toFixed(2);

    setRows(prev => prev.some(r => r.csfloatSale?.tradeId === sale.tradeId) ? prev : prev.map(r => r !== targetRow ? r : {
      ...r,
      sellPrice: sellStr,
      siteFee,
      profit: calcProfit(r.buyPrice, sellStr, siteFee) || '0',
      tradedFor: undefined,
      csfloatSale: { tradeId: sale.tradeId, state: 'verified', soldAt: sale.soldAt, matchedBy: 'manual' },
    }));

    closeSaleQuestion(sale);
  }, [settings, closeSaleQuestion]);

  useEffect(() => {
    const saved = localStorage.getItem('user');
    if (!saved) return;
    // Reconcile only once the rows are actually on screen: it replaces them, and
    // running the two in parallel would race the load into overwriting the result.
    loadRowsAndSettings(saved).then(() => reconcileSales(saved));

    // Load synced inventory (needed by all tabs for trade-lock status)
    loadSyncedInventory(saved);
    loadPendingNewItems(saved);
    loadWishlist(saved);
    loadWallets(saved);
    loadTransfers(saved);
    loadTradeBoot(saved);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Wallets ────────────────────────────────────────────
  // Cash outside the portfolio: CSFloat credit written by the extension, plus
  // crypto and bank figures the user keeps up to date themselves.
  const loadWallets = useCallback((username) => {
    const u = username || user;
    if (!u) return;
    apiFetch(`/api/tracker/wallet/${u}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.wallets) setWallets(data.wallets); })
      .catch(() => {});
  }, [user]);

  // ── Wallet transfers ───────────────────────────────────
  // Money that moved between wallets, spotted as a CSFloat reading the trades
  // could not explain. Loaded alongside the wallets because the answer changes
  // a balance, and a question the user never sees is a balance left wrong.
  //
  // Must stay ABOVE the focus effect below: a dependency array is evaluated
  // during render, so an effect referencing this from above its own `const` is
  // a temporal-dead-zone crash rather than a hoisting convenience.
  const loadTransfers = useCallback((username) => {
    const u = username || user;
    if (!u) return;
    apiFetch(`/api/tracker/wallet-transfers/${u}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        setTransfers(data.transfers || []);
        setCryptoIsAutomatic(data.cryptoIsAutomatic === true);
      })
      .catch(() => {});
  }, [user]);

  // Answering moves the money and hands back both the updated wallets and the
  // remaining questions, so one round trip settles the whole panel rather than
  // leaving the balance and the prompt to be re-fetched separately.
  const resolveTransfer = useCallback(async (id, source) => {
    const u = user;
    if (!u) return;
    try {
      const res = await apiFetch(`/api/tracker/wallet-transfers/${u}/${encodeURIComponent(id)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source }),
      });
      if (!res.ok) throw new Error('resolve failed');
      const data = await res.json();
      if (data?.wallets) setWallets(data.wallets);
      setTransfers(prev => prev.filter(t => t.id !== id));
    } catch {
      // Re-read rather than dropping a question that was never actually
      // answered: a prompt that vanishes without moving the money is worse
      // than one that is still there to click again.
      loadTransfers();
      loadWallets();
    }
  }, [user, loadTransfers, loadWallets]);

  // The CSFloat figure changes on csfloat.com, in another tab. Coming back to
  // this one is the cue to pick that up; there is nothing to poll for otherwise.
  // A reading is also what raises a transfer question, so the two are re-read
  // together: the question always arrives with the balance that prompted it.
  useEffect(() => {
    const onFocus = () => { loadWallets(); loadTransfers(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadWallets, loadTransfers]);

  // Reads every configured wallet address on the server and writes the USD
  // total into the crypto wallet. Slow by nature (one call per token per
  // address, paced under Etherscan's rate limit), so it is never on a render
  // path — only an explicit refresh or a stale figure on load.
  // Returns a result rather than swallowing failures. A chain read takes ten
  // seconds or more, so a silent failure and a slow success look identical from
  // the outside — the caller has to be able to say which happened.
  const refreshCrypto = useCallback(async ({ force = false } = {}) => {
    const u = user;
    if (!u) return { ok: false, error: 'Not signed in' };
    try {
      const res = await apiFetch(`/api/tracker/crypto-refresh/${u}${force ? '?force=true' : ''}`, { method: 'POST' });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        return { ok: false, error: data?.error || `Server returned ${res.status}` };
      }
      if (data?.wallet) setWallets(prev => ({ ...prev, crypto: data.wallet }));
      return { ok: true, ...data };
    } catch (err) {
      // Network-level: the API is unreachable, which is a different problem
      // from the API saying no, and the user needs to be told which.
      return { ok: false, error: `Could not reach the server (${err.message})` };
    }
  }, [user]);

  // Ask the server once per page load whether the crypto figure needs re-reading.
  //
  // The question is asked here, but ANSWERED on the server: it owns the twelve
  // hour interval, so a dozen open tabs or a reload loop cannot turn into a
  // dozen chain sweeps. This call is to our own API and costs nothing outside;
  // when nothing is due it comes straight back as `too-soon`.
  //
  // Re-asked when the address list changes, because that is the one edit that
  // makes the stored figure wrong immediately.
  //
  // Must stay BELOW refreshCrypto: a dependency array is evaluated during
  // render, so referencing the callback from above its own `const` is a
  // temporal-dead-zone crash rather than a hoisting convenience.
  const cryptoAskedRef = useRef(null);
  useEffect(() => {
    if (!user) return;
    const list = settings?.cryptoWallets || [];
    // Also runs with no addresses left but an auto figure still stored: that
    // call is what hands the row back to manual editing after the last address
    // is removed. It settles after one pass, because the reply clears `auto`.
    if (list.length === 0 && wallets?.crypto?.auto !== true) return;

    const signature = list.map(w => w.address).sort().join(',');
    if (cryptoAskedRef.current === signature) return;
    cryptoAskedRef.current = signature;
    refreshCrypto();
  }, [user, settings, wallets?.crypto?.auto, refreshCrypto]);

  const saveWallet = useCallback((source, { balance, pending }) => {
    const u = user;
    if (!u) return;
    // Optimistic: the panel is a form, and waiting on a round trip to see your
    // own typing land reads as a dropped edit.
    setWallets(prev => ({
      ...prev,
      [source]: { ...(prev?.[source] || {}), balance, pending, currency: 'USD', updatedAt: new Date().toISOString() },
    }));
    apiFetch(`/api/tracker/wallet/${u}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ market: source, balance, pending, currency: 'USD' }),
    })
      .then(r => { if (!r.ok) throw new Error('save failed'); })
      // Re-read on failure rather than leaving the optimistic value showing a
      // balance the server never accepted.
      .catch(() => loadWallets());
  }, [user, loadWallets]);

  // ── Trade boot ─────────────────────────────────────────
  // Trades the extension linked automatically from Steam history, waiting to be
  // told whether cash was part of the deal. Steam records items moving and never
  // money, so this is the one thing an automatic link cannot work out.
  const loadTradeBoot = useCallback((username) => {
    const u = username || user;
    if (!u) return;
    apiFetch(`/api/tracker/trade-boot/${u}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setTradeBoot(data.pending || []); })
      .catch(() => {});
  }, [user]);

  // Answering rewrites the cost basis of every item that came out of the trade,
  // so the server hands back the rows and they replace what is on screen. Doing
  // that arithmetic here as well would be two implementations of one rule.
  const resolveTradeBoot = useCallback(async (tradeId, boot, source) => {
    const u = user;
    if (!u) return;
    try {
      const res = await apiFetch(`/api/tracker/trade-boot/${u}/${encodeURIComponent(tradeId)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ boot, source }),
      });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      if (Array.isArray(data?.rows)) setRows(data.rows);
      if (data?.wallets) setWallets(data.wallets);
      setTradeBoot(prev => prev.filter(t => t.tradeId !== tradeId));
    } catch {
      loadTradeBoot();
    }
  }, [user, loadTradeBoot]);

  const dismissTradeBoot = useCallback(async (tradeId) => {
    const u = user;
    if (!u) return;
    try {
      await apiFetch(`/api/tracker/trade-boot/${u}/${encodeURIComponent(tradeId)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dismiss: true }),
      });
      setTradeBoot(prev => prev.filter(t => t.tradeId !== tradeId));
    } catch { /* it stays in the list, which is the safe direction */ }
  }, [user]);

  // The itemized version of the CSFloat projection: which trades moved the
  // balance since the extension last read it, and what is still locked in trade
  // protection. Only fetched when the panel is actually opened, since the wallet
  // response already carries the figures and the counts.
  const loadWalletLedger = useCallback(async () => {
    const u = user;
    if (!u) return null;
    try {
      const res = await apiFetch(`/api/tracker/wallet-ledger/${u}`);
      return res.ok ? await res.json() : null;
    } catch {
      return null;
    }
  }, [user]);

  // ── Wallet spends ──────────────────────────────────────
  // Money leaving or entering a wallet because of something here: an item
  // bought from a chosen source, or cash handed over on a trade. Signed, with
  // negative spending.
  //
  // The `id` is the important argument. Rows autosave on a debounce, so the
  // same purchase is posted repeatedly, and a server that deducted on each one
  // would drain the wallet by evening. A stable id makes every repeat a no-op,
  // which is why callers derive it from the row or trade rather than letting it
  // be generated here.
  //
  // Whether the named wallet actually moves is the server's call: CSFloat and
  // an address-backed crypto wallet read themselves and already contain the
  // spend, so those record the attribution and change no figure.
  const recordWalletSpend = useCallback(async ({ id, source, amount, reason, ref, note }) => {
    const u = user;
    if (!u || !amount) return null;
    try {
      const res = await apiFetch(`/api/tracker/wallet-spend/${u}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, source, amount, reason, ref, note }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data?.wallets) setWallets(data.wallets);
      return data;
    } catch {
      return null;
    }
  }, [user]);

  // Putting money in, as opposed to correcting the total.
  //
  // A fresh id per click on purpose: unlike a purchase, which is one event that
  // may be saved many times, each click here IS a separate deposit. Two $500
  // top-ups in a row are $1000 and must not collapse into one.
  const depositToWallet = useCallback((source, amount) => {
    return recordWalletSpend({
      id: `dep_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
      source, amount, reason: 'deposit', ref: null, note: 'Added by hand',
    });
  }, [recordWalletSpend]);

  const loadPendingNewItems = useCallback((username) => {
    const u = username || user;
    if (!u) return;
    apiFetch(`/api/tracker/pending-appearances/${u}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.items?.length) {
          setPendingNewItems(data.items);
          setNewItemsBannerDismissed(false);
        }
      })
      .catch(() => {});
  }, [user]);

  const loadWishlist = useCallback((username) => {
    const u = username || user;
    if (!u) return;
    apiFetch(`/api/tracker/wishlist/${u}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.items) setWishlist(data.items); })
      .catch(() => {});
  }, [user]);

  const handleWishlistSave = useCallback((item) => {
    const u = user;
    if (!u) return;
    setWishlist(prev => {
      const idx = prev.findIndex(i => i.id === item.id);
      return idx !== -1 ? prev.map((i, ix) => ix === idx ? item : i) : [...prev, item];
    });
    apiFetch(`/api/tracker/wishlist/${u}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ item }),
    }).catch(() => {});
  }, [user]);

  const handleWishlistDelete = useCallback((id) => {
    const u = user;
    if (!u) return;
    setWishlist(prev => prev.filter(i => i.id !== id));
    apiFetch(`/api/tracker/wishlist/${u}/${id}`, { method: 'DELETE' }).catch(() => {});
  }, [user]);

  // ── Watch list (the heart button) ──────────────────────
  // Hearting a holding adds a skin+condition entry to the wishlist, which is
  // what makes its price get recorded daily (see the recorder below). The
  // entry is keyed on skin+condition rather than assetId on purpose: it is a
  // watch on the *skin*, so it outlives the specific item being sold or
  // traded away, and only disappears when removed from the Wishlist tab.
  const watchedKeys = useMemo(
    () => new Set((wishlist || []).map(i => `${i.skin}|${i.condition || ''}`)),
    [wishlist],
  );

  const handleToggleWatch = useCallback((skin, condition) => {
    if (!skin) return;
    const cond = condition || null;
    const existing = (wishlist || []).find(
      i => i.skin === skin && (i.condition || null) === cond);
    if (existing) { handleWishlistDelete(existing.id); return; }
    handleWishlistSave({
      id:          crypto.randomUUID(),
      skin,
      condition:   cond,
      targetPrice: null,          // watch-only: no target means no alert
      note:        '',
      createdAt:   new Date().toISOString(),
      priceHistory: [],
      watchedFromHolding: true,
    });
  }, [wishlist, handleWishlistSave, handleWishlistDelete]);

  // Always replaces, never merges: a sync is what takes items back OUT of
  // storage, and it does so by emptying the record. Keeping the old list on an
  // empty answer would leave items showing as stored after they came back.
  const loadStoredItems = useCallback((username) => {
    const u = username || user;
    if (!u) return;
    apiFetch(`/api/tracker/stored-items/${u}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setStoredItems(d.items || []); })
      .catch(() => {});
  }, [user]);

  const loadSyncedInventory = useCallback((username) => {
    const u = username || user;
    if (!u) return;
    apiFetch(`/api/tracker/inventory/${u}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.items?.length) setSyncedInventory(data.items); })
      .catch(() => {});
    apiFetch(`/api/tracker/trade-history/${u}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.events) setTradeHistory(d.events); })
      .catch(() => {});
    loadStoredItems(u);
  }, [user, loadStoredItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload synced inventory whenever the extension finishes a sync
  useEffect(() => {
    function onMessage(event) {
      if (event.source !== window) return;
      if (event.data?.type === EXT_EVENT.SYNC_COMPLETE) {
        loadSyncedInventory();
        loadPendingNewItems();
        // A sync is also when an item can turn out to have LEFT. Anything the
        // reconciler below cannot prove was sold ends up on the server's
        // disappearance queue, and this is what makes the banner notice.
        setVanishCheck(n => n + 1);
        if (user) {
          apiFetch(`/api/tracker/trade-history/${user}`)
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d?.events?.length) setTradeHistory(d.events); })
            .catch(() => {});
          // A sync is exactly when an item can turn out to be gone, and when a
          // CSFloat sale can turn out to have settled. The server has already
          // reconciled on its own side; asking again is a no-op there and is
          // how this page learns about it without a refresh.
          reconcileSales(user);
        }
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [loadSyncedInventory, loadPendingNewItems, reconcileSales, user]);

  // ── inventoryMap: assetId → inventory item ─────────────
  const inventoryMap = useMemo(() => {
    const m = new Map();
    for (const item of syncedInventory) if (item.assetId) m.set(item.assetId, item);
    return m;
  }, [syncedInventory]);

  // Steam inventory items carry no trade history of their own, so the counterparty
  // the extension recorded is looked up by assetId off the tracker rows.
  const tradePartners = useMemo(
    () => buildTradePartnerIndex({ rows, inventoryItems: syncedInventory, tradeHistory }),
    [rows, syncedInventory, tradeHistory],
  );

  // ── Auto-save ──────────────────────────────────────────
  // Guard with dataLoadedRef: never overwrite server data before a successful load.
  // Debounced so rapid edits (e.g. typing a price) don't fire a request per keystroke;
  // always reads the latest state via a ref so the debounced closure never goes stale.
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const saveStateRef = useRef({ rows, settings, user });
  saveStateRef.current = { rows, settings, user };

  const debouncedSaveRef = useRef();
  if (!debouncedSaveRef.current) {
    debouncedSaveRef.current = debounce(() => {
      const { rows: r, settings: s, user: u } = saveStateRef.current;
      if (r === null || !u || s === null || !dataLoadedRef.current) return;
      setSaveStatus('saving');
      apiFetch(`/api/tracker/rows/${u}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: r, settings: s }),
      })
        .then(res => {
          if (!res.ok) throw new Error('save failed');
          setSaveStatus('saved');
        })
        .catch(() => {
          setSaveStatus('error');
          // One retry shortly after — a whole-state save means the next attempt
          // (whether triggered by this or a later edit) always carries the latest data.
          setTimeout(() => debouncedSaveRef.current?.(), 3000);
        });
    }, 800);
  }

  useEffect(() => {
    if (rows !== null && user && settings !== null && dataLoadedRef.current) {
      debouncedSaveRef.current();
    }
  }, [rows, settings, user]);

  // Flush any pending debounced save on unmount (e.g. logout) so the last edit isn't dropped.
  useEffect(() => () => { debouncedSaveRef.current?.flush(); }, []);

  // ── Wishlist price history recording ──────────────────
  // Appends today's Buff price to each wishlist item once per calendar day.
  // CSFloat rides along when its feed has loaded, so the detail modal can
  // chart both marketplaces; `price` stays as the Buff figure the cards read.
  useEffect(() => {
    if (!livePrices) return;
    const today = new Date().toISOString().slice(0, 10);
    const csf   = csfloatRef.current;
    setWishlist(prev => {
      if (!prev?.length) return prev;
      let changed = false;
      const next = prev.map(item => {
        const cond = item.condition ? (CONDITION_MAP[item.condition] || item.condition) : null;
        const key  = cond ? `${item.skin} (${cond})` : item.skin;
        const lp = (() => {
          if (!item.skin) return null;
          if (cond) return livePrices[key] || livePrices[item.skin] || null;
          return livePrices[item.skin] || null;
        })();
        const price = lp?.starting_at?.price ?? null;
        if (price == null) return item;
        const csfloatPrice = (csf?.[key] || csf?.[item.skin])?.price ?? null;
        const history   = item.priceHistory || [];
        const lastEntry = history[history.length - 1];
        if (lastEntry?.date === today && lastEntry.price === price
            && (lastEntry.csfloatPrice ?? null) === csfloatPrice) return item;
        changed = true;
        const entry = { date: today, price, csfloatPrice };
        if (lastEntry?.date === today) {
          return { ...item, priceHistory: [...history.slice(0, -1), entry] };
        }
        return { ...item, priceHistory: [...history, entry].slice(-90) };
      });
      if (!changed) return prev;
      // Persist each changed item
      next.forEach((item, i) => {
        if (item !== prev[i]) handleWishlistSave(item);
      });
      return next;
    });
  }, [livePrices, handleWishlistSave]);

  // ── Push notifications ─────────────────────────────────
  useEffect(() => {
    if (!settings?.notificationsEnabled) { notifiedRef.current.clear(); return; }
    if (Notification.permission !== 'granted') return;
    if (!livePrices || !rows) return;
    const dropPct = parseFloat(settings?.priceDropAlertPct ?? 10);

    const notify = (key, title, body) => {
      if (notifiedRef.current.has(key)) return;
      notifiedRef.current.add(key);
      try { new Notification(title, { body, icon: '/favicon.ico' }); } catch (_) {}
    };

    // Price drop alerts on live items
    (rows || []).forEach((row, i) => {
      if (!row.skin?.value || parseFloat(row.sellPrice) > 0) return;
      const lp = getLivePrice(livePrices, row);
      const mkt = lp?.starting_at?.price;
      const buy = parseFloat(row.buyPrice) || 0;
      if (!mkt || !buy) return;
      if (mkt < buy * (1 - dropPct / 100)) {
        const drop = (((mkt - buy) / buy) * 100).toFixed(1);
        notify(
          `drop:${i}`,
          `Price drop — ${row.skin.value}`,
          `Market $${mkt.toFixed(2)} vs. your buy $${buy.toFixed(2)} (${drop}%)`,
        );
      } else {
        // Clear key so it can re-fire if it drops again later
        notifiedRef.current.delete(`drop:${i}`);
      }
    });

    // Wishlist target hits
    (wishlist || []).forEach(item => {
      if (!item.skin || item.targetPrice == null) return;
      const lp = (() => {
        if (item.condition) {
          const cond = CONDITION_MAP[item.condition] || item.condition;
          return livePrices[`${item.skin} (${cond})`] || livePrices[item.skin] || null;
        }
        return livePrices[item.skin] || null;
      })();
      const mkt = lp?.starting_at?.price;
      if (!mkt) return;
      if (mkt <= item.targetPrice) {
        notify(
          `wish:${item.id}`,
          `Wishlist hit — ${item.skin}`,
          `Market $${mkt.toFixed(2)} ≤ your target $${item.targetPrice.toFixed(2)}`,
        );
      } else {
        notifiedRef.current.delete(`wish:${item.id}`);
      }
    });
  }, [livePrices, rows, wishlist, settings]);

  // ── Live prices (Buff163) ──────────────────────────────
  useEffect(() => {
    const load = () => {
      fetch(`${API}/api/prices/buff163`).then(r=>r.json()).then(({data})=>{ if(data) setLivePrices(data); }).catch(()=>{});
    };
    load();
    const iv = setInterval(load, 10 * 60 * 1000);
    return () => clearInterval(iv);
  }, []);

  // ── CSFloat prices ─────────────────────────────────────
  useEffect(() => {
    fetch(`${API}/api/prices/csfloat`).then(r=>r.json()).then(({data})=>{ if(data) { setCsfloatPrices(data); csfloatRef.current = data; } }).catch(()=>{});
  }, []);

  // ── Daily price history snapshots for unsold items ─────
  useEffect(() => {
    if (!livePrices || !rows?.length) return;
    const today = new Date().toISOString().split('T')[0];
    const csf   = csfloatRef.current;
    setRows(prev => {
      let changed = false;
      const next = prev.map(row => {
        if (!row.skin?.value || parseFloat(row.sellPrice) > 0) return row;
        const lp        = getLivePrice(livePrices, row);
        const buffPrice = lp?.starting_at?.price || null;
        if (!buffPrice) return row;
        const history = row.priceHistory || [];
        if (history.length && history[history.length - 1].date === today) return row;
        // Build CSFloat key using same mapping as ItemCard
        const csfKey = row.condition?.value
          ? `${row.skin.value} (${CONDITION_MAP[row.condition.value] || row.condition.value})`
          : row.skin.value;
        const csfloatPrice = csf?.[csfKey]?.price || null;
        changed = true;
        return { ...row, priceHistory: [...history, { date: today, buffPrice, csfloatPrice }].slice(-365) };
      });
      return changed ? next : prev;
    });
  }, [livePrices]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Daily portfolio value snapshot ─────────────────────
  useEffect(() => {
    if (!livePrices || !rows?.length) return;
    const today = new Date().toISOString().split('T')[0];
    setSettings(prev => {
      if (!prev) return prev;
      const history = prev.portfolioHistory || [];
      if (history.length && history[history.length - 1]?.date === today) return prev;
      const unsold = rows.filter(r => !(parseFloat(r.sellPrice) > 0) && r.skin?.value);
      let marketValue = 0, costBasis = 0;
      unsold.forEach(r => {
        const lp = getLivePrice(livePrices, r);
        costBasis    += parseFloat(r.buyPrice) || 0;
        marketValue  += lp?.starting_at?.price || (parseFloat(r.buyPrice) || 0);
      });
      if (!costBasis) return prev;
      return {
        ...prev,
        portfolioHistory: [...history, {
          date: today,
          marketValue: parseFloat(marketValue.toFixed(2)),
          costBasis:   parseFloat(costBasis.toFixed(2)),
        }].slice(-730),
      };
    });
  }, [livePrices]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Skins database (loaded from /public) ──────────────
  // skins-lite.json is all.json reduced to the three fields anything here
  // actually reads — 13 MB instead of 63 MB, which is that much less to
  // download, parse on the main thread, and then hold for the session.
  // scripts/slim-skins.js generates it; all.json is the fallback so a missing
  // or not-yet-regenerated lite file only costs speed, never data.
  useEffect(() => {
    const load = url => fetch(url).then(r => {
      if (!r.ok) throw new Error(`${url} -> ${r.status}`);
      return r.json();
    });
    load('/skins-lite.json')
      .catch(() => load('/all.json'))
      .then(data => setSkinsData(Array.isArray(data) ? data : Object.values(data)))
      .catch(err => console.error('Failed to load skins:', err));
  }, []);

  // ── Build skin options + condition set once data loads ──
  useEffect(() => {
    if (!skinsData.length) return;
    const seen    = new Set();
    const condSet = new Set(); // base names that have condition variants
    const deduped = [];
    skinsData.forEach(s => {
      const raw  = s.name || s.market_hash_name || '';
      const base = stripCondition(raw);
      if (!base) return;
      // If the raw name and stripped name differ, this item has a condition tier
      if (raw !== base) condSet.add(base);
      if (!seen.has(base)) { seen.add(base); deduped.push({ ...s, name: base }); }
    });
    setSearchableOptions(createSearchOptions(deduped));
    setItemsWithConditions(condSet);
  }, [skinsData]);

  // ── Scroll tracking ────────────────────────────────────
  useEffect(() => {
    const fn = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      setIsAtBottom(scrollTop + clientHeight >= scrollHeight - 100);
    };
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // ── Background color ───────────────────────────────────
  useEffect(() => {
    document.body.style.backgroundColor = settings?.backgroundColor || '#05080f';
  }, [settings?.backgroundColor]);

  // ── Derived row sets ───────────────────────────────────
  // "Traded away" now has exactly one definition, in helpers.js, because five
  // hand-written copies is how the Live tabs and the Traded tab ended up
  // disagreeing about where an item lives. Kept as a local alias so the two
  // predicates below still read as a set.
  const isRowTradedAway  = isTradedAway;
  const isRowSold        = r => parseFloat(r.sellPrice) > 0;
  const isRowLive        = r => r.skin?.value && !isRowSold(r) && !isRowTradedAway(r);

  // ── Lending ────────────────────────────────────────────
  // A lent row is deliberately absent from every predicate above. It carries no
  // tradeId and no sellPrice, so it stays exactly what it was — a live holding —
  // and every total that already counted it goes on counting it. The Lending
  // page is a second view of those rows, not a fourth state for them.
  const lendingOn = settings?.lendingEnabled === true;
  const lentCount = useMemo(() => (rows || []).filter(isLent).length, [rows]);

  // The Vault's badge, counted by the same predicate the Vault itself filters
  // on — the tab said one number and the page showed another when these two
  // were written out separately.
  const investCount = useMemo(() => (rows || []).filter(isVaultRow).length, [rows]);

  // Everyone worth offering as a borrower, best guesses first.
  const lendFriends = useMemo(
    () => knownFriends({ rows: rows || [], tradeHistory }),
    [rows, tradeHistory],
  );

  /**
   * Open the lend modal for a set of rows.
   *
   * `suggested` and `at` are what make the trade-side entry a single click: the
   * caller passes the counterparty and timestamp Steam already recorded, and
   * there is nothing left to fill in. Called with no arguments it opens the
   * picker instead, which is the manual path.
   */
  const openLendModal = useCallback((indexes, { suggested = null, at = null } = {}) => {
    if (!indexes?.length) { setLendPicker(true); return; }
    setLendModal({ indexes, suggested, defaultAt: at });
  }, []);

  /** Lend the chosen rows out. One person, one date, however many items. */
  const confirmLend = useCallback(({ to, at, dueAt, note, prices = {} }) => {
    const indexes = new Set(lendModal?.indexes || []);
    const newItems = lendModal?.newItems || [];
    if (!indexes.size && !newItems.length) { setLendModal(null); return; }

    // Any row arriving from the Resolve state is taking its auto-linked trade
    // with it. The server is still holding a "was cash involved?" question for
    // that trade, and a lend has no cash side, so the question is withdrawn
    // rather than left to be answered about something that did not happen.
    const unwound = [...indexes]
      .map(i => (rows || [])[i]?.tradeId)
      .filter(Boolean);

    // Rows that were recorded as a full trade also carry received rows holding a
    // slice of this row's cost basis. Calling it a lend says those items never
    // arrived, so they go. LendPicker has already refused any trade whose
    // received items have been sold, so nothing realized is at risk here.
    const toUnwind = [...indexes]
      .map(i => (rows || [])[i])
      .filter(r => r?.tradeId && r.tradedFor?.length > 0)
      .map(r => r.tradeId);

    setRows(prev => {
      // Map before filter: the indexes were taken against this same array, so
      // they stay valid right up until rows are removed.
      // Stamp today's market on each lend as it goes out. Snapshots only cover
      // days the app was open, so reading it back later is guesswork; reading
      // it now is not.
      let next = prev.map((r, i) => indexes.has(i)
        ? lendRow(r, { to, at, dueAt, note, valueAt: getBuffPrice(livePrices, r) })
        : r);
      for (const tradeId of toUnwind) next = unwindTrade(next, tradeId);

      // Items that existed only in the Steam trade history become real rows
      // here, already lent. Appended last so nothing above shifts position
      // while the map is still using the original indexes.
      for (const cand of newItems) {
        const created = rowFromHistoryItem(cand, { buyPrice: prices[cand.key] ?? '' });
        next = [...next, lendRow(created, { to, at, dueAt, note, valueAt: getBuffPrice(livePrices, created) })];
      }
      return next;
    });
    unwound.forEach(tradeId => { void dismissTradeBoot(tradeId); });

    console.log(`[lend] ${indexes.size + newItems.length} item(s) lent to ${to.name}`
      + `${newItems.length ? ` · created ${newItems.length} row(s) from trade history` : ''}`
      + `${unwound.length ? ` · withdrew ${unwound.length} trade question(s)` : ''}`
      + `${toUnwind.length ? ` · unwound ${toUnwind.length} recorded trade(s)` : ''}`);
    setLendModal(null);
  }, [lendModal, rows, dismissTradeBoot, livePrices]);

  /**
   * It came back. `assetId` is the new id Steam issued on the way in, when the
   * Lending page was able to spot the item in a synced inventory.
   */
  const markLendReturned = useCallback((realIndex, assetId = null) => {
    setRows(prev => prev.map((r, i) => i === realIndex ? returnRow(r, { assetId }) : r));
  }, []);

  /**
   * It is not coming back. Closes the row at zero so the money lands in realized
   * P/L as a loss, instead of sitting in your portfolio value forever.
   */
  const writeOffLend = useCallback(realIndex => {
    // The confirm has to happen out here, not inside the setRows updater: React
    // may run an updater more than once, and a prompt that appears twice for one
    // click is its own kind of wrong answer.
    const row = (rows || [])[realIndex];
    if (!row) return;
    const name = row.skin?.value || 'item';
    const paid = (parseFloat(row.buyPrice) || 0).toFixed(2);
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Write off "${name}" as lost?\n\nIt closes at $0, so the $${paid} you paid becomes a realized loss.`)) return;
    setRows(prev => prev.map((r, i) => i === realIndex ? writeOffRow(r) : r));
  }, [rows]);

  /**
   * "That was not a trade, it was a lend."
   *
   * The row is sitting in the amber Resolve state because Steam saw it leave and
   * autoLinkTrades assumed a trade. lendRow withdraws that guess — tradeId and
   * tradedFor come off — and the partner and timestamp Steam captured carry
   * straight into the loan.
   */
  const lendFromTrade = useCallback((row, realIndex) => {
    openLendModal([realIndex], {
      suggested: suggestedPartner(row, tradePartners),
      at:        row?.timestamp || null,
    });
  }, [openLendModal, tradePartners]);

  /**
   * New inventory items worth asking about, with returning loans taken out.
   *
   * An item coming back from a friend is not a purchase. Left in this list it
   * would be offered as one, and saying yes would create a second row for an
   * item you already own at a cost basis of whatever it is worth today. The
   * Lending page handles the return properly, so the question is not asked here
   * at all.
   */
  const newItemsForReview = useMemo(() => {
    if (!lendingOn || !pendingNewItems.length) return pendingNewItems;
    const returning = new Set(
      openLends({ rows: rows || [], inventoryItems: pendingNewItems })
        .map(e => e.back?.item?.assetId)
        .filter(Boolean),
    );
    if (!returning.size) return pendingNewItems;
    return pendingNewItems.filter(i => !returning.has(i.assetId));
  }, [lendingOn, pendingNewItems, rows]);

  /**
   * Inventory items the tracker already speaks for, so the Locker can darken
   * them and every "add these" path can refuse them.
   *
   * Not just the asset ids the rows carry. A trade drops its received items
   * into Live with no asset id at all — the copy is still trade-protected when
   * the trade is read — so those items used to look untracked in the Locker and
   * could be sent to Live a second time. coveredAssetIds counts by name and
   * wear as well, which is the rule the server already applies from the other
   * side in planReceivedRows.
   *
   * Every list of items that can be sent to Live is judged together: the synced
   * inventory, the new-appearances prompt and storage all draw from the same
   * rows, and counting them separately would let one row cover an item in each.
   */
  const trackedAssetIds = useMemo(
    () => coveredAssetIds(rows, syncedInventory, newItemsForReview, storedItems),
    [rows, syncedInventory, newItemsForReview, storedItems],
  );

  /** Lend items picked in the Locker. Inventory assetIds in, row indexes out. */
  const lendFromInventory = useCallback(assetIds => {
    const ids = new Set(assetIds || []);
    const indexes = (rows || [])
      .map((r, i) => ({ r, i }))
      .filter(({ r }) => r.assetId && ids.has(r.assetId) && !r.lend && !(parseFloat(r.sellPrice) > 0))
      .map(({ i }) => i);
    if (!indexes.length) {
      // Every picked item is either already out on loan or was never tracked, so
      // there is no row to hang a lend on. Saying so beats an empty modal.
      window.alert('None of those items are tracked as live holdings, so there is nothing to lend. Send them to the tracker first.');
      return;
    }
    openLendModal(indexes);
  }, [rows, openLendModal]);

  /**
   * "That one is stock, not a flip."
   *
   * Unlike a lend, this DOES move the row: an investment leaves the Live tabs
   * and lives in the Vault instead. The two lists are disjoint on purpose —
   * before this, a flagged row appeared on both and every figure built on Live
   * counted an item the Vault was also counting.
   *
   * Whole-portfolio totals (Overview, Analytics) still see it, because they
   * work off getUnsoldRows, which asks whether you hold the item rather than
   * which tab it is drawn on.
   */
  const moveToInvestment = useCallback(indexes => {
    const fresh = new Set(
      (indexes || []).filter(i => (rows || [])[i]?.skin?.value && !(rows || [])[i].isInvestment),
    );
    if (!fresh.size) {
      window.alert('Nothing to move — those items are already in the vault.');
      return;
    }
    setRows(prev => prev.map((r, i) => fresh.has(i) ? { ...r, isInvestment: true } : r));
    console.log(`[move] ${fresh.size} item(s) moved to investments`);
  }, [rows]);

  /**
   * Record an item that left Steam and was never in the tracker at all.
   *
   * The vanished-items prompt used to offer these only "In storage" or
   * "Dismiss" — so the commonest true answer, "I sold it", was the one answer
   * there was no way to give, and the sale simply went unrecorded.
   *
   * The row is built from what Steam still knows about the copy that left:
   * name, wear, float, pattern, stickers and the icon. Cost is deliberately
   * left empty and flagged `costUnknown` rather than defaulted to zero — zero
   * is a claim that it was free, and the sale modal reads the flag to ask for
   * the real figure instead of quietly booking the whole sale price as profit.
   */
  const trackVanishedItem = useCallback(item => {
    const name = stripCondition(item?.market_hash_name || item?.marketHashName || item?.name || '');
    if (!name) { window.alert('That item has no name recorded, so there is nothing to build a row from.'); return; }

    const cond = EXTERIOR_TO_COND[item.exterior] || null;
    const row = {
      ...createEmptyRow(settings),
      skin:      { value: name, label: name },
      condition: cond ? { value: cond, label: cond } : null,
      assetId:   item.assetId || undefined,
      float:     item.float ?? null,
      paintSeed: item.paintSeed ?? null,
      stickers:  (item.stickers || []).filter(st => st?.name),
      charms:    (item.charms || []).filter(ch => ch?.name),
      ...(getInventoryIcon(item) ? { iconUrl: getInventoryIcon(item) } : {}),
      // Dated to when it left, not to now: it is the only date we actually know
      // anything about, and "bought today, sold today" would be a worse guess.
      timestamp: item._disappearedAt || new Date().toISOString(),
      buyPrice:  '',
      costUnknown: true,
    };

    setRows(prev => {
      const next = [...prev, row];
      // Opened against the index it will really have once this lands.
      setSoldTradedModal({ row, realIndex: next.length - 1 });
      return next;
    });
    console.log(`[vanished] tracking "${name}" so its sale can be recorded`);
  }, [settings]);

  /** Bulk category — put one item type on (or take it off) a set of rows.
   *
   *  A row's `itemType` is a list, not a single value: an item can be a King
   *  and a Craft at once. So this edits the list rather than replacing it —
   *  tagging a dozen items as Keepers must not quietly strip the Craft tag off
   *  the ones that had it. `action` is 'add', 'remove', or 'clear'.
   *
   *  The label and colour are copied onto the row alongside the value, matching
   *  what the item form writes, so a row still draws correctly if the type is
   *  later renamed or deleted in settings. */
  const setRowsCategory = useCallback((indexes, type, action = 'add') => {
    const sel = new Set((indexes || []).filter(i => (rows || [])[i]));
    if (!sel.size) return;
    setRows(prev => prev.map((r, i) => {
      if (!sel.has(i)) return r;
      const cur = getItemTypes(r).filter(t => t?.value);
      if (action === 'clear') return cur.length ? { ...r, itemType: [] } : r;
      const has = cur.some(t => t.value === type?.value);
      if (action === 'remove') {
        return has ? { ...r, itemType: cur.filter(t => t.value !== type.value) } : r;
      }
      return has
        ? r
        : { ...r, itemType: [...cur, { value: type.value, label: type.label, color: type.color }] };
    }));
    console.log(`[category] ${action} "${type?.label ?? ''}" on ${sel.size} item(s)`);
  }, [rows]);

  /** Back out of the Vault — the item becomes an ordinary holding again and
   *  reappears in Live. Its folder goes with it, since it no longer has one. */
  const returnToLive = useCallback(indexes => {
    const back = new Set((indexes || []).filter(i => (rows || [])[i]?.isInvestment));
    if (!back.size) return;
    setRows(prev => prev.map((r, i) =>
      back.has(i) ? { ...r, isInvestment: false, investmentFolder: null } : r));
    console.log(`[move] ${back.size} item(s) returned to live`);
  }, [rows]);

  // ── Vault folders ──────────────────────────────────────
  // A folder is a name the user gave a pile of units. The list lives on
  // settings and the membership lives on the row, so both ride the existing
  // autosave — there is no folder endpoint and no separate thing to keep in
  // sync with the rows it describes.
  // Returns the new folder's id, so "create one and put these in it" can happen
  // in a single step. The object is minted here rather than inside the updater
  // because a functional setState is not guaranteed to run before this returns,
  // and the caller needs the id now. `extra` carries the setId when the folder
  // stands for an in-game collection, case or capsule.
  //
  // The colour is the one exception: it is picked inside the updater, off the
  // list as it actually stands when the write lands. Filing a mixed batch by
  // collection creates several folders in one go, and minting them all against
  // the same stale list would hand them all the same colour. A colour the user
  // chose in the folder editor is left alone — that is not a default to spread
  // out, it is an answer.
  const createFolder = useCallback((name, extra) => {
    const folder = makeFolder(name, getFolders(settings), extra);
    setSettings(prev => {
      const existing = getFolders(prev);
      return {
        ...prev,
        investmentFolders: [
          ...existing,
          extra?.color
            ? folder
            : { ...folder, color: FOLDER_COLORS[existing.length % FOLDER_COLORS.length] },
        ],
      };
    });
    return folder.id;
  }, [settings]);

  /**
   * Edit a folder in place — its name, its colour, its picture.
   *
   * A patch rather than three callbacks: the folder editor asks all three
   * questions in one modal and answers them in one save, and a rename that
   * landed while a colour change was still in flight would otherwise drop one
   * of the two.
   *
   * `image: null` clears the picture, which is why the patch is spread rather
   * than filtered for empties.
   */
  const updateFolder = useCallback((id, patch) => {
    setSettings(prev => ({
      ...prev,
      investmentFolders: getFolders(prev).map(f => f.id === id ? { ...f, ...patch } : f),
    }));
  }, []);

  /**
   * Deleting a folder deletes the NAME, never the items.
   *
   * The units inside it are still investments you own; they simply stop being
   * filed, and folderOf reads an unknown id as unfiled anyway. Clearing the
   * field as well keeps the rows honest rather than leaving them pointing at
   * something that no longer exists.
   */
  const deleteFolder = useCallback(id => {
    setSettings(prev => ({ ...prev, investmentFolders: getFolders(prev).filter(f => f.id !== id) }));
    setRows(prev => prev.map(r => r.investmentFolder === id ? { ...r, investmentFolder: null } : r));
  }, []);

  const assignFolder = useCallback((indexes, folderId) => {
    const picked = new Set(indexes || []);
    if (!picked.size) return;
    setRows(prev => prev.map((r, i) => picked.has(i) ? { ...r, investmentFolder: folderId || null } : r));
  }, []);

  // ── Handlers ───────────────────────────────────────────
  const handleSaveItem = useCallback((rowData, realIndex) => {
    // A purchase only moves a wallet once it says which wallet paid for it.
    // Everything else stays exactly as it was: items from trades, from openings,
    // or from before any of this existed have no source and are left alone.
    const price  = parseFloat(rowData.buyPrice) || 0;
    const source = rowData.paidWith || null;

    // Rows are identified by array position, which is not stable enough to key
    // money on: inserting above one would re-charge it. So a purchase that draws
    // on a wallet gets its own id, minted once and carried on the row forever
    // after. It is what makes the debounced autosave harmless, because every
    // repeat of the same purchase reuses it and the server refuses the duplicate.
    const purchaseId = rowData.purchaseId
      || (source && price > 0 ? `buy_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}` : null);

    const saved = purchaseId ? { ...rowData, purchaseId } : rowData;

    setRows(prev => {
      const next = [...prev];
      if (realIndex === -1) {
        next.push({ ...saved, timestamp: saved.timestamp || new Date().toISOString() });
      } else {
        next[realIndex] = saved;
      }
      return next;
    });

    if (!purchaseId) return;

    const before = realIndex === -1 ? null : rows?.[realIndex];
    const priceChanged  = (parseFloat(before?.buyPrice) || 0) !== price;
    const sourceChanged = (before?.paidWith || null) !== source;

    // Editing the price or the funding source of a purchase already charged
    // means the old deduction is now wrong. Reverse it and re-apply, rather than
    // trying to net the difference: the server holds what was actually taken, so
    // giving that exact amount back cannot drift even if the row was edited
    // several times.
    const wasCharged = !!before?.purchaseId && !!before?.paidWith;
    if (wasCharged && (priceChanged || sourceChanged)) {
      void (async () => {
        await apiFetch(`/api/tracker/wallet-spend/${user}/${encodeURIComponent(purchaseId)}`, { method: 'DELETE' })
          .catch(() => {});
        if (source && price > 0) {
          await recordWalletSpend({
            id: purchaseId, source, amount: -price, reason: 'purchase',
            ref: purchaseId, note: rowData.skin?.value || 'Item',
          });
        } else {
          loadWallets();
        }
      })();
      return;
    }

    if (source && price > 0) {
      void recordWalletSpend({
        id: purchaseId, source, amount: -price, reason: 'purchase',
        ref: purchaseId, note: rowData.skin?.value || 'Item',
      });
    }
  }, [rows, user, recordWalletSpend, loadWallets]);

  const handleDeleteItem = useCallback(realIndex => {
    // Deleting a purchase gives the money back, but only what was actually
    // taken. The stored spend is the record of that, so a row whose price was
    // edited after the fact still refunds the real figure.
    const row = rows?.[realIndex];
    if (row?.purchaseId && row?.paidWith) {
      void apiFetch(`/api/tracker/wallet-spend/${user}/${encodeURIComponent(row.purchaseId)}`, { method: 'DELETE' })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data?.wallets) setWallets(data.wallets); })
        .catch(() => {});
    }
    setRows(prev => prev.filter((_, i) => i !== realIndex));
  }, [rows, user]);

  const handleLogout = () => {
    apiFetch('/api/tracker/session', { method: 'DELETE' }).catch(() => {});
    clearSession();
    setUser(null); setRows(null); setSettings(null);
  };
  const handleUpdateSettings = s => { setSettings(s); setRows(prev => [...prev]); };

  const handleRestore = useCallback((parsed) => {
    if (parsed.rows) setRows(parsed.rows);
    if (parsed.settings) {
      const s = { ...parsed.settings, sites: mergeSites(parsed.settings.sites) };
      setSettings(s);
    }
  }, []);

  // Save steamId to user settings when first used in inventory import
  const handleSteamIdSaved = useCallback(steamId => {
    setSettings(prev => ({ ...prev, steamId }));
  }, []);

  // Called when an item is marked as "sold".
  //
  // `target` is how the caller says WHICH row it means, and it comes in two
  // shapes because there are two callers that know different things:
  //
  //   number  a row index — the Mark Sold button, acting on a row you clicked.
  //   object  the inventory item that left — the changes list, which knows an
  //           assetId but not where that item sits in the rows array.
  //
  // Both are handled here. This used to be documented as an index only while the
  // changes list passed an item, and an object index reads as a miss: marking a
  // tracked item sold from the changes list left the original sitting in Live
  // and pushed a second, zero-cost row into Sold beside it.
  const handleSellItem = useCallback((skinName, condition, platform, sellPrice, target, opts = {}) => {
    // A cost supplied by the caller wins over whatever the row holds — set only
    // when the sale modal had to ask because the row had no purchase price.
    const givenBasis = opts.buyPrice != null && Number.isFinite(parseFloat(opts.buyPrice))
      ? parseFloat(opts.buyPrice).toFixed(2)
      : null;
    const rowIndex   = typeof target === 'number' ? target : null;
    const sourceItem = target && typeof target === 'object' ? target : null;
    // Set when the server proved this sale against a CSFloat trade. Stamped
    // onto whichever row ends up carrying it, so the trade is recorded as
    // settled and can never be offered a second time.
    const proven = sourceItem?._csfloatSale || null;
    const receipt = proven
      ? { csfloatSale: { tradeId: proven.tradeId, state: 'verified', soldAt: proven.soldAt, matchedBy: 'manual' } }
      : {};

    setRows(prev => {
      const next = [...prev];

      // Already filed, by whichever route. Doing it again would put the same
      // sale on a second row.
      if (proven && next.some(r => r.csfloatSale?.tradeId === proven.tradeId)) return prev;
      const siteFee = settings?.sites?.find(s => s.value === platform) || settings?.sites?.[0] || null;
      const priceStr = sellPrice ? parseFloat(sellPrice).toFixed(2) : '';

      // In order of how sure each one is: an index the caller chose, then the
      // asset that actually left, then the oldest same-skin holding.
      let idx = rowIndex;
      if (idx == null && sourceItem?.assetId) {
        idx = next.findIndex(r => r.assetId === sourceItem.assetId
          && !(parseFloat(r.sellPrice) > 0) && !r.tradeId);
      }
      if (idx == null || idx === -1) {
        idx = pickFifoMatch(next, r =>
          r.skin?.value === skinName &&
          (r.condition?.value || null) === condition &&
          !(parseFloat(r.sellPrice) > 0) && !r.tradeId,
          `${skinName} (${condition || 'no condition'})`
        );
      }

      if (idx !== -1 && next[idx]) {
        const buyStr = givenBasis ?? next[idx].buyPrice;
        const profit = calcProfit(buyStr, priceStr, siteFee);
        next[idx] = {
          ...next[idx], buyPrice: buyStr, sellPrice: priceStr, siteFee,
          profit: profit || '0', tradedFor: undefined, ...receipt,
          // The cost is on record now, so the flag that made the modal ask goes.
          ...(givenBasis ? { costUnknown: undefined } : {}),
        };
      } else {
        // Genuinely untracked — an item that was sold without ever being added.
        // Whatever the source knew about it is carried over, so the new row is a
        // real record rather than a bare name and a price.
        next.push({
          ...createEmptyRow(settings),
          skin:        { value: skinName, label: skinName },
          condition:   condition ? { value: condition, label: condition } : null,
          assetId:     sourceItem?.assetId || undefined,
          iconUrl:     sourceItem?.icon_url || sourceItem?.iconUrl || undefined,
          float:       sourceItem?.float ?? undefined,
          paintSeed:   sourceItem?.paintSeed ?? undefined,
          inspectLink: sourceItem?.inspectLink || '',
          sellPrice: priceStr, siteFee,
          profit:    calcProfit('0', priceStr, siteFee) || '0',
          timestamp: new Date().toISOString(),
          ...receipt,
        });
      }
      return next;
    });
  }, [settings]);

  // Called when an inventory item is marked as "traded"
  // allReceived: full array of received items for multi-item trades
  // sourceRowIndex: if provided, directly targets that row to mark it as traded
  const handleTradeItem = useCallback(({
    offeredItem, receivedName, receivedCondition, receivedAssetId,
    pendingTradeId, tradedAt, allReceived, sourceRowIndex, cashReceived, boot, tradedWith,
  }) => {
    const tradeId      = 'trade_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 6);
    const receivedList = allReceived || (receivedName ? [{ name: receivedName, condition: receivedCondition, assetId: receivedAssetId }] : []);
    const tradedForList = receivedList.map(r => ({ name: r.name, condition: r.condition || null }));

    // Signed: positive is cash you took, negative is cash you added. Callers
    // that predate boot still pass a positive `cashReceived`, which means the
    // same thing, so both are accepted rather than making every one of them change.
    const bootAmount = Number.isFinite(Number(boot?.amount))
      ? Number(boot.amount)
      : (parseFloat(cashReceived) || 0);
    const bootSource = boot?.source || null;

    const tradeExtra = {
      ...(bootAmount !== 0 ? { boot: { amount: bootAmount, source: bootSource } } : {}),
      ...(tradedWith ? { tradedWith } : {}),
    };

    // The cash moves only when a wallet was named. A boot with no source is a
    // complete, valid record: the amount is what fixes the cost basis, and
    // whether any balance follows it is a separate choice.
    if (bootAmount !== 0 && bootSource) {
      void recordWalletSpend({
        id: `${tradeId}_boot`,
        source: bootSource,
        amount: bootAmount,
        reason: 'trade-boot',
        ref: tradeId,
        note: receivedList[0]?.name ? `Trade for ${receivedList[0].name}` : 'Trade',
      });
    }

    const offeredLabel = offeredItem?.market_hash_name || offeredItem?.skin?.value || '(unknown)';
    console.log(`[handleTradeItem] tradeId=${tradeId} offered="${offeredLabel}" received=${receivedList.length} tradedWith=${tradedWith || null}`);

    setRows(prev => {
      const next = [...prev];

      // Find the offered row and mark it as traded
      let offeredIdx = sourceRowIndex != null ? sourceRowIndex : -1;
      if (offeredIdx === -1 && offeredItem) {
        const offeredNameForMatch = stripCondition(offeredItem.market_hash_name || offeredItem.skin?.value || '');
        offeredIdx = pickFifoMatch(next, r =>
          (r.assetId && r.assetId === offeredItem.assetId) ||
          (r.skin?.value === offeredNameForMatch &&
           !(parseFloat(r.sellPrice) > 0) && !r.tradeId),
          offeredNameForMatch
        );
      }

      // Cost basis of the offered row (if it was actually tracked) minus any cash
      // received — this is what gets allocated across the received items below,
      // so a later sale of a received item nets against real cost instead of
      // treating the whole sale price as profit. The offered row's own buyPrice
      // is left untouched as a historical record — it's already excluded from
      // every profit aggregate (traded-away rows), so nothing double-counts.
      let offeredBuyPrice = 0;

      if (offeredIdx !== -1 && next[offeredIdx]) {
        console.log(`[handleTradeItem] found offered row at index ${offeredIdx}: "${next[offeredIdx].skin?.value}"`);
        // A cost carried on offeredItem means the sale modal had to ask for it,
        // because the row had none — an item tracked from the vanished-items
        // prompt, say. It is written onto the row BEFORE being read, since it
        // is the real basis of this trade and everything received splits it.
        // Never overwrites a cost the row already has.
        const given = parseFloat(offeredItem?.buyPrice);
        const useGiven = Number.isFinite(given) && given > 0
          && !(parseFloat(next[offeredIdx].buyPrice) > 0);
        const buyStr = useGiven ? given.toFixed(2) : next[offeredIdx].buyPrice;

        offeredBuyPrice = parseFloat(buyStr) || 0;
        next[offeredIdx] = {
          ...next[offeredIdx], tradeId, tradedFor: tradedForList, ...tradeExtra,
          // The trade's own date. `timestamp` is left alone because it is when
          // this item was acquired, and an item held for months before being
          // traded would otherwise report the trade on the day it was bought.
          tradedAt: tradedAt || new Date().toISOString(),
          ...(useGiven ? { buyPrice: buyStr, costUnknown: undefined } : {}),
        };
      } else if (offeredItem) {
        // Item was never in the tracker — create a row for it so the trade is recorded
        const offeredName = stripCondition(
          offeredItem.market_hash_name || offeredItem.skin?.value || ''
        );
        const offeredCond =
          offeredItem.condition?.value || EXTERIOR_TO_COND[offeredItem.exterior] || null;
        console.log(`[handleTradeItem] offered row NOT found in tracker → creating new row: "${offeredName}" (assetId=${offeredItem.assetId})`);
        if (offeredName) {
          next.push({
            ...createEmptyRow(settings),
            skin:      { value: offeredName, label: offeredName },
            condition: offeredCond ? { value: offeredCond, label: offeredCond } : null,
            assetId:   offeredItem.assetId  || undefined,
            float:     offeredItem.float    ?? undefined,
            paintSeed: offeredItem.paintSeed ?? undefined,
            buyPrice:  '0',
            tradeId,
            tradedFor: tradedForList,
            tradedAt:  tradedAt || new Date().toISOString(),
            timestamp: tradedAt || new Date().toISOString(),
            ...tradeExtra,
          });
        } else {
          console.warn(`[handleTradeItem] could not determine offered item name — row NOT created`);
        }
      } else {
        console.warn(`[handleTradeItem] no offeredItem and offeredIdx=-1 — nothing to mark`);
      }

      // Create rows for every received item (always, even if not in inventory yet).
      // Split the offered item's leftover cost basis across them, proportional to
      // each item's live Buff price at trade time (falls back to an even split
      // when no live price is available) — same largest-remainder cents allocation
      // server/routes/groupedPurchases.js uses to split a purchase across items.
      //
      // Boot moves that basis in both directions: cash taken means the received
      // items cost you that much less, cash added means they cost that much
      // more. Ignoring the second case would understate what you paid and
      // overstate every future profit on the items you got.
      const validReceived = receivedList.filter(r => r.name);
      const allocatableCents = Math.round(Math.max(0, offeredBuyPrice - bootAmount) * 100);
      const weights = validReceived.map(r => {
        const cond = r.condition ? { value: r.condition } : null;
        return getBuffPrice(livePrices, { skin: { value: r.name }, condition: cond }) || 0;
      });
      const allocatedCents = allocatableCents > 0
        ? allocateCents(weights, allocatableCents)
        : validReceived.map(() => 0);

      // Whether the split above actually managed to weight by value. With no
      // live price for any of them allocateCents falls back to an even split,
      // and stamping that as "value-weighted" would tell the server's repair
      // pass to leave a basis alone that is exactly what it exists to fix.
      const weightedSplit = weights.some(w => w > 0) && weights.every(w => w > 0);
      const splitDay = (tradedAt ? new Date(tradedAt) : new Date()).toISOString().slice(0, 10);

      const toAdd = validReceived.map((r, i) => {
        console.log(`[handleTradeItem] creating received row: "${r.name}" (assetId=${r.assetId}) buyPrice=${(allocatedCents[i] / 100).toFixed(2)}`);
        return {
          ...createEmptyRow(settings),
          skin:               { value: r.name, label: r.name },
          condition:          r.condition ? { value: r.condition, label: r.condition } : null,
          assetId:            r.assetId || undefined,
          buyPrice:           (allocatedCents[i] / 100).toFixed(2),
          receivedFromTradeId: tradeId,
          // Same stamp the server writes, for the same two readers: the repair
          // pass, which must skip a basis somebody already chose properly, and
          // the detail modal, which explains where the number came from.
          ...(weightedSplit ? {
            basisSplit: {
              method: 'value-weighted', at: splitDay, pricedOn: splitDay,
              daysOff: 0, price: weights[i], on: new Date().toISOString(),
            },
          } : {}),
          description:        '',
          timestamp:          tradedAt || new Date().toISOString(),
          ...(tradedWith ? { tradedWith } : {}),
        };
      });

      console.log(`[handleTradeItem] done — ${toAdd.length} received row(s) added`);
      return toAdd.length > 0 ? [...next, ...toAdd] : next;
    });
  }, [settings, livePrices, recordWalletSpend]);

  // ── CSV export ─────────────────────────────────────────
  const handleExportCSV = useCallback(() => {
    const headers = ['Date','Skin','Condition','Type','Float','Pattern','Buy Price','Sell Price','Profit','Platform','Target Price','Stickers','Description'];
    const csvRows = [headers.join(',')];
    (rows || []).forEach(r => {
      if (!r.skin?.value) return;
      const stickersStr = (r.stickers || []).map(s => s.name).join('; ');
      csvRows.push([
        new Date(r.timestamp).toLocaleDateString(),
        `"${r.skin.value}"`,
        r.condition?.value || '',
        getItemTypes(r).map(t=>t.label).join('+') || '',
        r.float != null ? r.float.toFixed(6) : '',
        r.paintSeed != null ? r.paintSeed : '',
        r.buyPrice || '',
        r.sellPrice || '',
        r.profit || '',
        r.siteFee?.label?.split(' ')[0] || '',
        r.targetPrice || '',
        `"${stickersStr}"`,
        `"${(r.description || '').replace(/"/g, '""')}"`,
      ].join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `cs2-tracker-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows]);

  if (!user) return <Navigate to="/signin" replace />;
  if (loadError) return (
    <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:12, color:'var(--text-muted)' }}>
      <div style={{ fontSize:'1.09rem', fontWeight:600, color:'var(--red)' }}>Could not connect to server</div>
      <div style={{ fontSize:'0.98rem' }}>Make sure the server is running, then refresh the page.</div>
      <button onClick={() => window.location.reload()} style={{ marginTop:8, padding:'8px 20px', borderRadius:8, background:'rgba(255,255,255,0.08)', border:'1px solid var(--border)', color:'var(--text)', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:'0.98rem' }}>
        Refresh
      </button>
    </div>
  );
  if (!rows || !settings) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-muted)' }}>Loading…</div>
  );

  // ── Tracker Content ────────────────────────────────────
  // Exclude rows whose item type has overrideProfitLoss set
  const unsold = rows.filter(r => !(parseFloat(r.sellPrice) > 0) && r.skin?.value);
  // Alert + pending trade counts for navbar
  const alertCount       = unsold.filter(r => { const t=parseFloat(r.targetPrice); const lp=getLivePrice(livePrices,r); return t>0&&lp?.starting_at?.price>=t; }).length;
  const pendingTradeCount = (settings?.pendingTrades||[]).filter(pt=>pt.status==='pending').length;

  // Adds picked inventory items to the tracker as new rows. Shared by the
  // Inventory tab and its Zone twin so both behave identically.
  const addInventoryRows = (assetIds, asInvestment, folderId = null) => {
    // Covered items are refused here as well as darkened in the grid. The card
    // is not clickable, but a selection made before the last sync landed can
    // still arrive holding an item a trade has since put into Live, and one
    // stale click is all a duplicate row has ever needed.
    const itemsToAdd = syncedInventory
      .filter(i => assetIds.includes(i.assetId) && !trackedAssetIds.has(i.assetId));
    const newRows = itemsToAdd.map(item => {
      const mhn = item.marketHashName || item.market_hash_name || '';
      const baseName = mhn.replace(/ \((Factory New|Minimal Wear|Field-Tested|Well-Worn|Battle-Scarred)\)$/, '').trim();
      const condVal  = EXTERIOR_TO_CONDITION[item.exterior] || null;
      const price    = livePrices?.[mhn]?.starting_at?.price ?? null;
      const stickerDesc = (item.stickers || []).filter(s => s.name).map(s => s.name).join(', ');
      return {
        ...createEmptyRow(settings),
        skin:                { value: baseName, label: baseName },
        condition:           condVal ? { value: condVal, label: condVal } : null,
        assetId:             item.assetId,
        iconUrl:             item.iconUrl || item.icon_url || null,
        inspectLink:         item.inspectLink || '',
        float:               item.float ?? null,
        paintSeed:           item.paintSeed ?? null,
        description:         stickerDesc,
        stickers:            (item.stickers || []).filter(s => s.name),
        charms:              (item.charms || []).filter(c => c.name),
        buyPrice:            price != null ? price.toFixed(2) : '',
        timestamp:           new Date().toISOString(),
        tradeProtected:      item.tradeProtected ?? false,
        tradeProtectedUntil: item.tradeProtectedUntil ?? null,
        isInvestment:        asInvestment === true,
        // Only an investment can be filed. A live holding carrying a folder id
        // would start showing up in the Vault the moment it was flagged later.
        investmentFolder:    asInvestment === true ? (folderId || null) : null,
      };
    });
    if (newRows.length > 0) {
      setRows(prev => [...prev, ...newRows]);
      setSelectedForGroup(new Set());
    }
  };

  // Sending to Live is a straight add. Sending to the Vault asks which folder
  // first: filing at the door is the only moment the user still remembers why
  // these particular items were bought, and "No folder" is one click away.
  const sendInventoryToTracker = (assetIds, asInvestment) => {
    if (!asInvestment) { addInventoryRows(assetIds, false); return; }
    const ids = Array.from(assetIds || []);
    if (!ids.length) return;
    // The names come along so the picker can work out which collections the
    // batch is from and offer those folders first.
    const names = syncedInventory
      .filter(i => ids.includes(i.assetId))
      .map(i => i.marketHashName || i.market_hash_name || '')
      .filter(Boolean);
    setFolderPick({ assetIds: ids, names });
  };

  return (
    <SettlingProvider inFlight={saleReconcile?.inFlight} rows={rows}>
    <TradeHistoryProvider events={tradeHistory}>
    <div className={`app-shell${zoneTheme ? ' zn-theme' : ''}${zoneWidth === 'wide' ? ' zn-wide' : ''}${isMobile ? ' mb-shell' : ''}`}>
      {/* ── Phone shell ────────────────────────────────────────────────
          Replaces the navbar, the tab bar and the pages below 768px. The
          modals further down are shared, so "edit item" or "settings"
          opens the same component on either shell. */}
      {isMobile && (
        <MobileApp
          activeTab={activeTab} onChangeTab={setActiveTab}
          user={user} steamName={account?.steamName} navTheme={navTheme} onToggleTheme={toggleNavTheme}
          onSettings={() => setShowSettings(true)} onLogout={handleLogout}
          alertCount={alertCount} pendingTradeCount={pendingTradeCount}
          saveStatus={saveStatus}
          rows={rows} settings={settings} livePrices={livePrices}
          skinsData={skinsData} inventoryMap={inventoryMap} wishlist={wishlist}
          onEdit={(r, ri) => setEditingItem({ row: r, realIndex: ri })}
          onAddItem={() => setShowAddModal(true)}
          onBulkAdd={() => setShowBulkAdd(true)}
          onImportInventory={() => setShowInventoryImport(true)}
          onExportCSV={handleExportCSV}
          soldCount={(rows||[]).filter(isRowSold).length}
          tradedCount={(rows||[]).filter(isRowTradedAway).length}
          investCount={investCount}
          syncedInventory={syncedInventory}
          setSyncedInventory={setSyncedInventory}
          storedItems={storedItems}
          selectedForGroup={selectedForGroup}
          setSelectedForGroup={setSelectedForGroup}
          groupedPurchases={groupedPurchases}
          setGroupedPurchases={setGroupedPurchases}
          onOpenGroupModal={() => setShowGroupedPurchase(true)}
          trackedAssetIds={trackedAssetIds}
          onSendToTracker={sendInventoryToTracker}
          onSellVaultUnit={(row, realIndex) => setSoldTradedModal({ row, realIndex })}
          onCreateFolder={createFolder}
          onAssignFolder={assignFolder}
          onReturnToLive={returnToLive}
        />
      )}

      {!isMobile && (<>
      {(saveStatus === 'saving' || saveStatus === 'error') && (
        <div style={{
          position: 'fixed', bottom: 16, right: 16, zIndex: 9999,
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '7px 12px', borderRadius: 8,
          background: 'var(--panel, #0b1220)', border: '1px solid var(--border, #232b38)',
          color: saveStatus === 'error' ? '#f87171' : 'var(--text-dim, #8d97ab)',
          fontFamily: 'var(--font-body)', fontSize: '0.96rem', boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
        }}>
          {saveStatus === 'saving' ? 'Saving…' : 'Save failed — retrying'}
        </div>
      )}
      <AppNavbar username={user} account={account} onSettings={()=>setShowSettings(true)} onLogout={handleLogout}
        onProfile={()=>setActiveTab('profile')} profileActive={activeTab==='profile'}
        alertCount={alertCount} pendingTradeCount={pendingTradeCount}
        navTheme={navTheme} onToggleTheme={toggleNavTheme}
        zoneWidth={zoneWidth} onToggleWidth={toggleZoneWidth}
        wallets={wallets} onSaveWallet={saveWallet} onRefreshCrypto={refreshCrypto}
        onLoadWalletLedger={loadWalletLedger} walletTransfers={transfers}
        cryptoIsAutomatic={cryptoIsAutomatic} onResolveTransfer={resolveTransfer}
        onDepositWallet={depositToWallet}
        useBankWallet={settings?.useBankWallet !== false} />
      <TabBar active={activeTab} onChange={setActiveTab} pricesLoaded={!!livePrices}
        navTheme={navTheme}
        soldCount={(rows||[]).filter(isRowSold).length}
        tradedCount={(rows||[]).filter(isRowTradedAway).length}
        investCount={investCount}
        lendingEnabled={lendingOn} lentCount={lentCount}
      />

      {/* Trades linked from Steam that may have had cash on the side. Shown
          anywhere, not just one tab: a wrong cost basis affects every figure in
          the app, so it should not depend on visiting the right page. */}
      {tradeBoot.length > 0 && !tradeBootDismissed && (
        <div style={{ padding: '12px 20px 0' }}>
          <TradeBootBanner
            count={tradeBoot.length}
            variant={zoneTheme ? 'zone' : 'classic'}
            onReview={() => setShowTradeBoot(true)}
            onDismiss={() => setTradeBootDismissed(true)}
          />
        </div>
      )}

      {showTradeBoot && (
        <TradeBootModal
          pending={tradeBoot}
          variant={zoneTheme ? 'zone' : 'classic'}
          trackWallets={settings?.trackWallets !== false}
          useBank={settings?.useBankWallet !== false}
          onResolve={resolveTradeBoot}
          onDismissOne={dismissTradeBoot}
          onClose={() => setShowTradeBoot(false)}
        />
      )}

      {/* Positions that closed themselves. Shown anywhere, like the trade-boot
          banner and for the same reason: rows moving between Live and Sold
          changes every figure in the app, so being told should not depend on
          which tab you happen to be looking at. */}
      {!saleReconcileDismissed
        && (saleReconcile?.applied?.length || saleReconcile?.unmatched?.length || saleReconcile?.inFlight?.length) > 0 && (
        <div style={{ padding: '12px 20px 0' }}>
          <AutoSoldBanner
            applied={saleReconcile.applied}
            inFlight={saleReconcile.inFlight}
            unmatched={saleReconcile.unmatched}
            autoFiled={saleReconcile.autoFiled}
            variant={zoneTheme ? 'zone' : 'classic'}
            onReview={saleReconcile.unmatched?.length ? () => setShowUntrackedSales(true) : null}
            onDismiss={() => setSaleReconcileDismissed(true)}
          />
        </div>
      )}

      {showUntrackedSales && (
        <UntrackedSalesModal
          sales={saleReconcile?.unmatched || []}
          rows={rows || []}
          // Marketplace sales carry no picture of their own — see the note at
          // the top of NewItemsPrompt.
          skinsData={skinsData}
          // What each applied sticker is worth today, for the strip's hover
          // card. The sale record carries the price CSFloat reported at the
          // time, in cents, which is a different figure in a different unit —
          // so the live map is the one that gets shown.
          livePrices={livePrices}
          variant={zoneTheme ? 'zone' : 'classic'}
          onAdd={handleAddUntrackedSale}
          onAddMany={handleAddUntrackedSales}
          onCloseRow={handleCloseRowForSale}
          onIgnore={closeSaleQuestion}
          onClose={() => setShowUntrackedSales(false)}
        />
      )}

      {/* New-items notification banner — shown on the Inventory tabs */}
      {(() => {
        const untrackedCount = newItemsForReview.filter(i => !trackedAssetIds.has(i.assetId)).length;
        if (untrackedCount === 0 || newItemsBannerDismissed) return null;
        if (activeTab !== 'extension') return null;
        return (
          <div style={{ padding: '12px 20px 0' }}>
            <NewItemsBanner
              count={untrackedCount}
              variant={zoneTheme ? 'zone' : 'classic'}
              onReview={() => setShowNewItemsModal(true)}
              onDismiss={() => setNewItemsBannerDismissed(true)}
            />
          </div>
        );
      })()}

      {/* ── Themed pages — the Classic/Zone switch picks the component ──
          Both halves of every pair read the same props and show the same
          figures; only the styling differs. */}
      {activeTab==='overview3' && (zoneTheme
        ? <OverviewTabZone rows={rows} settings={settings} livePrices={livePrices} csfloatPrices={csfloatPrices} skinsData={skinsData} inventoryMap={inventoryMap} watchedKeys={watchedKeys} onToggleWatch={handleToggleWatch} />
        : <OverviewTab3    rows={rows} settings={settings} livePrices={livePrices} csfloatPrices={csfloatPrices} skinsData={skinsData} inventoryMap={inventoryMap} watchedKeys={watchedKeys} onToggleWatch={handleToggleWatch} />)}
      {activeTab==='analytics2' && (zoneTheme
        ? <AnalyticsTabZone rows={rows} settings={settings} livePrices={livePrices} skinsData={skinsData} />
        : <AnalyticsTab2    rows={rows} settings={settings} livePrices={livePrices} skinsData={skinsData} />)}
      {activeTab==='tracker2' && (zoneTheme
        ? <LiveTabZone rows={rows||[]} settings={settings} livePrices={livePrices} csfloatPrices={csfloatPrices} skinsData={skinsData} inventoryMap={inventoryMap} tradePartners={tradePartners} onEdit={(r,ri)=>setEditingItem({row:r,realIndex:ri})}
            onBulkAdd={()=>setShowBulkAdd(true)}
            onGroupPrice={setGroupPriceItems} onSetCategory={setRowsCategory}
            lendingEnabled={lendingOn} onLendItems={openLendModal} onMoveToInvestment={moveToInvestment} />
        : <LiveTab2    rows={rows||[]} settings={settings} livePrices={livePrices} csfloatPrices={csfloatPrices} skinsData={skinsData} inventoryMap={inventoryMap} tradePartners={tradePartners} onEdit={(r,ri)=>setEditingItem({row:r,realIndex:ri})}
            onBulkAdd={()=>setShowBulkAdd(true)}
            onGroupPrice={setGroupPriceItems} onSetCategory={setRowsCategory}
            lendingEnabled={lendingOn} onLendItems={openLendModal} onMoveToInvestment={moveToInvestment} />)}
      {activeTab==='sold3' && (zoneTheme
        ? <SoldTabZone rows={rows||[]} skinsData={skinsData} livePrices={livePrices} csfloatPrices={csfloatPrices} onEdit={(r,ri)=>setEditingItem({row:r,realIndex:ri})} />
        : <SoldTab3    rows={rows||[]} settings={settings} skinsData={skinsData} livePrices={livePrices} csfloatPrices={csfloatPrices} onEdit={(r,ri)=>setEditingItem({row:r,realIndex:ri})} />)}
      {activeTab==='traded2' && (zoneTheme
        ? <TradedTabZone rows={rows||[]} skinsData={skinsData} livePrices={livePrices} csfloatPrices={csfloatPrices} tradePartners={tradePartners}
            lendingEnabled={lendingOn} onLendFromTrade={lendFromTrade} />
        : <TradedTab2    rows={rows||[]} settings={settings} skinsData={skinsData} livePrices={livePrices} csfloatPrices={csfloatPrices} tradePartners={tradePartners}
            lendingEnabled={lendingOn} onLendFromTrade={lendFromTrade} />)}
      {/* Optional page. The tab is hidden when the setting is off, but the guard
          is repeated here so a stale `activeTab` from before it was switched off
          renders nothing rather than a page that is supposed to be gone. */}
      {activeTab==='lending' && lendingOn && (zoneTheme
        ? <LendingTabZone rows={rows||[]} skinsData={skinsData} livePrices={livePrices} csfloatPrices={csfloatPrices}
            inventoryMap={inventoryMap} syncedInventory={syncedInventory}
            onLendItems={openLendModal} onMarkReturned={markLendReturned} onWriteOff={writeOffLend} />
        : <LendingTab     rows={rows||[]} settings={settings} skinsData={skinsData} livePrices={livePrices} csfloatPrices={csfloatPrices}
            inventoryMap={inventoryMap} syncedInventory={syncedInventory}
            onLendItems={openLendModal} onMarkReturned={markLendReturned} onWriteOff={writeOffLend} />)}
      {activeTab==='history' && (zoneTheme
        ? <HistoryTabZone skinsData={skinsData} />
        : <HistoryTab     settings={settings} skinsData={skinsData} />)}
      {/* The board is also where wishes are written down: the heart button can
          only ever watch something already owned, and a watchlist is mostly
          about what is not. Both themes get the same editor. */}
      {activeTab==='wishlist2' && (() => {
        const wishProps = {
          wishlist, livePrices, csfloatPrices, skinsData, itemsWithConditions,
          onSave: handleWishlistSave, onDelete: handleWishlistDelete,
        };
        return zoneTheme ? <WishlistTabZone {...wishProps} /> : <WishlistTab2 {...wishProps} />;
      })()}
      {/* Pricer: the item picker and the price feeds are all it needs — no
          rows, so it is the one page that works on an empty account. Both
          themes render the same basket hook, so switching keeps the pile. */}
      {activeTab==='pricer' && (() => {
        const pricerProps = { skinsData, searchableOptions, livePrices, csfloatPrices, itemsWithConditions };
        return zoneTheme ? <PricerTabZone {...pricerProps} /> : <PricerTab {...pricerProps} />;
      })()}
      {activeTab==='investments2' && (() => {
        // Selling a vault unit goes through the very same modal the Live tabs
        // use, so a sale out of the Vault and a sale out of Live produce
        // identical rows — including the trade branch, which is how a knife
        // that was traded rather than sold gets recorded correctly.
        const vaultProps = {
          rows: rows || [], settings, skinsData, livePrices, csfloatPrices,
          onSell: (row, realIndex) => setSoldTradedModal({ row, realIndex }),
          onEdit: (row, realIndex) => setEditingItem({ row, realIndex }),
          onCreateFolder: createFolder,
          onUpdateFolder: updateFolder,
          onDeleteFolder: deleteFolder,
          onAssignFolder: assignFolder,
          onReturnToLive: returnToLive,
        };
        return zoneTheme
          ? <InvestmentsTabZone {...vaultProps} />
          : <InvestmentsTab2    {...vaultProps} />;
      })()}
      {activeTab==='profile' && (
        <ProfileTab
          user={user}
          rowCount={rows?.length || 0}
          inventoryCount={syncedInventory?.length || 0}
          zone={zoneTheme}
        />
      )}
      {activeTab==='extension' && (() => {
        const InventoryPage = zoneTheme ? InventoryTabZone : ExtensionTabContent;
        return (
          <InventoryPage
            user={user}
            livePrices={livePrices}
            csfloatPrices={csfloatPrices}
            syncedInventory={syncedInventory}
            setSyncedInventory={setSyncedInventory}
            storedItems={storedItems}
            selectedForGroup={selectedForGroup}
            setSelectedForGroup={setSelectedForGroup}
            groupedPurchases={groupedPurchases}
            setGroupedPurchases={setGroupedPurchases}
            onOpenGroupModal={() => setShowGroupedPurchase(true)}
            onReloadInventory={loadSyncedInventory}
            trackedAssetIds={trackedAssetIds}
            tradePartners={tradePartners}
            onSendToTracker={sendInventoryToTracker}
            lendingEnabled={lendingOn}
            onLendItems={lendFromInventory}
          />
        );
      })()}

      {/* ── Classic-only pages (no Zone twin yet) ────────────────────── */}
      {activeTab==='overview5' && <OverviewTab5 rows={rows} settings={settings} livePrices={livePrices} skinsData={skinsData} inventoryMap={inventoryMap} />}
      {activeTab==='overview7' && <OverviewTab7 rows={rows} settings={settings} livePrices={livePrices} skinsData={skinsData} />}
      {activeTab==='sold2'     && <SoldTab2 rows={rows||[]} settings={settings} skinsData={skinsData} onEdit={(r,ri)=>setEditingItem({row:r,realIndex:ri})} />}
      {activeTab==='tracker3'  && <LiveTab3 rows={rows||[]} settings={settings} livePrices={livePrices} skinsData={skinsData} inventoryMap={inventoryMap} onEdit={(r,ri)=>setEditingItem({row:r,realIndex:ri})} />}
      {activeTab==='traded'    && <TradedTab rows={rows||[]} settings={settings} skinsData={skinsData} livePrices={livePrices} csfloatPrices={csfloatPrices} onMarkReceived={(r,ri)=>setSoldTradedModal({row:r,realIndex:ri})} onUpdateRow={(oldRow, updates) => setRows(prev => prev.map(r => r === oldRow ? { ...r, ...updates } : r))} />}
      {activeTab==='investments' && <InvestmentsTab rows={rows||[]} settings={settings} skinsData={skinsData} livePrices={livePrices}
        onDeleteRow={row => setRows(prev => prev.filter(r => r !== row))}
        onMarkSoldTraded={row => { const ri = (rows||[]).indexOf(row); if (ri !== -1) setSoldTradedModal({ row, realIndex: ri }); }}
        onUpdateRow={(oldRow, updates) => setRows(prev => prev.map(r => r === oldRow ? { ...r, ...updates } : r))}
      />}

      {/* ── Multi-sell floating action bar ──────────────── */}
      {selectMode && selectedForSell.size > 0 && (() => {
        const selRows = [...selectedForSell].map(ri => rows[ri]).filter(Boolean);
        const totalBuy = selRows.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
        const handleBulkDelete = () => {
          if (!window.confirm(`Delete ${selectedForSell.size} item${selectedForSell.size !== 1 ? 's' : ''}? This cannot be undone.`)) return;
          const toDelete = new Set(selectedForSell);
          setRows(prev => prev.filter((_, i) => !toDelete.has(i)));
          setSelectedForSell(new Set());
          setSelectMode(false);
        };
        const handleBulkSetType = (type) => {
          const sel = selectedForSell;
          setRows(prev => prev.map((r, i) => sel.has(i) ? { ...r, itemType: type } : r));
          setShowBulkTypeMenu(false);
        };
        return (
          <div style={{
            position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', alignItems: 'center', gap: 12,
            background: '#13172a', border: '1px solid rgba(99,102,241,0.45)',
            borderRadius: 16, padding: '12px 22px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.55)', zIndex: 50,
            whiteSpace: 'nowrap', flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: '0.99rem', color: '#c7d2fe', fontWeight: 600 }}>
              {selectedForSell.size} item{selectedForSell.size !== 1 ? 's' : ''} selected
            </span>
            {totalBuy > 0 && (
              <span style={{ fontSize: '0.95rem', color: 'var(--text-dim)' }}>
                Total buy: <strong style={{ color: 'var(--text)' }}>${totalBuy.toFixed(2)}</strong>
              </span>
            )}
            <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.10)' }} />
            <button onClick={() => setShowGroupSell(true)} style={{
              padding: '7px 18px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: 'var(--green)', color: '#000', fontSize: '0.98rem', fontWeight: 700,
              boxShadow: '0 2px 10px rgba(74,222,128,0.30)',
            }}>
              Group Sell
            </button>
            {/* Bulk type-set */}
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowBulkTypeMenu(v => !v)} style={{
                padding: '7px 16px', borderRadius: 10, cursor: 'pointer',
                border: '1px solid rgba(129,140,248,0.40)',
                background: 'rgba(129,140,248,0.12)', color: '#a5b4fc',
                fontSize: '0.97rem', fontWeight: 600,
              }}>
                Set Type
              </button>
              {showBulkTypeMenu && (
                <div style={{
                  position: 'absolute', bottom: '110%', left: 0,
                  background: '#13172a', border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 12, padding: '6px 0', minWidth: 150,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.55)', zIndex: 100,
                }}>
                  {(settings?.itemTypes || []).map(t => (
                    <button key={t.value} onClick={() => handleBulkSetType({ value: t.value, label: t.label })} style={{
                      display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                      padding: '7px 14px', background: 'transparent', border: 'none', cursor: 'pointer',
                      color: 'var(--text)', fontSize: '0.97rem',
                    }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.color, flexShrink: 0 }} />
                      {t.label}
                    </button>
                  ))}
                  <button onClick={() => handleBulkSetType(null)} style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '7px 14px', background: 'transparent', border: 'none',
                    borderTop: '1px solid rgba(255,255,255,0.07)',
                    cursor: 'pointer', color: 'var(--text-dim)', fontSize: '0.97rem', marginTop: 4,
                  }}>
                    Clear type
                  </button>
                </div>
              )}
            </div>
            {/* Bulk delete */}
            <button onClick={handleBulkDelete} style={{
              padding: '7px 16px', borderRadius: 10, cursor: 'pointer',
              border: '1px solid rgba(248,113,113,0.40)',
              background: 'rgba(248,113,113,0.10)', color: '#f87171',
              fontSize: '0.97rem', fontWeight: 600,
            }}>
              Delete
            </button>
          </div>
        );
      })()}

      {/* Scroll button */}
      <button
        onClick={()=>isAtBottom?window.scrollTo({top:0,behavior:'smooth'}):window.scrollTo({top:document.body.scrollHeight,behavior:'smooth'})}
        style={{
          position:'fixed', bottom:24, right:24, width:44, height:44, borderRadius:'50%',
          background:'var(--panel)', border:'1px solid var(--border)',
          color:'var(--text-muted)', fontSize:'1.17rem', cursor:'pointer',
          display:'flex', alignItems:'center', justifyContent:'center',
          boxShadow:'0 8px 24px rgba(0,0,0,0.40)', transition:'all .15s ease', zIndex:40,
        }}
        onMouseEnter={e=>{e.currentTarget.style.color='#fff';e.currentTarget.style.borderColor='var(--border-hover)';}}
        onMouseLeave={e=>{e.currentTarget.style.color='var(--text-muted)';e.currentTarget.style.borderColor='var(--border)';}}
      >
        <span style={{ display:'inline-block', transform:isAtBottom?'rotate(180deg)':'none', transition:'transform .3s' }}>↓</span>
      </button>

      </>)}

      {/* Items that left the inventory with nothing to explain them.
          Deliberately outside the desktop-only block above: an item vanishing
          changes what you own, so being asked about it should not depend on
          which shell or which tab you happen to be on. The provable case never
          reaches here — saleReconciler has already closed it. */}
      <VanishedItemsPrompt
        user={user}
        rows={rows || []}
        refreshKey={vanishCheck}
        variant={zoneTheme ? 'zone' : 'classic'}
        // What the server could work out about the same items. A sale still
        // inside trade protection explains a disappearance without proving it
        // hard enough to write, so the queue keeps the entry and this turns the
        // question into a statement.
        inFlight={saleReconcile?.inFlight || []}
        onSell={(row, realIndex) => setSoldTradedModal({ row, realIndex })}
        onTrack={trackVanishedItem}
        onStored={loadStoredItems}
      />

      {/* ── Shared modals — both shells open the same components ────── */}

      {/* "Which folder?" on the way into the Vault. Cancelling adds nothing:
          the items stay in the locker, unchanged. */}
      {folderPick && (
        <FolderPickModal
          folders={getFolders(settings)}
          variant={zoneTheme ? 'zone' : 'classic'}
          names={folderPick.names}
          title={`Send ${folderPick.assetIds.length} item${folderPick.assetIds.length !== 1 ? 's' : ''} to the Vault`}
          subtitle="File them now, or leave them unfiled and sort them later. Either way they leave the Live tabs."
          confirmLabel={`Send ${folderPick.assetIds.length} item${folderPick.assetIds.length !== 1 ? 's' : ''}`}
          note="Investments live in the Vault only."
          onConfirm={pick => {
            const id = applyFolderPick(pick, getFolders(settings), createFolder);
            addInventoryRows(folderPick.assetIds, true, id);
          }}
          onClose={() => setFolderPick(null)}
        />
      )}

      {/* Add item modal */}
      {showAddModal && (() => {
        const shared = {
          isNew: true, realIndex: -1,
          initialRow: createEmptyRow(settings),
          onSave: handleSaveItem, onClose: () => setShowAddModal(false),
          onDelete: handleDeleteItem,
          settings, searchableOptions, skinsData, livePrices, itemsWithConditions,
        };
        // Zone pages get the paper form; the classic form keeps its duplicate
        // hint, which the Alt layouts never had.
        return zoneTheme
          ? <ItemFormModalAlt design="zone" {...shared} />
          : <ItemFormModal
              {...shared}
              liveKeys={new Set((rows||[]).filter(isRowLive).map(r=>`${r.skin?.value}__${r.condition?.value||''}`))}
            />;
      })()}

      {/* Sold / Traded modal */}
      {soldTradedModal && (
        <MarkSoldTradedModal
          row={soldTradedModal.row}
          settings={settings}
          inventoryItems={syncedInventory}
          claimedAssetIds={new Set(rows.filter(r => r.receivedFromTradeId && r.assetId).map(r => r.assetId))}
          searchableOptions={searchableOptions}
          livePrices={livePrices}
          csfloatPrices={csfloatPrices}
          onClose={() => setSoldTradedModal(null)}
          onConfirm={result => {
            if (result.mode === 'sold') {
              handleSellItem(
                soldTradedModal.row.skin?.value,
                soldTradedModal.row.condition?.value || null,
                result.platform || null,
                result.price,
                soldTradedModal.realIndex,
                { buyPrice: result.buyPrice },
              );
            } else {
              handleTradeItem({
                offeredItem: {
                  ...soldTradedModal.row,
                  // A cost the modal had to ask for is the real basis of this
                  // trade; without it every received item inherits nothing.
                  ...(result.buyPrice ? { buyPrice: result.buyPrice } : {}),
                  market_hash_name: soldTradedModal.row.skin?.value,
                },
                receivedName: result.receivedItems[0]?.name || null,
                receivedCondition: result.receivedItems[0]?.condition || null,
                receivedAssetId: result.receivedItems[0]?.assetId || null,
                isInInventory: result.receivedItems[0]?.assetId
                  ? syncedInventory.some(i => i.assetId === result.receivedItems[0].assetId)
                  : false,
                tradedAt: new Date().toISOString(),
                allReceived: result.receivedItems,
                sourceRowIndex: soldTradedModal.realIndex,
                boot: result.boot,
              });
            }
            setSoldTradedModal(null);
          }}
        />
      )}

      {/* Edit item modal */}
      {editingItem && (() => {
        const shared = {
          isNew: false,
          initialRow: editingItem.row, realIndex: editingItem.realIndex,
          onSave: handleSaveItem, onClose: () => setEditingItem(null),
          onDelete: ri => { handleDeleteItem(ri); setEditingItem(null); },
          settings, searchableOptions, skinsData, livePrices, itemsWithConditions,
          switcher: <DesignSwitcher value={editDesign} onChange={chooseEditDesign} />,
        };
        return editDesign === 'classic'
          ? <ItemFormModal {...shared} />
          : <ItemFormModalAlt design={editDesign} {...shared} />;
      })()}

      {/* Inventory import */}
      {showInventoryImport && (
        <InventoryImportModal
          variant={zoneTheme ? 'zone' : 'classic'}
          onClose={() => setShowInventoryImport(false)}
          onImport={items => setRows(prev => [...prev, ...items])}
          settings={settings}
          username={user}
          rows={rows}
          livePrices={livePrices}
          csfloatPrices={csfloatPrices}
          searchableOptions={searchableOptions}
          onSteamIdSaved={handleSteamIdSaved}
          onSellItem={handleSellItem}
          onTradeItem={handleTradeItem}
          onReconcileSales={reconcileSales}
          tradeHistory={tradeHistory}
        />
      )}

      {/* Bulk add */}
      {showBulkAdd && (
        <BulkAddModal
          onClose={()=>setShowBulkAdd(false)}
          onAdd={items=>setRows(prev=>[...prev,...items])}
          settings={settings} searchableOptions={searchableOptions}
          variant={zoneTheme ? 'zone' : 'classic'}
        />
      )}

      {/* Settings */}
      {showSettings && (
        <SettingsModal onClose={()=>setShowSettings(false)} settings={settings} onUpdate={handleUpdateSettings} rows={rows} onRestore={handleRestore}
          cryptoWallet={wallets?.crypto} onRefreshCrypto={refreshCrypto} variant={zoneTheme ? 'zone' : 'classic'} />
      )}

      {/* Category modal */}
      {selCat && (
        <CategoryItems rows={rows} category={selCat} onClose={()=>setSelCat(null)} settings={settings} skinsData={skinsData} />
      )}

      {/* Grouped purchase modal */}
      {showGroupedPurchase && (
        <GroupedPurchaseModal
          user={user}
          variant={zoneTheme ? 'zone' : 'classic'}
          items={Array.from(selectedForGroup).map(id => syncedInventory.find(i => i.assetId === id)).filter(Boolean)}
          prices={livePrices}
          onSave={gp => {
            setGroupedPurchases(prev => [gp, ...prev]);
            setSelectedForGroup(new Set());

            // Add each item to the tracker rows with its assigned purchase price
            const newRows = (gp.enrichedItems || [])
              .filter(item => !trackedAssetIds.has(item.assetId))
              .map(item => {
                // Strip exterior suffix: "AK-47 | Redline (Field-Tested)" → "AK-47 | Redline"
                const mhn = item.marketHashName || '';
                const exteriorRe = /\s*\((Factory New|Minimal Wear|Field-Tested|Well-Worn|Battle-Scarred)\)$/;
                const skinName   = mhn.replace(exteriorRe, '').trim();
                const cond       = item.exterior
                  ? { value: item.exterior, label: item.exterior }
                  : null;
                const siteFee    = (settings.sites || DEFAULT_SETTINGS.sites)[0];
                return {
                  skin:        skinName ? { value: skinName, label: skinName } : null,
                  description: '',
                  condition:   cond,
                  itemType:    (settings.itemTypes || DEFAULT_SETTINGS.itemTypes)[0],
                  buyPrice:    String(item.assignedPurchasePrice ?? ''),
                  sellPrice:   '',
                  siteFee,
                  profit:      '',
                  timestamp:   new Date().toISOString(),
                  priceHistory: [],
                  float:               item.float               ?? null,
                  paintSeed:           item.paintSeed           ?? null,
                  inspectLink:         item.inspectLink         ?? null,
                  stickers:            item.stickers            ?? [],
                  charms:              item.charms              ?? [],
                  assetId:             item.assetId,
                  tradeProtected:      item.tradeProtected      ?? false,
                  tradeProtectedUntil: item.tradeProtectedUntil ?? null,
                };
              });

            if (newRows.length > 0) setRows(prev => [...prev, ...newRows]);
          }}
          onClose={() => setShowGroupedPurchase(false)}
        />
      )}

      {/* Group price modal — one purchase price split across several Live items */}
      {groupPriceItems && groupPriceItems.length > 0 && (
        <GroupPriceModal
          items={groupPriceItems}
          settings={settings}
          livePrices={livePrices}
          csfloatPrices={csfloatPrices}
          skinsData={skinsData}
          inventoryMap={inventoryMap}
          variant={zoneTheme ? 'zone' : 'classic'}
          onClose={() => setGroupPriceItems(null)}
          onConfirm={({ items: allocation, paidWith }) => {
            // Rows are saved one at a time through the normal item-save path, so
            // a re-priced purchase settles its wallet exactly as an edit in the
            // item form would: the old spend is reversed and the new one charged.
            //
            // The index the Live tab handed over was taken when the modal opened.
            // Look the row up by identity first, in case a sync or a
            // reconciliation shifted the array while the modal was on screen —
            // writing a price into whatever now sits at that index would put the
            // money on the wrong item.
            allocation.forEach(({ row, realIndex, buyPrice }) => {
              const idx = (rows || []).indexOf(row);
              const at  = idx !== -1 ? idx : realIndex;
              const current = (rows || [])[at];
              if (!current || current !== row) {
                console.warn('[groupPrice] row moved or gone since the modal opened, skipped:', row?.skin?.value);
                return;
              }
              handleSaveItem({
                ...current,
                buyPrice,
                profit: calcProfit(buyPrice, current.sellPrice, current.siteFee),
                ...(paidWith !== 'keep' ? { paidWith } : {}),
              }, at);
            });
            setGroupPriceItems(null);
          }}
        />
      )}

      {/* Group sell modal */}
      {showGroupSell && selectedForSell.size > 0 && (
        <GroupSellModal
          items={[...selectedForSell].map(ri => ({ row: rows[ri], realIndex: ri })).filter(({ row }) => row)}
          settings={settings}
          skinsData={skinsData}
          livePrices={livePrices}
          onConfirm={results => {
            setRows(prev => {
              const next = [...prev];
              for (const { realIndex, sellPrice, profit } of results) {
                if (!next[realIndex]) continue;
                next[realIndex] = { ...next[realIndex], sellPrice, profit };
              }
              return next;
            });
            setShowGroupSell(false);
            setSelectMode(false);
            setSelectedForSell(new Set());
          }}
          onClose={() => setShowGroupSell(false)}
        />
      )}

      {/* ── Lending ──────────────────────────────────────────────────
          Two steps, and the second is usually skipped: an item leaving in a
          Steam trade or picked in the Locker goes straight to LendModal with
          the borrower already filled in. The picker only opens for a lend that
          has no trade behind it. */}
      {lendingOn && lendPicker && (
        <LendPicker
          rows={rows || []}
          skinsData={skinsData}
          livePrices={livePrices}
          inventoryMap={inventoryMap}
          syncedInventory={syncedInventory}
          tradeHistory={tradeHistory}
          variant={zoneTheme ? 'zone' : 'classic'}
          onPick={({ indexes, newItems }) => {
            // Items picked here have often already left, and Steam usually
            // recorded who took them. Carry that through when everything picked
            // agrees on one person — disagreement means there is nothing to
            // suggest, and a wrong name pre-filled is worse than a blank field.
            //
            // Two sources agree on this: a row's own tradedWith, and a SENT
            // trade-history candidate, where the person Steam says you handed
            // the item to is by definition the one holding it now.
            const rowPartners = indexes.map(i => suggestedPartner((rows || [])[i], tradePartners));
            const fromHistory = borrowerFromCandidates(newItems);
            const all = [...rowPartners, ...(fromHistory ? [fromHistory] : [])];
            const first = all[0] || null;
            const agree = first && all.every(p =>
              p && (p.steamId64 || p.name) === (first.steamId64 || first.name));

            // Only something that actually left has a departure time worth
            // reusing as the lend date.
            const rowTimes = indexes
              .map(i => (rows || [])[i])
              .filter(r => r?.tradeId)
              .map(r => r.timestamp)
              .filter(Boolean);
            const histTime = departureFromCandidates(newItems);
            const times = [...new Set([...rowTimes, ...(histTime ? [histTime] : [])])];

            setLendPicker(false);
            setLendModal({
              indexes,
              newItems,
              suggested: agree ? first : null,
              defaultAt: times.length === 1 ? times[0] : null,
            });
          }}
          onClose={() => setLendPicker(false)}
        />
      )}

      {lendingOn && lendModal && (
        <LendModal
          items={lendModal.indexes.map(i => {
            const row = (rows || [])[i];
            if (!row) return null;
            return {
              key:      i,
              name:     row.skin?.value || 'Unknown item',
              subtitle: `${row.condition?.value || 'n/a'} · paid $${(parseFloat(row.buyPrice) || 0).toFixed(2)}`,
              image:    getRowImage(row, skinsData, inventoryMap),
              // Only so the modal can say how much is walking out the door.
              // Nothing downstream reads it.
              value:    getBuffPrice(livePrices, row) ?? parseFloat(row.buyPrice) ?? 0,
            };
          }).filter(Boolean)}
          newItems={lendModal.newItems || []}
          friends={lendFriends}
          suggested={lendModal.suggested}
          defaultAt={lendModal.defaultAt}
          variant={zoneTheme ? 'zone' : 'classic'}
          onConfirm={confirmLend}
          onClose={() => setLendModal(null)}
        />
      )}

      {/* New-items modal */}
      {showNewItemsModal && (
        <NewItemsModal
          items={newItemsForReview}
          livePrices={livePrices}
          settings={settings}
          skinsData={skinsData}
          variant={zoneTheme ? 'zone' : 'classic'}
          trackedAssetIds={trackedAssetIds}
          onAddToLive={(itemsToAdd, allAssetIds) => {
            // Build Live rows from selected inventory items. Anything the rows
            // already speak for is dropped rather than added twice — the prompt
            // filters on the same set, so this only catches a selection made
            // before a trade landed.
            const newRows = itemsToAdd.filter(item => !trackedAssetIds.has(item.assetId)).map(item => {
              const mhn      = item.market_hash_name || item.marketHashName || '';
              const baseName = mhn.replace(/ \((Factory New|Minimal Wear|Field-Tested|Well-Worn|Battle-Scarred)\)$/, '').trim();
              const condVal  = EXTERIOR_TO_CONDITION[item.exterior] || null;
              const price    = livePrices?.[mhn]?.starting_at?.price ?? null;
              const stickerDesc = (item.stickers || []).filter(s => s.name).map(s => s.name).join(', ');
              return {
                ...createEmptyRow(settings),
                skin:               { value: baseName, label: baseName },
                condition:          condVal ? { value: condVal, label: condVal } : null,
                assetId:            item.assetId,
                // Steam's own render, kept on the row so items too new to be in
                // the skins database still show a picture everywhere.
                iconUrl:            item.iconUrl || item.icon_url || null,
                inspectLink:        item.inspectLink || '',
                float:              item.float ?? null,
                paintSeed:          item.paintSeed ?? null,
                description:        stickerDesc,
                stickers:           (item.stickers || []).filter(s => s.name),
                charms:             (item.charms || []).filter(c => c.name),
                buyPrice:           price != null ? price.toFixed(2) : '',
                timestamp:          new Date().toISOString(),
                tradeProtected:     item.tradeProtected     ?? false,
                tradeProtectedUntil: item.tradeProtectedUntil ?? null,
                isInvestment:       item.isInvestment === true,
              };
            });
            if (newRows.length > 0) setRows(prev => [...prev, ...newRows]);

            // Clear all resolved appearance records (both added and skipped)
            apiFetch(`/api/tracker/pending-appearances-resolve/${user}`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ assetIds: allAssetIds }),
            }).catch(() => {});
            setPendingNewItems(prev => prev.filter(i => !allAssetIds.includes(i.assetId)));
            setShowNewItemsModal(false);
          }}
          onDismiss={() => setShowNewItemsModal(false)}
        />
      )}
    </div>
    </TradeHistoryProvider>
    </SettlingProvider>
  );
}

// ─── Inventory Item Card ──────────────────────────────────────
const EXTERIOR_ABBR = {
  'Factory New':'FN','Minimal Wear':'MW','Field-Tested':'FT',
  'Well-Worn':'WW','Battle-Scarred':'BS',
};
const EXTERIOR_RE = /\s*\((Factory New|Minimal Wear|Field-Tested|Well-Worn|Battle-Scarred)\)$/;

function InventoryItemCard({ item, price, selected, tracked, stored, onToggle, stickerPrices, tradePartner }) {
  const [copiedSlot, setCopiedSlot] = useState(null);
  const [hoveredSlot, setHoveredSlot] = useState(null);

  const mhn         = item.marketHashName || item.market_hash_name || '';
  const exteriorMatch = mhn.match(EXTERIOR_RE);
  const exterior    = exteriorMatch ? exteriorMatch[1] : null;
  const baseName    = mhn.replace(EXTERIOR_RE, '').trim();
  const isST        = baseName.startsWith('StatTrak™');
  const isSV        = baseName.startsWith('Souvenir');
  const displayName = baseName.replace('StatTrak™ ', '').replace('Souvenir ', '');
  const abbr        = EXTERIOR_ABBR[exterior] || null;

  const MAX_SLOTS = 5;
  const stickers  = item.stickers || [];
  const slots     = Array.from({ length: MAX_SLOTS }, (_, i) => stickers[i] || null);
  const hasSkin   = exterior != null;

  const float    = item.float;
  const pct      = float != null ? float * 100 : null;

  // Chips: trade lock only — float/pattern get their own dedicated labeled row.
  // A stored item's last trade-lock reading predates it being put away, so it
  // is dropped rather than shown; where it sits is the only fact left.
  const { locked: rawLocked, until: lockUntil } = getTradeLock(item, null);
  const locked = stored ? false : rawLocked;
  const chips = [];
  if (stored) {
    chips.push({ label: 'In storage', green: false });
  } else if (locked) {
    chips.push({
      label: lockUntil
        ? `Locked ${lockUntil.toLocaleDateString('en-US',{month:'short',day:'numeric'})}`
        : 'Locked',
      green: false,
    });
  }

  return (
    <article
      className={`profile-history-item-card${selected ? ' phi-selected' : ''}${tracked ? ' phi-tracked' : ''}`}
      onClick={() => !tracked && !stored && onToggle(item.assetId)}
      style={tracked || stored ? { opacity: 0.45, cursor: 'default' } : undefined}
    >
      {/* 1. Name row */}
      <div className="phi-name-row">
        <span className="phi-name">{displayName || mhn}</span>
        <div className="phi-badges">
          {tracked && <span className="phi-badge" style={{ background:'rgba(255,255,255,0.10)', color:'var(--text-dim)', fontSize:'0.85rem' }}>tracked</span>}
          {abbr && <span className="phi-exterior-tag">{abbr}</span>}
          {isST && <span className="phi-badge phi-badge--st">ST™</span>}
          {isSV && <span className="phi-badge phi-badge--sv">SV</span>}
        </div>
      </div>
      {/* Always in the layout, filled or not, so a card that arrived in a
          trade lines up with one that did not. */}
      <div className="phi-from">
        {tradePartner && <TradePartnerBadge partner={tradePartner} style={{ minWidth: 0 }} />}
      </div>

      {/* 2. Image */}
      <div className="phi-image-wrap">
        {/* The same badge a Live card wears, class and wording included. */}
        {locked && (
          <span className="lv2-lock">
            {lockUntil
              ? `🔒 ${lockUntil.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
              : '🔒 Locked'}
          </span>
        )}
        {item.iconUrl
          ? <img src={item.iconUrl} alt={displayName} loading="lazy" />
          : <div className="phi-image-placeholder" />}
      </div>

      {/* 3. Sticker row — always rendered; hidden for non-skin items */}
      <div
        className="phi-sticker-row"
        style={{ visibility: hasSkin ? 'visible' : 'hidden', pointerEvents: hasSkin ? 'auto' : 'none' }}
      >
        {slots.map((sticker, i) => {
          const sp   = sticker?.name ? stickerPrices?.['Sticker | ' + sticker.name]?.starting_at?.price : null;
          const wPct = sticker?.wear != null ? Math.round(sticker.wear * 100) : null;
          const copied = copiedSlot === i;
          return (
            <div
              key={i}
              className={`phi-sticker-slot${sticker ? ' filled' : ''}${copied ? ' phi-sticker-slot--copied' : ''}`}
              onMouseEnter={() => sticker && setHoveredSlot(i)}
              onMouseLeave={() => setHoveredSlot(null)}
              onClick={sticker ? e => {
                e.stopPropagation();
                navigator.clipboard?.writeText(sticker.name || '').then(() => {
                  setCopiedSlot(i);
                  setTimeout(() => setCopiedSlot(null), 1400);
                }).catch(() => {});
              } : undefined}
            >
              <span className="phi-sticker-slot-num">{i + 1}</span>
              {sticker?.image && <img src={sticker.image} alt="" />}
              {sticker && (hoveredSlot === i || copied) && (
                <div className={`phi-sticker-tooltip${copied ? ' phi-sticker-tooltip-copied' : ''}`}
                  style={{ opacity: 1 }}>
                  {copied ? (
                    <span className="phi-sticker-tooltip-copied">Copied!</span>
                  ) : (
                    <>
                      <span className="phi-tooltip-name">{sticker.name}</span>
                      {sp != null && <span className="phi-tooltip-price">${sp.toFixed(2)}</span>}
                      {wPct != null && wPct > 0 && <span className="phi-tooltip-hint">{wPct}% worn</span>}
                      <span className="phi-tooltip-hint">Click to copy</span>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {/* The charm rides on the end of the same rail. Slots are flex:1, so
            an item wearing one gives up a little width across the row rather
            than needing a line of its own. */}
        {(item.charms || []).map((charm, i) => (
          <CharmSlot key={`charm-${i}`} charm={charm} prices={stickerPrices} variant="classic" />
        ))}
      </div>

      {/* 4. Float bar — always rendered; marker appears only when float exists */}
      <div className="phi-float-bar-wrap">
        <div className="phi-float-bar-track" style={{ opacity: pct != null ? 0.7 : 0.18 }}>
          {pct != null && (
            <div className="phi-float-bar-marker" style={{ left: `${Math.min(Math.max(pct, 0), 100)}%` }} />
          )}
        </div>
      </div>

      {/* Float + Pattern labeled row — always visible */}
      <div className="phi-float-pattern-row">
        <div className="phi-fp-cell">
          <span className="phi-fp-label">Float</span>
          <span className={`phi-fp-value${float == null ? ' phi-fp-empty' : ''}`}>
            {float != null ? float.toFixed(6) : '—'}
          </span>
        </div>
        <div className="phi-fp-divider" />
        <div className="phi-fp-cell">
          <span className="phi-fp-label">Pattern</span>
          <span className={`phi-fp-value${item.paintSeed == null ? ' phi-fp-empty' : ' phi-fp-pattern'}`}>
            {item.paintSeed != null ? `#${item.paintSeed}` : '—'}
          </span>
        </div>
      </div>

      {/* 5. Meta chips — trade lock only */}
      {/* Always in the layout, empty or not — the Zone card's chip row does the
          same. Rendered only when there were chips, an item on trade hold sat
          taller than the plain one beside it. */}
      <div className="phi-meta-row">
        {chips.map((c, i) => (
          <span key={i} className={`phi-meta-chip${c.green ? ' phi-meta-chip--green' : ''}`}>{c.label}</span>
        ))}
      </div>

      {/* 6. Spacer — pushes price + button to bottom */}
      <span className="phi-card-spacer" />

      {/* 7. Price section */}
      <div className="phi-price-section">
        {price != null
          ? <div className="phi-price-row"><span>Market</span><strong>${price.toFixed(2)}</strong></div>
          : <div className="phi-price-row"><span>Market</span><strong>—</strong></div>}
        <div className="phi-price-row">
          <span>Status</span>
          <strong style={{ color: selected && !tracked ? '#818cf8' : 'var(--text-dim)' }}>
            {stored ? 'In storage' : tracked ? 'In tracker' : selected ? 'In group' : 'Not tracked'}
          </strong>
        </div>
      </div>

      {/* 8. Inspect button */}
      {item.inspectLink
        ? <a href={item.inspectLink} className="phi-inspect-btn"
            onClick={e => e.stopPropagation()} target="_blank" rel="noreferrer">
            Inspect in Game
          </a>
        : <span className="phi-inspect-btn phi-inspect-btn--disabled">No Inspect Link</span>}
    </article>
  );
}

// ─── Extension Tab Content ────────────────────────────────────
// Filters and sorts are shared verbatim with InventoryTabZone ("the Locker"),
// which is this tab's Zone-theme twin.
const INVENTORY_FILTERS = [
  { key: 'all',       label: 'All' },
  { key: 'locked',    label: 'Trade locked' },
  { key: 'tradable',  label: 'Tradable' },
  { key: 'tracked',   label: 'In tracker' },
  { key: 'untracked', label: 'Not tracked' },
];

// Storage is not one of the filters above, it is a different list.
//
// Everything above narrows what Steam can currently see. Items in a storage
// unit are not in that reading at all — Steam does not list casket contents —
// so they cannot be a subset of it, and folding them in would change what "All"
// and every total on this page mean. They get their own chip, appended only
// when there is something to look at, and the only view that shows them.
const STORAGE_FILTER = { key: 'stored', label: 'Storage' };

const INVENTORY_SORTS = [
  { key: 'price',    label: 'Highest price' },
  { key: 'name',     label: 'Name A-Z' },
  { key: 'float',    label: 'Lowest float' },
  { key: 'stickers', label: 'Most stickers' },
  { key: 'unlock',   label: 'Unlocking soonest' },
];

function ExtensionTabContent({
  user, livePrices, syncedInventory, setSyncedInventory, storedItems = [],
  selectedForGroup, setSelectedForGroup,
  groupedPurchases, setGroupedPurchases,
  onOpenGroupModal, onReloadInventory,
  trackedAssetIds, tradePartners, onSendToTracker,
  lendingEnabled = false, onLendItems,
}) {
  const [loading, setLoading]         = useState(false);
  const [syncMeta, setSyncMeta]       = useState(null);
  const [localGroups, setLocalGroups] = useState(groupedPurchases);
  const [query, setQuery]             = useState('');
  const [filter, setFilter]           = useState('all');
  const [sort, setSort]               = useState('price');

  // Load inventory meta + grouped purchases on mount
  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        // Reload inventory (picks up fresh float/pattern data after a sync)
        const res  = await apiFetch(`/api/tracker/inventory/${user}`);
        const data = await res.json();
        if (data.items?.length) {
          setSyncedInventory(data.items);
          setSyncMeta(data.syncMeta || null);
        } else {
          setSyncMeta(data.syncMeta || null);
        }
        const gRes  = await apiFetch(`/api/grouped-purchases/${user}`);
        const gData = await gRes.json();
        if (gData.groupedPurchases) {
          setGroupedPurchases(gData.groupedPurchases);
          setLocalGroups(gData.groupedPurchases);
        }
      } catch { /* non-fatal */ }
      setLoading(false);
    }
    load();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setLocalGroups(groupedPurchases); }, [groupedPurchases]);

  // Stored items are read-only here. Steam reissues the assetId when the item
  // comes back out of the unit, so grouping one or sending it to the tracker
  // would file an id that is already dead — and the item would then arrive as
  // "new" and be added a second time.
  const storedAssetIds = useMemo(
    () => new Set((storedItems || []).map(i => i.assetId).filter(Boolean)),
    [storedItems]
  );

  const toggleItem = assetId => {
    if (trackedAssetIds?.has(assetId)) return;
    if (storedAssetIds.has(assetId)) return;
    setSelectedForGroup(prev => {
      const next = new Set(prev);
      next.has(assetId) ? next.delete(assetId) : next.add(assetId);
      return next;
    });
  };

  const fmtTime = iso => {
    if (!iso) return 'Never';
    return new Date(iso).toLocaleString(undefined, { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
  };

  const fmtPrice = v => {
    if (v == null) return '—';
    const r = Math.round(v * 100) / 100;
    return '$' + r.toFixed(2).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  };

  // Search / filter / sort — kept identical to the Locker so the same query
  // yields the same set of cards in either theme.
  const items = useMemo(() => (syncedInventory || []).map(it => {
    const mhn = it.marketHashName || it.market_hash_name || '';
    const { locked, until } = getTradeLock(it, null);
    return {
      it, mhn,
      price:    livePrices?.[mhn]?.starting_at?.price ?? null,
      locked, until,
      stickers: (it.stickers || []).filter(s => s.name).length,
      tracked:  trackedAssetIds?.has(it.assetId) ?? false,
    };
  }), [syncedInventory, livePrices, trackedAssetIds]);

  // The storage shelf, built to the same shape so one card and one sort work on
  // both. Trade lock is blanked rather than read: the last reading of it is
  // from before the item was put away and says nothing about now.
  const stored = useMemo(() => (storedItems || []).map(it => {
    const mhn = it.marketHashName || it.market_hash_name || '';
    return {
      it, mhn,
      price:    livePrices?.[mhn]?.starting_at?.price ?? null,
      locked: false, until: null,
      stickers: (it.stickers || []).filter(s => s.name).length,
      tracked:  trackedAssetIds?.has(it.assetId) ?? false,
      stored:   true,
    };
  }), [storedItems, livePrices, trackedAssetIds]);

  const chips = useMemo(
    () => (stored.length ? [...INVENTORY_FILTERS, STORAGE_FILTER] : INVENTORY_FILTERS),
    [stored.length]
  );

  // The last stored item coming back takes its chip with it. Without this the
  // view would sit on a filter that no longer has a button to leave it by.
  useEffect(() => {
    if (filter === 'stored' && stored.length === 0) setFilter('all');
  }, [filter, stored.length]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Two separate pools, never mixed: nothing in storage is visible in Steam,
    // so no filter over the live inventory can honestly include it.
    const pool = filter === 'stored' ? stored : items;
    const list = pool.filter(e => {
      if (q && !e.mhn.toLowerCase().includes(q)) return false;
      if (filter === 'locked'    && !e.locked) return false;
      if (filter === 'tradable'  && e.locked) return false;
      if (filter === 'tracked'   && !e.tracked) return false;
      if (filter === 'untracked' && e.tracked) return false;
      return true;
    });
    const by = {
      price:    (a, b) => (b.price ?? -1) - (a.price ?? -1),
      name:     (a, b) => a.mhn.localeCompare(b.mhn),
      float:    (a, b) => (a.it.float ?? Infinity) - (b.it.float ?? Infinity),
      stickers: (a, b) => b.stickers - a.stickers,
      unlock:   (a, b) => (a.until ? +a.until : Infinity) - (b.until ? +b.until : Infinity),
    }[sort];
    return list.sort(by);
  }, [items, stored, query, filter, sort]);

  // Live items only. The headline numbers answer "what is in my Steam
  // inventory", and something sitting in a casket is not part of that answer.
  const totals = useMemo(() => {
    const value       = items.reduce((s, e) => s + (e.price ?? 0), 0);
    const locked      = items.filter(e => e.locked).length;
    const lockedValue = items.filter(e => e.locked).reduce((s, e) => s + (e.price ?? 0), 0);
    const tracked     = items.filter(e => e.tracked).length;
    const priced      = items.filter(e => e.price != null).length;
    return { value, locked, lockedValue, tracked, priced, count: items.length };
  }, [items]);

  const shownValue = useMemo(() => filtered.reduce((s, e) => s + (e.price ?? 0), 0), [filtered]);

  return (
    <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <ExtensionPanel user={user} />

      {/* Sync metadata */}
      {syncMeta && (
        <div style={{ background:'var(--panel)', border:'1px solid var(--border)', borderRadius:8, padding:'12px 16px', fontSize:15, color:'var(--text-muted)', display:'flex', gap:20, flexWrap:'wrap' }}>
          <span>Last sync: <strong style={{ color:'var(--text)' }}>{fmtTime(syncMeta.syncCompletedAt)}</strong></span>
          <span>Total: <strong style={{ color:'var(--text)' }}>{syncMeta.totalItemCount ?? 0}</strong></span>
          <span>Tradable: <strong style={{ color:'var(--green)' }}>{syncMeta.tradableItemCount ?? 0}</strong></span>
          <span>Trade-locked: <strong style={{ color:'#f59e0b' }}>{syncMeta.protectedItemCount ?? 0}</strong></span>
        </div>
      )}

      {/* Headline totals */}
      {totals.count > 0 && (
        <div className="pt-hero" style={{ marginBottom: 0 }}>
          <div className="pt-hero-cell">
            <div className="k">Locker value</div>
            <div className="v" style={{ color: 'var(--indigo)' }}>${totals.value.toFixed(2)}</div>
            <div className="s">{totals.priced} of {totals.count} have a live price</div>
          </div>
          <div className="pt-hero-cell">
            <div className="k">Locked up</div>
            <div className="v">${totals.lockedValue.toFixed(2)}</div>
            <div className="s">{totals.locked} item{totals.locked !== 1 ? 's' : ''} you can't trade yet</div>
          </div>
          <div className="pt-hero-cell">
            <div className="k">In the tracker</div>
            <div className="v">{totals.tracked}</div>
            <div className="s">{totals.count - totals.tracked} still untracked</div>
          </div>
        </div>
      )}

      {/* Inventory grid */}
      <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
          <span style={{ fontWeight:700, fontSize:15.5, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>
            Inventory {totals.count > 0 && `(${totals.count})`}
            {totals.locked > 0 && (
              <span style={{ fontWeight:600, textTransform:'none', letterSpacing:0, color:'var(--text-dim)' }}>
                {' · '}{totals.locked} trade locked
              </span>
            )}
          </span>
          {selectedForGroup.size > 0 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={() => onSendToTracker && onSendToTracker(Array.from(selectedForGroup), false)} style={{
                padding:'6px 14px', borderRadius:8, fontSize:15, fontWeight:600,
                background:'rgba(129,140,248,0.15)', color:'#818cf8',
                border:'1px solid rgba(129,140,248,0.35)', cursor:'pointer',
              }}>
                → Live ({selectedForGroup.size})
              </button>
              <button onClick={() => onSendToTracker && onSendToTracker(Array.from(selectedForGroup), true)} style={{
                padding:'6px 14px', borderRadius:8, fontSize:15, fontWeight:600,
                background:'rgba(74,222,128,0.12)', color:'#4ade80',
                border:'1px solid rgba(74,222,128,0.30)', cursor:'pointer',
              }}>
                → Investments ({selectedForGroup.size})
              </button>
              {/* Lending out of the locker: the items are picked already, so
                  this jumps straight to "who has it". */}
              {lendingEnabled && (
                <button onClick={() => onLendItems && onLendItems(Array.from(selectedForGroup))} style={{
                  padding:'6px 14px', borderRadius:8, fontSize:15, fontWeight:600,
                  background:'rgba(56,189,248,0.12)', color:'#38bdf8',
                  border:'1px solid rgba(56,189,248,0.36)', cursor:'pointer',
                }}>
                  Lend out ({selectedForGroup.size})
                </button>
              )}
              <button onClick={onOpenGroupModal} style={{
                padding:'6px 16px', borderRadius:8, fontSize:15, fontWeight:600,
                background:'var(--indigo,#818cf8)', color:'#fff', border:'none', cursor:'pointer',
                boxShadow:'0 2px 8px rgba(129,140,248,0.30)',
              }}>
                Group
              </button>
            </div>
          )}
        </div>

        {/* Toolbar */}
        {(totals.count > 0 || stored.length > 0) && (
          <div className="lv2-bar" style={{ position:'static', margin:0 }}>
            <div className="lv2-search">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" />
              </svg>
              <input placeholder="Search the locker…" value={query} onChange={e => setQuery(e.target.value)} />
            </div>
            {chips.map(f => (
              <button key={f.key} className={`lv2-chip ${filter === f.key ? 'on' : ''}`}
                onClick={() => setFilter(f.key)}>
                {f.label}{f.key === 'stored' ? ` (${stored.length})` : ''}
              </button>
            ))}
            <span style={{ marginLeft:'auto', fontSize:'0.92rem', color:'var(--text-dim)', fontWeight:600 }}>
              {filtered.length} shown · ${shownValue.toFixed(2)}
            </span>
            <select className="lv2-select" value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort locker">
              {INVENTORY_SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        )}

        {loading && totals.count === 0 ? (
          <div className="profile-history-grid">
            {Array.from({ length: 12 }).map((_, i) => <div key={i} className="profile-skeleton" />)}
          </div>
        ) : totals.count === 0 && stored.length === 0 ? (
          <div className="lv2-empty">
            The locker is empty. Connect the browser extension above and hit sync to pull your Steam inventory in.
          </div>
        ) : filtered.length === 0 ? (
          <div className="lv2-empty">Nothing matches your filters.</div>
        ) : (
          <>
            {filter === 'stored' && (
              <div className="lv2-empty" style={{ padding:'10px 14px', textAlign:'left' }}>
                Items you said are in a storage unit. Steam does not list what is inside one, so
                these are here on your word alone and are left out of every other view and total.
                They come back on their own the next time a sync sees them.
              </div>
            )}
            <div className="profile-history-grid">
              {filtered.map(e => (
                <InventoryItemCard
                  key={e.it.assetId}
                  item={e.it}
                  price={e.price}
                  selected={selectedForGroup.has(e.it.assetId)}
                  tracked={e.tracked}
                  stored={e.stored}
                  onToggle={toggleItem}
                  stickerPrices={livePrices}
                  tradePartner={tradePartners?.byAssetId?.get(e.it.assetId)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Grouped purchases list */}
      {localGroups.length > 0 && (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          <span style={{ fontWeight:700, fontSize:15.5, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>
            Saved Groups ({localGroups.length})
          </span>
          {localGroups.map(gp => {
            const pct = gp.displayPercentage;
            const displayPct = (pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(1)) + '%';
            return (
              <div key={gp.id} style={{ background:'var(--panel)', border:'1px solid var(--border)', borderRadius:8, padding:'12px 16px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <span style={{ fontSize:15.5, fontWeight:600, color:'var(--text)' }}>
                    {gp.items.length} item{gp.items.length !== 1 ? 's' : ''} · paid {fmtPrice(gp.totalPurchasePrice)}
                  </span>
                  <span style={{ fontSize:16, fontWeight:700, color:'#818cf8' }}>{displayPct} of market</span>
                </div>
                <div style={{ fontSize:14.5, color:'var(--text-muted)', display:'flex', gap:16 }}>
                  <span>Market value at purchase: {fmtPrice(gp.combinedMarketValueAtCreation)}</span>
                  <span>Created: {fmtTime(gp.createdAt)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
